import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { localDateStr } from '../utils/dateUtils';
import { calcAiScore } from '../utils/companyScore';
import { useApp } from '../context/AppContext';
import { Card, CardHeader } from '../components/UI/Card';
import Button from '../components/UI/Button';
import Badge, { PlanBadge } from '../components/UI/Badge';
import { Input, Select } from '../components/UI/Input';
import SectorCombobox from '../components/SectorCombobox';
import { api } from '../services/api';
import mascotImg from '../assets/genie-appee.png';
import GenieTip from '../components/GenieTip';
import SectionHint from '../components/SectionHint';
import './Settings.css';

function SmtpHealthBadge({ status }) {
  if (!status) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 10, height: 10, borderRadius: '50%', background: status.ok ? '#22C55E' : '#EF4444', flexShrink: 0 }} />
      <span style={{ fontSize: 13, fontWeight: 600, color: status.ok ? '#16A34A' : '#DC2626' }}>
        {status.ok ? 'SMTP opérationnel' : 'SMTP en erreur'}
      </span>
      {status.last_checked && (
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>
          · vérifié {new Date(status.last_checked).toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' })}
        </span>
      )}
    </div>
  );
}

const REMINDER_TYPES = [
  { value: 'birthday',      label: '🎂 Anniversaire' },
  { value: 'prive',         label: '🏠 Privé' },
  { value: 'professionnel', label: '💼 Professionnel' },
  { value: 'apero',         label: '🥂 Apéro' },
  { value: 'autre',         label: '📌 Autre' },
];
const DAYS_BEFORE_OPTIONS = [
  { value: 0,  label: 'Le jour même' },
  { value: 1,  label: '1 jour avant' },
  { value: 2,  label: '2 jours avant' },
  { value: 3,  label: '3 jours avant' },
  { value: 7,  label: '1 semaine avant' },
  { value: 14, label: '2 semaines avant' },
  { value: 30, label: '1 mois avant' },
];

const REMINDER_MAX = 10;

function ReminderForm({ form, setRF, onSave, onCancel, isNew, saving }) {
  return (
    <div style={{
      padding: '18px 20px',
      background: 'white',
      border: '2px solid var(--violet)',
      borderRadius: 14,
      display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--violet)', marginBottom: 2 }}>
        {isNew ? '✨ Nouveau rappel' : '✏️ Modifier le rappel'}
      </div>

      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{ flex: 2 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Nom du rappel *</label>
          <input
            value={form.label}
            onChange={e => setRF('label', e.target.value)}
            placeholder="Ex : Anniversaire client, Baptême Marie…"
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--line)', fontSize: 14, boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Type</label>
          <select
            value={form.type}
            onChange={e => setRF('type', e.target.value)}
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--line)', fontSize: 14, background: 'white', boxSizing: 'border-box' }}
          >
            {REMINDER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{ flex: 2 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>
            {form.type === 'birthday' ? 'Date du rappel' : 'Date du rappel *'}
          </label>
          <input
            type="date"
            value={form.fixed_date || ''}
            onChange={e => setRF('fixed_date', e.target.value)}
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--line)', fontSize: 14, boxSizing: 'border-box' }}
          />
          {form.type === 'birthday' && (
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
              Optionnel — si vide, se déclenche chaque année automatiquement pour tous les contacts dont l'anniversaire approche.
            </div>
          )}
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Heure</label>
          <input
            type="time"
            value={form.time}
            onChange={e => setRF('time', e.target.value)}
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--line)', fontSize: 14, boxSizing: 'border-box' }}
          />
        </div>
      </div>
      {form.type === 'birthday' && (
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 2 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Délai avant l'anniversaire</label>
            <select
              value={form.days_before}
              onChange={e => setRF('days_before', Number(e.target.value))}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--line)', fontSize: 14, background: 'white', boxSizing: 'border-box' }}
            >
              {DAYS_BEFORE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }} />
        </div>
      )}

      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Note (optionnel)</label>
        <input
          value={form.note || ''}
          onChange={e => setRF('note', e.target.value)}
          placeholder="Ex : Préparer un cadeau, envoyer un bon de réduction…"
          style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--line)', fontSize: 14, boxSizing: 'border-box' }}
        />
      </div>

      {/* ── Canaux d'envoi ── */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 8 }}>Canal d'envoi</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[
            { value: ['email'],        label: '✉ Email',         color: '#5A35FF', bg: '#EDE9FF' },
            { value: ['sms'],          label: '📱 SMS',          color: '#10B981', bg: '#F0FDF4' },
            { value: ['email','sms'],  label: '✉ + 📱 Les deux', color: '#F59E0B', bg: '#FFFBEB' },
          ].map(opt => {
            const cur = form.channels || ['email'];
            const active = opt.value.length === cur.length && opt.value.every(v => cur.includes(v));
            return (
              <button
                key={opt.label}
                type="button"
                onClick={() => setRF('channels', opt.value)}
                style={{
                  padding: '8px 16px',
                  borderRadius: 20,
                  border: `1.5px solid ${active ? opt.color : 'var(--line)'}`,
                  background: active ? opt.bg : '#F9FAFB',
                  color: active ? opt.color : 'var(--muted)',
                  fontWeight: active ? 800 : 600,
                  fontSize: 13,
                  cursor: 'pointer',
                  transition: 'all .15s',
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Destinataires supplémentaires ── */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)' }}>
            Destinataires supplémentaires
            {(form.extra_recipients || []).length > 0 && (
              <span style={{ marginLeft: 7, fontSize: 11, background: '#EDE9FF', color: '#5A35FF', borderRadius: 20, padding: '1px 8px', fontWeight: 700 }}>
                {(form.extra_recipients || []).length}
              </span>
            )}
          </label>
          <button
            type="button"
            onClick={() => setRF('extra_recipients', [...(form.extra_recipients || []), { id: Date.now(), name: '', email: '', phone: '', channels: (form.channels || ['email']) }])}
            style={{ fontSize: 12, fontWeight: 700, color: 'var(--violet)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0' }}
          >
            + Ajouter un destinataire
          </button>
        </div>

        {(form.extra_recipients || []).length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--muted)', padding: '8px 12px', background: '#F9FAFB', borderRadius: 8, border: '1px dashed var(--line)' }}>
            Le rappel sera envoyé uniquement à l'adresse principale. Ajoute des destinataires pour en notifier d'autres.
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {(form.extra_recipients || []).map((rec, idx) => (
            <div key={rec.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#F9FAFB', border: '1px solid var(--line)', borderRadius: 10, padding: '12px 12px' }}>
              <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#EDE9FF', color: '#5A35FF', fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 6 }}>
                {idx + 1}
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}>
                <input
                  value={rec.name}
                  onChange={e => setRF('extra_recipients', (form.extra_recipients || []).map(r => r.id === rec.id ? { ...r, name: e.target.value } : r))}
                  placeholder="Nom"
                  style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1.5px solid var(--line)', fontSize: 13, boxSizing: 'border-box' }}
                />
                <input
                  type="email"
                  value={rec.email}
                  onChange={e => setRF('extra_recipients', (form.extra_recipients || []).map(r => r.id === rec.id ? { ...r, email: e.target.value } : r))}
                  placeholder="Email"
                  style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1.5px solid var(--line)', fontSize: 13, boxSizing: 'border-box' }}
                />
                <input
                  type="tel"
                  value={rec.phone}
                  onChange={e => setRF('extra_recipients', (form.extra_recipients || []).map(r => r.id === rec.id ? { ...r, phone: e.target.value } : r))}
                  placeholder="Téléphone (pour SMS)"
                  style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1.5px solid var(--line)', fontSize: 13, boxSizing: 'border-box' }}
                />
                {/* Canal par destinataire */}
                <div style={{ display: 'flex', gap: 6 }}>
                  {[
                    { id: 'email', label: '✉ Email',  color: '#5A35FF', bg: '#EDE9FF' },
                    { id: 'sms',   label: '📱 SMS',   color: '#10B981', bg: '#F0FDF4' },
                  ].map(ch => {
                    const active = (rec.channels || ['email']).includes(ch.id);
                    return (
                      <button
                        key={ch.id}
                        type="button"
                        onClick={() => {
                          const cur = rec.channels || ['email'];
                          const has = cur.includes(ch.id);
                          const next = has
                            ? (cur.length > 1 ? cur.filter(c => c !== ch.id) : cur)
                            : [...cur, ch.id];
                          setRF('extra_recipients', (form.extra_recipients || []).map(r => r.id === rec.id ? { ...r, channels: next } : r));
                        }}
                        style={{
                          padding: '5px 12px', borderRadius: 20,
                          border: `1.5px solid ${active ? ch.color : 'var(--line)'}`,
                          background: active ? ch.bg : '#fff',
                          color: active ? ch.color : 'var(--muted)',
                          fontWeight: 700, fontSize: 12, cursor: 'pointer', transition: 'all .15s',
                        }}
                      >
                        {ch.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setRF('extra_recipients', (form.extra_recipients || []).filter(r => r.id !== rec.id))}
                style={{ background: 'none', border: '1.5px solid #FECACA', borderRadius: 8, color: '#EF4444', fontSize: 13, fontWeight: 700, padding: '5px 8px', cursor: 'pointer', flexShrink: 0, marginTop: 4 }}
              >✕</button>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
        <Button variant="secondary" size="sm" onClick={onCancel}>Annuler</Button>
        <Button size="sm" onClick={onSave} disabled={!form.label.trim()} loading={saving}>
          {isNew ? 'Créer le rappel' : 'Sauvegarder'}
        </Button>
      </div>
    </div>
  );
}

const emptyReminder = () => ({
  id: Date.now(),
  label: '',
  type: 'birthday',
  days_before: 3,
  fixed_date: '',
  time: '08:00',
  enabled: true,
  note: '',
  channels: ['email'],
  extra_recipients: [],
});

/* Badge statut SMS simple */
function SmsStatusBadge({ status }) {
  const ok = status === 'sent';
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
      background: ok ? '#D1FAE5' : '#FEE2E2',
      color:      ok ? '#065F46' : '#991B1B',
    }}>
      {ok ? '✓ Envoyé' : '✗ Échec'}
    </span>
  );
}

/* Badge statut livraison WhatsApp (basé sur delivery_status Twilio) */
function WaDeliveryBadge({ deliveryStatus, status }) {
  const ds = deliveryStatus || status;
  const cfg = {
    delivered:   { bg: '#D1FAE5', color: '#065F46', label: '✓✓ Livré' },
    read:        { bg: '#EDE9FE', color: '#5B21B6', label: '👁 Lu' },
    sent:        { bg: '#DBEAFE', color: '#1D4ED8', label: '✓ Envoyé' },
    sending:     { bg: '#DBEAFE', color: '#1D4ED8', label: '⏳ En envoi' },
    queued:      { bg: '#F3F4F6', color: '#6B7280', label: '⏳ File d\'att.' },
    failed:      { bg: '#FEE2E2', color: '#991B1B', label: '✗ Échec' },
    undelivered: { bg: '#FEF3C7', color: '#92400E', label: '⚠ Non livré' },
  }[ds] || { bg: '#F3F4F6', color: '#6B7280', label: ds || '—' };

  return (
    <span style={{
      padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
      background: cfg.bg, color: cfg.color,
    }}>
      {cfg.label}
    </span>
  );
}

function SmsLogsTable() {
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getSmsLogs()
      .then(data => setLogs(Array.isArray(data) ? data : []))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 20, color: 'var(--muted)', fontSize: 13 }}>Chargement…</div>;
  if (logs.length === 0) return <div style={{ textAlign: 'center', padding: 20, color: 'var(--muted)', fontSize: 13 }}>Aucun SMS envoyé pour l'instant.</div>;

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '2px solid var(--line)' }}>
            {['Date', 'Contact', 'Numéro', 'Message', 'Statut', 'Coût'].map(h => (
              <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 600, color: 'var(--muted)' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {logs.map(log => (
            <tr key={log.id} style={{ borderBottom: '1px solid var(--line)' }}>
              <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', color: 'var(--muted)' }}>
                {new Date(log.sent_at || log.created_at).toLocaleString('fr-CH', { dateStyle: 'short', timeStyle: 'short' })}
              </td>
              <td style={{ padding: '8px 10px' }}>{log.contact?.name || '—'}</td>
              <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{log.phone}</td>
              <td style={{ padding: '8px 10px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={log.message}>{log.message}</td>
              <td style={{ padding: '8px 10px' }}><SmsStatusBadge status={log.status} /></td>
              <td style={{ padding: '8px 10px', color: 'var(--muted)' }}>
                {log.cost != null ? `${log.cost} CHF` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WhatsAppLogsTable() {
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [notEnabled, setNotEnabled] = useState(false);

  useEffect(() => {
    api.getWhatsAppLogs()
      .then(data => {
        if (Array.isArray(data)) setLogs(data);
        else setNotEnabled(true);
      })
      .catch(err => {
        // 503 = feature not enabled
        if (err?.status === 503 || err?.message?.includes('503')) setNotEnabled(true);
        setLogs([]);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 20, color: 'var(--muted)', fontSize: 13 }}>Chargement…</div>;
  if (notEnabled) return (
    <div style={{ textAlign: 'center', padding: 20, color: 'var(--muted)', fontSize: 13 }}>
      <span style={{ fontSize: 24 }}>📱</span>
      <div style={{ marginTop: 8 }}>WhatsApp sera disponible prochainement.</div>
    </div>
  );
  if (logs.length === 0) return <div style={{ textAlign: 'center', padding: 20, color: 'var(--muted)', fontSize: 13 }}>Aucun message WhatsApp envoyé pour l'instant.</div>;

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '2px solid var(--line)' }}>
            {['Date', 'Contact', 'Numéro', 'Message', 'Livraison', 'Coût'].map(h => (
              <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 600, color: 'var(--muted)' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {logs.map(log => (
            <tr key={log.id} style={{ borderBottom: '1px solid var(--line)' }}>
              <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', color: 'var(--muted)' }}>
                {new Date(log.sent_at || log.created_at).toLocaleString('fr-CH', { dateStyle: 'short', timeStyle: 'short' })}
              </td>
              <td style={{ padding: '8px 10px' }}>{log.contact?.name || '—'}</td>
              <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{log.phone}</td>
              <td style={{ padding: '8px 10px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={log.message}>{log.message}</td>
              <td style={{ padding: '8px 10px' }}>
                <WaDeliveryBadge deliveryStatus={log.delivery_status} status={log.status} />
              </td>
              <td style={{ padding: '8px 10px', color: 'var(--muted)' }}>
                {log.cost != null ? `${log.cost} CHF` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Supprime les suffixes juridiques courants (SA, Sàrl, SARL, GmbH, etc.)
function stripLegalSuffix(name) {
  return name
    .replace(/\b(s\.?a\.?r\.?l\.?|sàrl|sarl|s\.?a\.?|ag|gmbh|inc\.?|llc\.?|ltd\.?|s\.?à\.?r\.?l\.?|sas|sasu|eurl|ei|auto[-\s]?entrepreneur)\b\.?/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function getSuggestedSearchUrl(network, companyName) {
  if (!companyName) return null;
  const clean = stripLegalSuffix(companyName);
  const name = (clean && clean.length >= 2 ? clean : companyName).trim();
  // Google search ciblé par domaine — fonctionne même sans être connecté
  switch (network) {
    case 'facebook':  return `https://www.google.com/search?q=site%3Afacebook.com+%22${encodeURIComponent(name)}%22`;
    case 'instagram': return `https://www.google.com/search?q=site%3Ainstagram.com+%22${encodeURIComponent(name)}%22`;
    case 'linkedin':  return `https://www.google.com/search?q=site%3Alinkedin.com%2Fcompany+%22${encodeURIComponent(name)}%22`;
    default: return null;
  }
}

function buildAuditStrategy(audit) {
  function guessChannel(text = '', sectionTitle = '') {
    const t = (text + ' ' + sectionTitle).toLowerCase();
    if (t.includes('email') || t.includes('newsletter') || t.includes('mail')) return 'email';
    if (t.includes('sms') || t.includes('message') || t.includes('texte')) return 'sms';
    if (t.includes('appel') || t.includes('téléphone') || t.includes('call')) return 'phone';
    if (t.includes('social') || t.includes('facebook') || t.includes('instagram') || t.includes('linkedin') || t.includes('google') || t.includes('réseau')) return 'social';
    return 'email';
  }

  const sections = audit.sections || [];
  const critSections = sections.filter(s => s.status === 'critical' || s.score < 40);
  const warnSections = sections.filter(s => s.status === 'warning' && s.score >= 40);
  const otherSections = sections.filter(s => !critSections.includes(s) && !warnSections.includes(s));

  // Phase 1 — Actions prioritaires (critiques)
  const p1Actions = [];
  if (audit.top_priority) {
    p1Actions.push({
      id: 'audit-p1-priority',
      ch: guessChannel(audit.top_priority),
      title: audit.top_priority,
      desc: 'Priorité #1 identifiée par l\'audit Appee',
      appee: true, done: false,
    });
  }
  critSections.forEach((sec, si) => {
    (sec.actions || []).slice(0, 2).forEach((action, ai) => {
      const id = `audit-p1-${si}-${ai}`;
      if (!p1Actions.find(a => a.title === action)) {
        p1Actions.push({ id, ch: guessChannel(action, sec.title), title: action, desc: `${sec.title} · Score actuel : ${sec.score}/100`, appee: false, done: false });
      }
    });
  });

  // Phase 2 — Améliorations ciblées (warnings)
  const p2Actions = [];
  warnSections.forEach((sec, si) => {
    (sec.actions || []).slice(0, 2).forEach((action, ai) => {
      p2Actions.push({ id: `audit-p2-${si}-${ai}`, ch: guessChannel(action, sec.title), title: action, desc: `${sec.title} · Score actuel : ${sec.score}/100`, appee: false, done: false });
    });
  });
  if (p2Actions.length === 0) {
    p2Actions.push({ id: 'audit-p2-review', ch: 'email', title: 'Réviser la stratégie de communication', desc: 'Identifier les axes d\'amélioration supplémentaires', appee: false, done: false });
  }

  // Phase 3 — Consolidation & mesure
  const p3Actions = [];
  otherSections.forEach((sec, si) => {
    (sec.actions || []).slice(0, 1).forEach((action, ai) => {
      p3Actions.push({ id: `audit-p3-${si}-${ai}`, ch: guessChannel(action, sec.title), title: action, desc: `${sec.title} · Score actuel : ${sec.score}/100`, appee: false, done: false });
    });
  });
  const targetScore = Math.min(100, (audit.overall_score || 50) + 20);
  p3Actions.push({ id: 'audit-p3-reaudit', ch: 'email', title: 'Relancer un audit marketing complet', desc: `Mesurer les progrès et viser ${targetScore}/100`, appee: true, done: false });

  const phases = [];
  if (p1Actions.length > 0) phases.push({ id: 'audit-p1', name: '🚨 Actions prioritaires', week: 'Sem. 1–3', actions: p1Actions });
  if (p2Actions.length > 0) phases.push({ id: 'audit-p2', name: '📈 Améliorations ciblées', week: 'Sem. 4–8', actions: p2Actions });
  phases.push({ id: 'audit-p3', name: '✨ Consolidation & mesure', week: 'Sem. 9–12', actions: p3Actions });

  const id = `audit-strategy-${Date.now()}`;
  return {
    id,
    instanceId: id,
    category: 'custom',
    icon: '📊',
    color: '#7C3AED',
    title: '📊 Plan d\'amélioration marketing',
    subtitle: `Basé sur l'audit du ${new Date(audit.audited_at).toLocaleDateString('fr-CH')} · Score initial : ${audit.overall_score}/100`,
    duration: '3 mois',
    source: 'audit',
    auditScore: audit.overall_score,
    auditDate: audit.audited_at,
    launchedAt: new Date().toISOString(),
    currentPhase: 0,
    kpis: [],
    contacts_fn: () => 0,
    phases,
  };
}

export default function Settings() {
  const { user, setUser, tenant, setTenant, smtp, setSmtp, addNotification, smtpHealth, setContacts, reminders, setReminders, companyProfile, saveCompanyProfile, personalProfile, savePersonalProfile, collaborators, setCollaborators, setAutoSendEnabled, expertMode, toggleExpertMode } = useApp();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // alias : d'anciens liens pointent ?tab=company → même onglet que 'entreprise'
  const [tab, setTab]         = useState(() => {
    const t = searchParams.get('tab');
    return t === 'company' ? 'entreprise' : (t || 'entreprise');
  });
  // Resynchronise l'onglet quand l'URL ?tab change — indispensable pour que les liens
  // « Paramètres » (→ entreprise) et « Mon profil » (→ profile) du menu switchent même
  // quand on est DÉJÀ sur la page (useState ne relit pas tout seul).
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t) setTab(t === 'company' ? 'entreprise' : t);
  }, [searchParams]);
  const [billing, setBilling] = useState(null);

  // 🎯 Deep-link champ précis : /settings?tab=entreprise&focus=logo_url
  // → scrolle jusqu'au champ et le fait clignoter (liens « Renseigner » de la Home)
  useEffect(() => {
    const focus = searchParams.get('focus');
    if (!focus) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-focus-key="${focus}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('field-flash');
      setTimeout(() => el.classList.remove('field-flash'), 3000);
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Retour de Stripe Checkout (?billing=success|cancel)
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const b = q.get('billing');
    if (!b) return;
    if (b === 'success') addNotification('✅ Paiement confirmé ! Ton compte se met à jour dans la minute.');
    if (b === 'cancel')  addNotification("Paiement annulé — rien n'a été débité.", 'info');
    setTab('plan');
    window.history.replaceState({}, '', '/settings?tab=plan');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // État facturation (statut abonnement, catalogue) à l'ouverture de l'onglet Plan
  useEffect(() => {
    if (tab !== 'plan') return;
    api.getBilling().then(setBilling).catch(() => {});
  }, [tab]);

  // Redirige vers Stripe Checkout (plan ou pack). Rien n'est débité sans
  // passer par la page de paiement Stripe.
  const startCheckout = async (kind, a, b2) => {
    try {
      const res = kind === 'plan' ? await api.checkoutPlan(a) : await api.checkoutPack(a, b2);
      if (res?.url) { window.location.href = res.url; return; }
      addNotification('Paiement indisponible pour le moment.', 'error');
    } catch (err) {
      addNotification(err.message || 'Paiement indisponible pour le moment.', 'error');
    }
  };
  const [smtpForm, setSmtpForm] = useState(() => {
    const base = smtp || {};
    return {
      host:       base.host       || '',
      port:       base.port       ? parseInt(base.port, 10) : 587,
      username:   base.username   || '',
      password:   base.password   || '',
      encryption: base.encryption || 'tls',
      from_name:  base.from_name  || '',
      from_email: base.from_email || '',
    };
  });
  const [testing, setTesting]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [testEmail, setTestEmail] = useState(user.email);
  const [smtpErr, setSmtpErr]   = useState('');
  const [healthLoading, setHealthLoading] = useState(false);
  const [localHealth, setLocalHealth]     = useState(smtpHealth?.ok ? smtpHealth : null);

  // ── Smart SMTP detection ──────────────────────────────────────────────────
  const [detectEmail, setDetectEmail]     = useState(smtp?.from_email || smtp?.username || '');
  const [detectPassword, setDetectPassword] = useState('');
  const [detecting, setDetecting]         = useState(false);
  const [detected, setDetected]           = useState(null);   // { label, provider, config }
  const [detectErr, setDetectErr]         = useState('');
  const [showAdvanced, setShowAdvanced]   = useState(false);
  const [smartTesting, setSmartTesting]   = useState(false);
  const [smartTestResult, setSmartTestResult] = useState(null);

  const handleDetect = async () => {
    if (!detectEmail) return;
    setDetecting(true); setDetectErr(''); setDetected(null);
    try {
      const res = await api.detectSmtp(detectEmail);
      if (res.detected) {
        setDetected(res);
        setSmtpForm(f => ({
          ...f,
          host:       res.config.host,
          port:       res.config.port,
          encryption: res.config.encryption,
          username:   detectEmail,
          from_email: detectEmail,
          from_name:  f.from_name || tenant?.name || '',
        }));
      } else {
        setDetectErr(res.message || 'Fournisseur non reconnu — configurez manuellement.');
        setShowAdvanced(true);
      }
    } catch { setDetectErr('Erreur lors de la détection.'); }
    finally { setDetecting(false); }
  };

  const handleSmartSave = async () => {
    setSmartTesting(true); setSmartTestResult(null); setSmtpErr('');
    try {
      const payload = {
        host:       smtpForm.host,
        port:       smtpForm.port,
        username:   smtpForm.username || detectEmail,
        password:   detectPassword,
        encryption: smtpForm.encryption,
        from_email: smtpForm.from_email || detectEmail,
        from_name:  smtpForm.from_name  || tenant?.name || 'Appee',
      };
      await api.saveSmtp(payload);
      await api.testSmtp(testEmail);
      setSmartTestResult({ ok: true });
      addNotification('✅ SMTP configuré et vérifié !');
    } catch (err) {
      setSmartTestResult({ ok: false, message: err.message });
    } finally { setSmartTesting(false); }
  };
  const handleDeleteAllContacts = async () => {
    if (!window.confirm('Supprimer définitivement tous les contacts ? Cette action est irréversible.')) return;
    try {
      const res = await api.deleteAllContacts();
      setContacts([]);
      addNotification(`🗑 ${res?.message ?? 'Tous les contacts supprimés.'}`);
    } catch (err) {
      addNotification(`Erreur : ${err.message}`, 'error');
    }
  };

  // §17 — Profil contrôlé
  const [profileForm, setProfileForm] = useState({
    name:    user?.name          || '',
    email:   user?.email         || '',
    company: user?.tenant?.name  || '',
    mobile:  user?.mobile        || '',
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const setP = (k, v) => setProfileForm(f => ({ ...f, [k]: v }));

  const handleSaveProfile = async () => {
    if (!profileForm.name?.trim() || !profileForm.email?.trim()) {
      addNotification('Nom et e-mail sont requis.', 'error'); return;
    }
    setProfileSaving(true);
    try {
      const updated = await api.updateProfile(profileForm);
      setUser(u => ({ ...u, ...updated }));
      setProfileForm(f => ({
        ...f,
        name:    updated.name          || f.name,
        email:   updated.email         || f.email,
        company: updated.tenant?.name  || f.company,
        mobile:  updated.mobile        ?? f.mobile,
      }));
      addNotification('Profil mis à jour ✅');
    } catch {
      addNotification('Impossible de sauvegarder le profil.', 'error');
    } finally { setProfileSaving(false); }
  };

  // §19 — Profil entreprise
  const [companyForm, setCompanyForm] = React.useState(companyProfile || {});
  const [fetchingBrandColors, setFetchingBrandColors] = React.useState(false);
  const [brandColorProposal, setBrandColorProposal] = React.useState(null); // aperçu AVANT application
  const [companySaving, setCompanySaving] = React.useState(false);
  const [uploadingLogo, setUploadingLogo] = React.useState(false);
  const [analyzingSocial, setAnalyzingSocial] = React.useState({});
  const [analyzingWebsite, setAnalyzingWebsite] = React.useState(false);
  const [websiteAnalysis, setWebsiteAnalysis] = React.useState(null);
  const [socialSearchQuery, setSocialSearchQuery] = React.useState({});
  const [socialSearching, setSocialSearching] = React.useState({});
  const [socialCandidates, setSocialCandidates] = React.useState({});
  const [auditLoading, setAuditLoading] = React.useState(false);
  const [auditLoadingMsg, setAuditLoadingMsg] = React.useState('');
  const [showAuditResult, setShowAuditResult] = React.useState(false);
  const [expandedSocial, setExpandedSocial] = React.useState({});
  const [auditHistory, setAuditHistory] = React.useState([]);
  const [creatingAuditStrat, setCreatingAuditStrat] = React.useState(false);
  const [expandedAuditIdx, setExpandedAuditIdx] = React.useState(0);

  React.useEffect(() => {
    if (companyProfile && Object.keys(companyProfile).length > 0) {
      setCompanyForm(f => ({
        ...companyProfile,
        gmb_has_page: companyProfile.gmb_has_page ?? null,
      }));
      if (companyProfile.marketing_audit) setShowAuditResult(true);
    }
  }, [companyProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    api.getAuditHistory().then(data => {
      setAuditHistory(data.history || []);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const setC = (k, v) => setCompanyForm(p => ({ ...p, [k]: v }));
  const saveCompany = async (overrideForm) => {
    setCompanySaving(true);
    try {
      await saveCompanyProfile(overrideForm || companyForm);
      addNotification('Profil entreprise sauvegardé ✓', 'success');
    } catch {
      addNotification('Erreur lors de la sauvegarde.', 'error');
    } finally { setCompanySaving(false); }
  };

  const handleLogoUpload = async (file) => {
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const res = await api.uploadLogo(fd);
      setC('logo_url', res.url);
      addNotification('Logo uploadé ✓');
    } catch { addNotification('Erreur upload logo', 'error'); }
    finally { setUploadingLogo(false); }
  };

  const handleAnalyzeSocial = async (network) => {
    setAnalyzingSocial(s => ({ ...s, [network]: true }));
    try {
      const url = network === 'google_mybusiness'
        ? (companyForm.google_mybusiness_url || '')
        : (companyForm[`${network}_url`] || '');
      const res = await api.analyzeSocial(network, url);
      // Conserver l'image de profil déjà stockée si l'analyse n'en remonte pas une
      const existingImage = companyForm.social_analysis?.[network]?.profile_image;
      setC('social_analysis', {
        ...(companyForm.social_analysis || {}),
        [network]: {
          ...res,
          profile_image: res.profile_image || existingImage || null,
          analyzed_at: new Date().toISOString(),
        },
      });
      addNotification(`Analyse ${network} terminée ✓`);
    } catch (err) {
      const msg = err?.message || 'Erreur analyse';
      addNotification(msg.includes('quota') ? '⚠️ Quota IA dépassé — analyse impossible' : `Erreur analyse ${network} : ${msg}`, 'error');
    }
    finally { setAnalyzingSocial(s => ({ ...s, [network]: false })); }
  };

  const handleFindSocialPage = async (network, overrideQuery) => {
    const query = (overrideQuery || socialSearchQuery[network] || '').trim();
    if (!query) return;
    setSocialSearching(s => ({ ...s, [network]: true }));
    setSocialCandidates(s => ({ ...s, [network]: null }));
    try {
      const res = await api.findSocialPage(network, query);
      setSocialCandidates(s => ({ ...s, [network]: res.candidates || [] }));
    } catch {
      addNotification('Erreur lors de la recherche', 'error');
      setSocialCandidates(s => ({ ...s, [network]: [] }));
    }
    finally { setSocialSearching(s => ({ ...s, [network]: false })); }
  };

  const handleSelectSocialCandidate = (network, urlKey, url, image) => {
    setC(urlKey, url);
    setSocialCandidates(s => ({ ...s, [network]: null }));
    setSocialSearchQuery(s => ({ ...s, [network]: '' }));
    // Pré-stocker l'image de profil pour affichage immédiat
    if (image) {
      setC('social_analysis', {
        ...(companyForm.social_analysis || {}),
        [network]: { ...(companyForm.social_analysis?.[network] || {}), profile_image: image },
      });
    } else if (companyForm.social_analysis?.[network]) {
      const newA = { ...companyForm.social_analysis };
      delete newA[network];
      setC('social_analysis', newA);
    }
    // Auto-trigger analysis after selection
    setTimeout(() => handleAnalyzeSocial(network), 300);
  };

  const handleAnalyzeWebsite = async () => {
    const url = companyForm.website_url || companyForm.website;
    if (!url) return;
    setAnalyzingWebsite(true);
    setWebsiteAnalysis(null);
    try {
      const res = await api.analyzeWebsite(url);

      // Génère analyse + conseil depuis les indicateurs si le backend ne les retourne pas
      if (res && res.indicators?.length > 0 && !res.analysis) {
        const inds = res.indicators;
        const criticals = inds.filter(i => i.status === 'critical');
        const warnings  = inds.filter(i => i.status === 'warning');
        const goods     = inds.filter(i => i.status === 'good');
        if (criticals.length === 0 && warnings.length === 0) {
          res.analysis = `Excellent — votre site affiche une très bonne santé technique. Les ${goods.length} indicateurs analysés sont au vert : mobile-responsive, HTTPS, SEO, Open Graph et données structurées sont correctement configurés.`;
        } else if (criticals.length > 0) {
          const pts = criticals.map(i => `${i.label.toLowerCase()} (${i.value})`).join(' ; ');
          res.analysis = `L'audit détecte ${criticals.length} point(s) critique(s) impactant directement votre visibilité : ${pts}. Ces éléments doivent être traités en priorité.`;
        } else {
          const pts = warnings.map(i => i.label.toLowerCase()).join(' ; ');
          res.analysis = `Votre site présente une base correcte (${goods.length} points positifs), mais ${warnings.length} élément(s) méritent attention : ${pts}.`;
        }
        const https  = inds.find(i => i.label === 'HTTPS / Sécurité');
        const mobile = inds.find(i => i.label === 'Mobile responsive');
        const seo    = inds.find(i => i.label === 'SEO de base');
        const og     = inds.find(i => i.label === 'Partage réseaux sociaux');
        const struct = inds.find(i => i.label === 'Données structurées');
        const weight = inds.find(i => i.label === 'Poids de la page');
        if (https?.status === 'critical') {
          res.conseil = "Activez HTTPS en priorité absolue — c'est un facteur de classement Google et un signal de confiance essentiel pour vos visiteurs.";
        } else if (mobile?.status === 'critical') {
          res.conseil = "Rendez votre site mobile-responsive — plus de 60 % du trafic vient des smartphones et Google pénalise les sites non adaptés (Mobile-First Indexing).";
        } else if (seo?.status !== 'good') {
          res.conseil = `Complétez les éléments SEO manquants — ${seo?.hint || 'titre, description, H1, canonical'} — c'est la base pour être trouvé sur Google.`;
        } else if (og?.status !== 'good') {
          res.conseil = "Ajoutez les balises Open Graph (og:title, og:description, og:image) pour que vos partages sur Facebook, LinkedIn et WhatsApp soient visuellement attractifs.";
        } else if (struct?.status !== 'good') {
          res.conseil = "Ajoutez des données structurées Schema.org (JSON-LD) pour aider Google à comprendre votre activité et obtenir des résultats enrichis dans les SERPs.";
        } else if (weight?.status === 'critical') {
          res.conseil = `Optimisez le poids de votre page (${weight?.value}) — compressez vos images (WebP), minifiez CSS/JS pour améliorer la vitesse de chargement.`;
        } else {
          res.conseil = "Bonne base technique ! Consultez vos Core Web Vitals dans Google Search Console et maintenez votre contenu régulièrement mis à jour.";
        }
      }

      setWebsiteAnalysis(res);
      // Auto-remplir les champs sociaux trouvés sur le site
      if (res.social_links) {
        if (res.social_links.facebook  && !companyForm.facebook_url)  setC('facebook_url',  res.social_links.facebook);
        if (res.social_links.instagram && !companyForm.instagram_url) setC('instagram_url', res.social_links.instagram);
        if (res.social_links.linkedin  && !companyForm.linkedin_url)  setC('linkedin_url',  res.social_links.linkedin);
        if (res.social_links.google_mybusiness && !companyForm.google_mybusiness_url) {
          setC('google_mybusiness_url', res.social_links.google_mybusiness);
          setC('gmb_has_page', true);
        }
      }
      if (res.social_links && Object.keys(res.social_links).length > 0) {
        addNotification(`✅ ${Object.keys(res.social_links).length} réseau(x) détecté(s) sur le site`);
      } else {
        addNotification('Analyse terminée — aucun réseau social détecté sur le site');
      }
    } catch { addNotification('Erreur analyse site', 'error'); }
    finally { setAnalyzingWebsite(false); }
  };

  const handleMarketingAudit = async () => {
    setAuditLoading(true);
    setShowAuditResult(false);
    const msgs = ['Analyse de votre positionnement…', 'Évaluation de votre présence digitale…', 'Identification des opportunités…', 'Rédaction du rapport…'];
    let i = 0;
    setAuditLoadingMsg(msgs[0]);
    const interval = setInterval(() => { i = (i + 1) % msgs.length; setAuditLoadingMsg(msgs[i]); }, 2500);
    try {
      const res = await api.marketingAudit();
      clearInterval(interval);
      const auditWithTs = { ...res, audited_at: new Date().toISOString() };
      setC('marketing_audit', auditWithTs);
      setShowAuditResult(true);
      await saveCompanyProfile({ ...companyForm, marketing_audit: auditWithTs });
      addNotification('Audit terminé ✓');
    } catch { clearInterval(interval); addNotification('Erreur audit', 'error'); }
    finally { setAuditLoading(false); }
  };

  const handleCreateAuditStrategy = async () => {
    if (!companyForm.marketing_audit) return;
    setCreatingAuditStrat(true);
    try {
      // L'IA génère les phases avec les bons canaux (landing pour SEO, social pour réseaux, etc.)
      addNotification('⏳ L\'IA prépare votre plan d\'action…');
      const res = await api.generateAuditStrategy(companyForm.marketing_audit);

      const audit = companyForm.marketing_audit;
      const id = `audit-strategy-${Date.now()}`;
      const strat = {
        id,
        instanceId: id,
        category: 'custom',
        icon: '📊',
        color: '#7C3AED',
        title: '📊 Plan d\'amélioration marketing',
        subtitle: `Basé sur l'audit du ${new Date(audit.audited_at).toLocaleDateString('fr-CH')} · Score initial : ${audit.overall_score}/100`,
        duration: '3 mois',
        source: 'audit',
        auditScore: audit.overall_score,
        auditDate: audit.audited_at,
        launchedAt: new Date().toISOString(),
        currentPhase: 0,
        kpis: [],
        phases: res.phases || [],
      };

      await api.saveStrategyInstance(strat);
      addNotification('✅ Stratégie créée ! Redirection vers vos stratégies…');
      setTimeout(() => navigate('/strategy'), 1200);
    } catch (e) {
      addNotification('Erreur lors de la création de la stratégie', 'error');
    } finally {
      setCreatingAuditStrat(false);
    }
  };

  // Score IA Readiness — source unique partagée (utils/companyScore.js).

  // §20 — Profil Personnel / Famille
  const [personalForm, setPersonalForm] = React.useState(personalProfile || {});
  const [personalSaving, setPersonalSaving] = React.useState(false);
  React.useEffect(() => {
    if (personalProfile && Object.keys(personalProfile).length > 0) {
      setPersonalForm(personalProfile);
    }
  }, [personalProfile]); // eslint-disable-line react-hooks/exhaustive-deps
  const setPers = (k, v) => setPersonalForm(p => ({ ...p, [k]: v }));
  const setChild = (idx, k, v) => setPersonalForm(p => {
    const children = [...(p.children || [])];
    children[idx] = { ...children[idx], [k]: v };
    return { ...p, children };
  });
  const addChild = () => setPersonalForm(p => ({ ...p, children: [...(p.children || []), { name: '', birthdate: '' }] }));
  const removeChild = (idx) => setPersonalForm(p => ({ ...p, children: (p.children || []).filter((_, i) => i !== idx) }));

  const deletePartner = async () => {
    const updated = { ...personalForm, partner_name: '', partner_birthdate: '', partner_gender: '' };
    setPersonalForm(updated);
    try { await savePersonalProfile(updated); addNotification('Conjoint(e) supprimé', 'success'); } catch {}
  };
  const deleteChildAndSave = async (idx) => {
    const updated = { ...personalForm, children: (personalForm.children || []).filter((_, i) => i !== idx) };
    setPersonalForm(updated);
    try { await savePersonalProfile(updated); addNotification('Enfant supprimé', 'success'); } catch {}
  };

  const savePersonal = async () => {
    setPersonalSaving(true);
    try {
      await savePersonalProfile(personalForm);
      addNotification('Profil personnel sauvegardé ✓', 'success');
    } catch {
      addNotification('Erreur lors de la sauvegarde.', 'error');
    } finally { setPersonalSaving(false); }
  };

  // §21 — Collaborateurs
  const [collabForm, setCollabForm]     = React.useState({ name: '', role: '', email: '', phone: '', birthdate: '' });
  const [editingCollab, setEditingCollab] = React.useState(null); // null | 'new' | id
  const [collabSaving, setCollabSaving]   = React.useState(false);
  const setCol = (k, v) => setCollabForm(f => ({ ...f, [k]: v }));

  const startNewCollab = () => { setCollabForm({ name: '', role: '', email: '', phone: '', birthdate: '' }); setEditingCollab('new'); };
  const startEditCollab = (c) => { setCollabForm({ ...c }); setEditingCollab(c.id); };
  const cancelCollab = () => { setEditingCollab(null); };

  const saveCollab = async () => {
    if (!collabForm.name.trim()) return;
    setCollabSaving(true);
    try {
      if (editingCollab === 'new') {
        const created = await api.createCollaborator(collabForm);
        setCollaborators(cs => [...cs, created]);
        addNotification(`${collabForm.name} ajouté(e) à l'équipe ✅`);
      } else {
        const updated = await api.updateCollaborator(editingCollab, collabForm);
        setCollaborators(cs => cs.map(c => c.id === editingCollab ? updated : c));
        addNotification('Collaborateur mis à jour ✅');
      }
      setEditingCollab(null);
    } catch (err) {
      addNotification(`Erreur : ${err.message}`, 'error');
    } finally { setCollabSaving(false); }
  };

  const deleteCollab = async (id, name) => {
    if (!window.confirm(`Supprimer ${name} de l'équipe ?`)) return;
    try {
      await api.deleteCollaborator(id);
      setCollaborators(cs => cs.filter(c => c.id !== id));
      addNotification('Collaborateur supprimé.');
    } catch (err) {
      addNotification(`Erreur : ${err.message}`, 'error');
    }
  };

  // §18 — Sécurité : changement de mot de passe
  const [pwForm, setPwForm] = useState({ current: '', password: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const setPw = (k, v) => setPwForm(f => ({ ...f, [k]: v }));

  const handleChangePassword = async () => {
    if (!pwForm.current) { addNotification('Saisissez votre mot de passe actuel.', 'error'); return; }
    if (pwForm.password.length < 8) { addNotification('Le nouveau mot de passe doit faire au moins 8 caractères.', 'error'); return; }
    if (pwForm.password !== pwForm.confirm) { addNotification('Les mots de passe ne correspondent pas.', 'error'); return; }
    setPwSaving(true);
    try {
      await api.changePassword({ current_password: pwForm.current, password: pwForm.password, password_confirmation: pwForm.confirm });
      addNotification('Mot de passe modifié avec succès ✅');
      setPwForm({ current: '', password: '', confirm: '' });
    } catch (err) {
      addNotification(err.message || 'Erreur lors du changement de mot de passe.', 'error');
    } finally { setPwSaving(false); }
  };

  // Suppression de compte (droit à l'effacement nLPD/RGPD) — confirmation forte
  const [delAccountOpen, setDelAccountOpen]   = useState(false);
  const [delAccountInput, setDelAccountInput] = useState('');
  const [delAccountBusy, setDelAccountBusy]   = useState(false);
  const handleDeleteAccount = async () => {
    setDelAccountBusy(true);
    try {
      await api.deleteAccount(delAccountInput.trim());
      localStorage.removeItem('appee_token');
      window.location.href = '/login';
    } catch (err) {
      addNotification(err.message || 'Suppression impossible.', 'error');
      setDelAccountBusy(false);
    }
  };

  // Portail de facturation Stripe (résiliation / changement de plan / factures)
  const [portalBusy, setPortalBusy] = useState(false);
  const openBillingPortal = async () => {
    setPortalBusy(true);
    try {
      const res = await api.billingPortal();
      if (res?.url) { window.location.href = res.url; return; }
      addNotification('Portail indisponible — écris-nous à hello@beeappee.com.', 'error');
    } catch (err) {
      addNotification(err.message || 'Portail indisponible — écris-nous à hello@beeappee.com.', 'error');
    } finally { setPortalBusy(false); }
  };

  const [adminForm, setAdminForm] = useState({
    email: '', company_name: '', report_time: '07:55', send_time: '07:00', auto_send_enabled: true, birthday_fallback_mailjet: false,
    email_mode: '', sender_name: '', reply_to: '',
  });
  const [showFallbackModal, setShowFallbackModal] = useState(false);
  const [adminSaving, setAdminSaving] = useState(false);
  const [adminLoaded, setAdminLoaded] = useState(false);
  // Mode d'envoi effectif : 'auto' (tout via Appee) ou 'own' (SMTP perso).
  const emailMode = adminForm.email_mode || (smtp?.verified ? 'own' : 'auto');
  const [autoEditing, setAutoEditing] = useState(false);
  const [smtpEditing, setSmtpEditing] = useState(false);

  // SMS
  const [smsForm, setSmsForm]           = useState({ sender: 'Appee', birthday_sms_enabled: false });
  const [smsSaving, setSmsSaving]       = useState(false);
  const [smsTestPhone, setSmsTestPhone] = useState('');
  const [smsTestLoading, setSmsTestLoading] = useState(false);
  const [smsTestResult, setSmsTestResult]   = useState(null);

  // Rappels
  const [reminderEmail, setReminderEmail]     = useState(user.email);
  const [reminderPhone, setReminderPhone]     = useState('');
  const [editingReminder, setEditingReminder] = useState(() =>
    searchParams.get('new') === '1' ? '__new__' : null
  );
  const [reminderForm, setReminderForm]       = useState(() => {
    if (searchParams.get('new') === '1') {
      const r = emptyReminder(); return r;
    }
    return null;
  });

  const startAddReminder = () => {
    const r = emptyReminder();
    setEditingReminder(r.id);
    setReminderForm(r);
  };
  const startEditReminder = (r) => {
    setEditingReminder(r.id);
    setReminderForm({
      ...r,
      channels:          Array.isArray(r.channels)          ? r.channels          : ['email'],
      extra_recipients:  Array.isArray(r.extra_recipients)  ? r.extra_recipients  : [],
    });
  };
  const cancelReminder = () => { setEditingReminder(null); setReminderForm(null); };
  const setRF = (k, v) => setReminderForm(f => ({ ...f, [k]: v }));

  const [reminderSaving, setReminderSaving] = useState(false);

  const saveReminder = async () => {
    if (!reminderForm.label.trim()) return;
    setReminderSaving(true);
    try {
      const exists = reminders.find(r => r.id === reminderForm.id);
      if (exists) {
        const updated = await api.updateReminder(reminderForm.id, reminderForm);
        // Normalise channels en cas de retour null depuis l'API
        const normalized = { ...updated, channels: Array.isArray(updated.channels) ? updated.channels : reminderForm.channels || ['email'] };
        setReminders(rs => rs.map(r => r.id === reminderForm.id ? normalized : r));
        addNotification('Rappel mis à jour ✅');
      } else {
        const created = await api.createReminder(reminderForm);
        setReminders(rs => [...rs, created]);
        addNotification('Rappel ajouté ✅');
      }
      setEditingReminder(null);
      setReminderForm(null);
    } catch (err) {
      addNotification(`Erreur : ${err.message}`, 'error');
    } finally {
      setReminderSaving(false);
    }
  };

  const deleteReminder = async (id) => {
    if (!window.confirm('Supprimer ce rappel ?')) return;
    try {
      await api.deleteReminder(id);
      setReminders(rs => rs.filter(r => r.id !== id));
      addNotification('Rappel supprimé.');
    } catch (err) {
      addNotification(`Erreur : ${err.message}`, 'error');
    }
  };

  const toggleReminder = async (id) => {
    const r = reminders.find(x => x.id === id);
    if (!r) return;
    try {
      const updated = await api.updateReminder(id, { enabled: !r.enabled });
      setReminders(rs => rs.map(x => x.id === id ? updated : x));
    } catch (err) {
      addNotification(`Erreur : ${err.message}`, 'error');
    }
  };

  useEffect(() => { setLocalHealth(smtpHealth); }, [smtpHealth]);

  useEffect(() => {
    if ((tab === 'reports' || tab === 'reminders' || tab === 'smtp') && !adminLoaded) {
      api.getAdmin().then(cfg => {
        if (cfg) {
          setAdminForm(f => ({ ...f, ...cfg }));
          if (cfg.reminder_email) setReminderEmail(cfg.reminder_email);
          if (cfg.reminder_phone) setReminderPhone(cfg.reminder_phone);
        }
        setAdminLoaded(true);
      }).catch(() => setAdminLoaded(true));
    }
  }, [tab, adminLoaded]);

  // Auto-refresh reminders every 60s when on reminders tab (to update sent status)
  useEffect(() => {
    if (tab !== 'reminders') return;
    const interval = setInterval(() => {
      api.getReminders().then(data => { if (Array.isArray(data)) setReminders(data); }).catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, [tab]);

  // Rafraîchit les données d'usage (dont ai_used + sms_credits) à chaque ouverture de l'onglet Plan
  useEffect(() => {
    if (tab !== 'plan') return;
    api.getPlan().then(data => {
      if (!data) return;
      setTenant(t => t ? ({
        ...t,
        contacts_used:     data.contacts_used     ?? t.contacts_used,
        contacts_limit:    data.contacts_limit    ?? t.contacts_limit,
        emails_used:       data.emails_used       ?? t.emails_used,
        emails_limit:      data.emails_limit      ?? t.emails_limit,
        ai_used:           data.ai_used           ?? t.ai_used,
        ai_limit:          data.ai_limit          ?? t.ai_limit,
        sms_credits:       data.sms_credits       ?? t.sms_credits,
        sms_plan_included: data.sms_plan_included ?? t.sms_plan_included,
        sms_packs:         data.sms_packs         ?? t.sms_packs,
      }) : t);
    }).catch(() => {});
  }, [tab]);

  // Charger settings SMS à l'ouverture de l'onglet SMS
  useEffect(() => {
    if (tab !== 'sms') return;
    api.getSmsSettings().then(data => {
      if (data && Object.keys(data).length > 0) setSmsForm(data);
    }).catch(() => {});
  }, [tab]);

  // ── WhatsApp Business ──────────────────────────────────────────────────────
  const [waConfig, setWaConfig]           = useState(null);   // config actuelle
  const [waForm, setWaForm]               = useState({ phone_number_id: '', access_token: '' });
  const [waSaving, setWaSaving]           = useState(false);
  const [waTesting, setWaTesting]         = useState(false);
  const [waTestResult, setWaTestResult]   = useState(null);
  const [waDeleting, setWaDeleting]       = useState(false);

  useEffect(() => {
    if (tab !== 'whatsapp') return;
    api.getWhatsAppSettings().then(data => {
      setWaConfig(data);
      if (data.phone_number_id) setWaForm(f => ({ ...f, phone_number_id: data.phone_number_id }));
    }).catch(() => {});
  }, [tab]);

  const handleSaveWa = async () => {
    if (!waForm.phone_number_id || !waForm.access_token) return;
    setWaSaving(true);
    try {
      await api.saveWhatsAppSettings(waForm);
      addNotification('Identifiants WhatsApp enregistrés ✅');
      setWaForm(f => ({ ...f, access_token: '' })); // effacer le token du form
      const fresh = await api.getWhatsAppSettings();
      setWaConfig(fresh);
    } catch { addNotification('Erreur lors de la sauvegarde', 'error'); }
    finally { setWaSaving(false); }
  };

  const handleTestWa = async () => {
    setWaTesting(true); setWaTestResult(null);
    try {
      const res = await api.testWhatsAppConnection();
      setWaTestResult(res);
      if (res.ok) {
        const fresh = await api.getWhatsAppSettings();
        setWaConfig(fresh);
        addNotification(`WhatsApp connecté : ${res.display_name} ✅`);
      }
    } catch { setWaTestResult({ ok: false, message: 'Erreur de connexion' }); }
    finally { setWaTesting(false); }
  };

  const handleDeleteWa = async () => {
    if (!window.confirm('Déconnecter WhatsApp Business ? Vous ne pourrez plus envoyer de messages WhatsApp.')) return;
    setWaDeleting(true);
    try {
      await api.deleteWhatsAppSettings();
      setWaConfig(null);
      setWaForm({ phone_number_id: '', access_token: '' });
      setWaTestResult(null);
      addNotification('WhatsApp déconnecté');
    } catch { addNotification('Erreur', 'error'); }
    finally { setWaDeleting(false); }
  };

  const handleSaveSms = async () => {
    setSmsSaving(true);
    try {
      await api.saveSmsSettings(smsForm);
      addNotification('Configuration SMS enregistrée ✅');
    } catch { addNotification('Erreur lors de la sauvegarde SMS', 'error'); }
    finally { setSmsSaving(false); }
  };

  const handleTestSms = async () => {
    setSmsTestLoading(true);
    setSmsTestResult(null);
    try {
      const res = await api.testSmsSend(smsTestPhone);
      setSmsTestResult(res);
    } catch { setSmsTestResult({ success: false, error: 'Erreur de connexion' }); }
    finally { setSmsTestLoading(false); }
  };

  const setS  = (k, v) => setSmtpForm(f => ({ ...f, [k]: v }));
  const setA  = (k, v) => setAdminForm(f => ({ ...f, [k]: v }));

  const buildPayload = () => ({
    host:       (smtpForm.host       || '').trim(),
    port:       parseInt(smtpForm.port, 10) || 587,
    username:   (smtpForm.username   || '').trim(),
    password:   smtpForm.password    || '',
    encryption: smtpForm.encryption  || 'tls',
    from_name:  (smtpForm.from_name  || '').trim(),
    from_email: (smtpForm.from_email || '').trim(),
  });

  const handleSaveSmtp = async () => {
    setSaving(true); setSmtpErr('');
    const payload = buildPayload();
    try {
      await api.saveSmtp(payload);
      setSmtp(s => ({ ...s, ...payload, configured: true, verified: false }));
      setLocalHealth(null);
      setSmtpErr('');
      addNotification('Paramètres SMTP sauvegardés. Cliquez sur "Envoyer test" pour vérifier.');
    } catch (err) {
      setSmtpErr(err.message);
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true); setSmtpErr('');
    const payload = buildPayload();
    try {
      await api.saveSmtp(payload);
      const result = await api.testSmtp(testEmail);
      setSmtp(s => ({ ...s, verified: true }));
      setLocalHealth({ ok: true, last_checked: new Date().toISOString(), error: null });
      addNotification(`✅ ${result.message}`);
    } catch (err) {
      setSmtpErr(`Échec SMTP : ${err.message}`);
      setLocalHealth({ ok: false, last_checked: new Date().toISOString(), error: err.message });
    } finally { setTesting(false); }
  };

  const handleCheckHealth = async () => {
    setHealthLoading(true);
    try {
      const status = await api.getSmtp();
      if (!status?.verified && !status?.last_test_error) {
        setLocalHealth(null);
      } else {
        setLocalHealth({ ok: !!status?.verified, last_checked: status?.verified_at ?? new Date().toISOString(), error: status?.last_test_error ?? null });
      }
    } catch {
      setLocalHealth({ ok: false, error: 'Impossible de vérifier' });
    }
    setHealthLoading(false);
  };

  const handleSaveAdmin = async () => {
    setAdminSaving(true);
    try {
      await api.saveAdmin(adminForm);
      setAutoSendEnabled(adminForm.auto_send_enabled !== false);
      addNotification('Configuration rapports sauvegardée.');
    } catch (err) {
      addNotification(`Erreur : ${err.message}`, 'error');
    } finally { setAdminSaving(false); }
  };

  const handleSendTestReport = async (type) => {
    try {
      if (type === 'daily')    await api.sendDailyReport();
      else if (type === 'birthday') await api.sendBirthdayReport();
      else if (type === 'tracking') await api.sendTrackingReport();
      else await api.sendWeeklyReport();
      addNotification(`Rapport envoyé à ${adminForm.email} ✅`);
    } catch (err) {
      addNotification(`Erreur : ${err.message}`, 'error');
    }
  };

  // ── Badges par catégorie ──────────────────────────────────
  const categoryBadges = React.useMemo(() => {
    const cp = companyProfile || {};
    const badges = {};

    // Mon profil
    const profileMissing = [!user?.first_name, !user?.last_name].filter(Boolean).length;
    if (profileMissing > 0) badges.profil = profileMissing;

    // Mon entreprise
    const entrepriseMissing = [
      !cp.name, !cp.target_audience, !cp.services, !cp.unique_selling_point,
      !cp.communication_tone, !cp.logo_url,
      !cp.facebook_url, !cp.instagram_url, !cp.linkedin_url,
      cp.gmb_has_page !== true,
      !cp.marketing_audit,
    ].filter(Boolean).length;
    if (entrepriseMissing > 0) badges.entreprise = entrepriseMissing;

    // Config : SMTP non configuré
    const smtpMissing = [!smtp?.host, !smtp?.username].filter(Boolean).length;
    if (smtpMissing > 0) badges.config = smtpMissing;

    return badges;
  }, [companyProfile, user, smtp]);

  // ── Pause globale des envois (bouton stop) ──────────────────────────────
  const sendingPaused = !!tenant?.settings?.sending_paused;
  const [savingPause, setSavingPause] = useState(false);
  const togglePause = async (next) => {
    setSavingPause(true);
    try {
      const res = await api.setSending(next);
      setTenant(t => t ? { ...t, settings: { ...(t.settings || {}), sending_paused: res.sending_paused } } : t);
      addNotification(res.message || (next ? 'Envois suspendus.' : 'Envois réactivés.'), next ? 'info' : 'success');
    } catch (err) {
      addNotification(err.message || 'Erreur', 'error');
    } finally {
      setSavingPause(false);
    }
  };

  const CATEGORIES = [
    {
      key: 'profil',
      icon: '👤',
      label: 'Mon profil',
      tabs: [
        { key: 'profile',  icon: '👤', label: 'Profil' },
        { key: 'famille',  icon: '👨‍👩‍👧', label: 'Famille & Perso' },
        { key: 'security', icon: '🔒', label: 'Mot de passe & RGPD' },
      ],
    },
    {
      key: 'entreprise',
      icon: '🏢',
      label: 'Mon entreprise',
      tabs: [
        { key: 'entreprise',     icon: '🏢', label: 'Entreprise' },
        { key: 'collaborateurs', icon: '👥', label: 'Équipe' },
      ],
    },
    {
      key: 'config',
      icon: '⚙️',
      label: 'Configuration',
      tabs: [
        { key: 'smtp',      icon: '📧', label: "Mon adresse d'envoi" },
        { key: 'sms',       icon: '📱', label: 'SMS' },
        { key: 'envois',    icon: '⏸️', label: 'Pause envois' },
        { key: 'reports',   icon: '📊', label: 'Rapports' },
        { key: 'reminders', icon: '🔔', label: 'Rappels' },
      ],
    },
    {
      key: 'compte',
      icon: '💳',
      label: 'Mon abonnement',
      tabs: [
        { key: 'plan',     icon: '📦', label: 'Mon plan' },
      ],
    },
  ];

  const activeCategory = CATEGORIES.find(c => c.tabs.some(t => t.key === tab)) ?? CATEGORIES[0];

  const HERO_CONTENT = {
    profil: {
      title: 'Mon profil',
      text: 'Personnalise ton identité et ajoute tes informations personnelles pour qu\'Appee puisse mieux t\'accompagner au quotidien.',
    },
    entreprise: {
      title: 'Mon entreprise',
      text: 'Renseigne le profil de ton entreprise et gère ton équipe. Ces infos permettent à Appee de personnaliser ses recommandations et de ne jamais rater les grandes dates.',
    },
    config: {
      title: 'Configuration',
      text: 'Configure tes canaux d\'envoi (e-mail, SMS), tes rapports automatiques et tes rappels personnalisés pour ne jamais manquer un moment important.',
    },
    compte: {
      title: 'Mon abonnement',
      text: 'Gère ton plan, consulte ta consommation IA et assure la sécurité de ton compte. Tu es le pilote — Appee s\'adapte à tes besoins.',
    },
  };

  const hero = HERO_CONTENT[activeCategory.key] ?? HERO_CONTENT.profil;

  return (
    <div className="settings-page">
      {/* ── Page hero ── */}
      {(() => {
        const aiScore = calcAiScore(companyForm);
        const aiColor = aiScore >= 75 ? '#10B981' : aiScore >= 40 ? '#F59E0B' : '#EF4444';
        const smtpOk      = localHealth?.ok === true || smtp?.verified;
        const credits     = tenant?.sms_credits ?? 0;
        const creditColor = credits === 0 ? '#EF4444' : credits < 20 ? '#F59E0B' : '#10B981';
        const ctUsed      = tenant?.contacts_used ?? 0;
        return (
          <div className="settings-page-hero">
            <div className="settings-page-hero-nebula" />
            <div className="settings-page-hero-top">
              <div className="settings-page-hero-left">
                <div className="settings-page-hero-icon-wrap">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.95)" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M14 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0"/><path d="M12 2v2m0 16v2M2 12h2m16 0h2m-3.05-6.95-1.41 1.41M6.46 17.54l-1.41 1.41M17.54 17.54l-1.41-1.41M6.46 6.46 5.05 5.05"/></svg>
                </div>
                <div>
                  <h1 className="settings-page-hero-title">Paramètres</h1>
                  <p className="settings-page-hero-sub">Configuration de ton compte Appee</p>
                </div>
              </div>
              <div className="settings-page-hero-kpis">
                <div className="settings-page-hero-kpi" title={`Score IA : ${aiScore}/100`}>
                  <div className="spk-val">{aiScore}/100</div>
                  <div className="spk-lbl">
                    <svg className="spk-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.6 4.8L18 9l-4.4 1.2L12 15l-1.6-4.8L6 9l4.4-1.2z"/></svg>
                    IA Ready
                  </div>
                </div>
                <div className="settings-page-hero-kpi" title={smtpOk ? 'Tes e-mails partent depuis ta propre adresse' : "Adresse d'envoi non configurée — les campagnes partent via Appee (noreply)"}>
                  <div className="spk-val">
                    {smtpOk
                      ? <svg className="spk-val-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                      : '—'}
                  </div>
                  <div className="spk-lbl">
                    <svg className="spk-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>
                    Adresse d'envoi
                  </div>
                </div>
                <div className="settings-page-hero-kpi" title={`${ctUsed} contacts dans la base`}>
                  <div className="spk-val">{ctUsed}</div>
                  <div className="spk-lbl">
                    <svg className="spk-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                    Contacts
                  </div>
                </div>
                <div className="settings-page-hero-kpi" title={`${credits} crédits SMS restants`}>
                  <div className="spk-val">{credits}</div>
                  <div className="spk-lbl">
                    <svg className="spk-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                    Crédits
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <GenieTip
        id="settings"
        title="⚙️ Tes réglages — la base de tout"
        foot={<>Le score « IA Ready » en haut te dit ce qu'il reste à remplir.</>}
      >
        Plus ton profil est complet (logo, couleurs, secteur, panier moyen), plus Appee travaille juste pour TOI et chiffre tes gains en francs.
      </GenieTip>

      {/* ── Hero violet — nav catégories + sous-onglets + texte ── */}
      <div className="settings-hero">

        {/* Barre catégories principale */}
        <div className="settings-hero-cat-bar">
          {CATEGORIES.map(cat => (
            <button
              key={cat.key}
              className={`settings-hero-cat-btn${activeCategory.key === cat.key ? ' active' : ''}`}
              onClick={() => setTab(cat.tabs[0].key)}
              style={{ position: 'relative' }}
            >
              <span>{cat.icon}</span>
              <span>{cat.label}</span>
              {categoryBadges[cat.key] > 0 && (
                <span className="settings-cat-badge">{categoryBadges[cat.key]}</span>
              )}
            </button>
          ))}
        </div>

        {/* Ligne basse : texte à gauche + sous-onglets au centre + mascotte à droite */}
        <div className="settings-hero-bottom">
          <div className="settings-hero-body">
            <div className="settings-hero-title">{hero.title}</div>
            <div className="settings-hero-text">{hero.text}</div>
          </div>

          {activeCategory.tabs.length > 1 && (
            <div className="settings-hero-subtab-bar">
              {activeCategory.tabs.map(t => (
                <button
                  key={t.key}
                  className={`settings-hero-subtab-btn${tab === t.key ? ' active' : ''}`}
                  onClick={() => setTab(t.key)}
                >
                  {t.icon} {t.label}
                </button>
              ))}
            </div>
          )}

        </div>
      </div>

      <div className="settings-layout settings-layout--flat">
        <div className="settings-content">

          {/* ── SMS ── */}
          {tab === 'sms' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Card>
                <CardHeader
                  title="📱 Nom d'expéditeur SMS"
                  subtitle="Ce nom apparaîtra à la place d'un numéro de téléphone sur tous vos SMS"
                />
                <SectionHint>
                  C'est <strong>ton entreprise</strong> que ton client voit comme expéditeur, jamais un numéro inconnu ni « Appee ». Ça rassure, ça fait pro, et ça augmente le taux de lecture.
                </SectionHint>

                {/* Aperçu visuel */}
                <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: '14px 18px', marginBottom: 20, display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                  <div style={{ fontSize: 28 }}>📱</div>
                  <div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 6 }}>Aperçu — ce que voit votre client :</div>
                    <div style={{ background: '#fff', borderRadius: 12, padding: '10px 14px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', maxWidth: 260 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 4 }}>
                        {smsForm?.sender || 'VotreNom'}
                      </div>
                      <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
                        Bonjour Marie, bonne fête ! 🎂 Profitez de -10% aujourd'hui avec le code ANNIV10.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="settings-form">
                  <div>
                    <Input
                      label="Nom expéditeur (max 11 caractères, sans espaces ni accents)"
                      placeholder="ex: Boulangerie, GarageDup, CabinetDr"
                      value={smsForm?.sender || ''}
                      onChange={e => {
                        // Alphanumeric uniquement, max 11 chars
                        const v = e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 11);
                        setSmsForm(f => ({ ...f, sender: v }));
                      }}
                    />
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                      Lettres et chiffres uniquement · max 11 caractères · pas d'espaces
                      {smsForm?.sender && (
                        <span style={{ marginLeft: 8, fontWeight: 600, color: (smsForm.sender.length <= 11) ? '#10B981' : '#EF4444' }}>
                          {smsForm.sender.length}/11
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#1D4ED8', lineHeight: 1.6 }}>
                    ℹ️ L'envoi SMS est entièrement géré par Appee. Aucune configuration requise — définissez simplement votre nom d'expéditeur.
                  </div>
                </div>
              </Card>

              <Card>
                <CardHeader title="💳 Solde de crédits SMS" />
                <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 36, fontWeight: 800, color: (tenant?.sms_credits || 0) > 10 ? '#10B981' : '#EF4444' }}>
                      {tenant?.sms_credits || 0}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>crédits restants</div>
                  </div>
                  <div style={{ flex: 1, fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
                    Ton plan <strong>{tenant?.plan || 'starter'}</strong> inclut <strong>{tenant?.sms_plan_included || 50} SMS/mois</strong>.
                    Les crédits achetés s'ajoutent à ce solde et ne périment jamais.
                  </div>
                  <button
                    onClick={() => setTab('plan')}
                    style={{ background: '#5A35FF', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                    Voir les packs →
                  </button>
                </div>
              </Card>

              {/* ── Anniversaires SMS ── */}
              <Card>
                <CardHeader
                  title="🎂 SMS d'anniversaire automatique"
                  subtitle="Envoyez un SMS de vœux en plus de l'email le jour de l'anniversaire de vos contacts"
                />
                <div className="settings-form">
                  {/* Toggle */}
                  <div
                    onClick={() => setSmsForm(f => ({ ...f, birthday_sms_enabled: !f.birthday_sms_enabled }))}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer',
                      padding: '14px 16px', borderRadius: 10,
                      background: smsForm.birthday_sms_enabled ? '#F0FDF4' : '#F9FAFB',
                      border: `2px solid ${smsForm.birthday_sms_enabled ? '#86EFAC' : 'var(--line)'}`,
                      transition: 'all 0.15s',
                    }}
                  >
                    {/* Switch pill */}
                    <div style={{
                      width: 44, height: 24, borderRadius: 12, flexShrink: 0,
                      background: smsForm.birthday_sms_enabled ? '#22C55E' : '#D1D5DB',
                      position: 'relative', transition: 'background 0.15s',
                    }}>
                      <div style={{
                        position: 'absolute', top: 3, left: smsForm.birthday_sms_enabled ? 23 : 3,
                        width: 18, height: 18, borderRadius: '50%', background: '#fff',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.15s',
                      }} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14, color: smsForm.birthday_sms_enabled ? '#166534' : '#374151' }}>
                        {smsForm.birthday_sms_enabled ? 'Activé — SMS envoyé le jour J 🎉' : 'Désactivé'}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                        Utilise 1 crédit SMS par contact avec un numéro de téléphone renseigné
                      </div>
                    </div>
                  </div>

                  {/* Aperçu du message SMS */}
                  {smsForm.birthday_sms_enabled && (
                    <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: '14px 18px', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                      <div style={{ fontSize: 24 }}>📱</div>
                      <div>
                        <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 6 }}>Aperçu du SMS envoyé :</div>
                        <div style={{ background: '#fff', borderRadius: 12, padding: '10px 14px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', maxWidth: 280 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 4 }}>
                            {smsForm.sender || 'VotreNom'}
                          </div>
                          <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
                            Joyeux anniversaire Marie ! 🎂 Toute l'équipe {smsForm.sender || 'VotreEntreprise'} vous souhaite une excellente journée et de beaux moments. 🎉
                          </div>
                        </div>
                        <div style={{ fontSize: 11, color: '#6B7280', marginTop: 8 }}>
                          ✦ Le prénom du contact est personnalisé automatiquement
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Conditions */}
                  {smsForm.birthday_sms_enabled && (
                    <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#1D4ED8', lineHeight: 1.6 }}>
                      ℹ️ Le SMS est envoyé chaque matin en même temps que l'email d'anniversaire.
                      Conditions : contact actif · numéro de téléphone renseigné · SMS non désactivé par le contact · crédits disponibles.
                    </div>
                  )}

                  <Button loading={smsSaving} onClick={handleSaveSms}>
                    Enregistrer les options SMS
                  </Button>
                </div>
              </Card>

              <Card>
                <CardHeader title="📋 Historique SMS" />
                <SmsLogsTable />
              </Card>

            </div>
          )}

          {/* ── WHATSAPP BUSINESS ── */}
          {tab === 'whatsapp' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

              {/* Statut de connexion */}
              <Card>
                <CardHeader
                  title="💬 WhatsApp Business"
                  subtitle="Connectez votre compte Meta Business pour envoyer des messages WhatsApp depuis votre numéro d'entreprise"
                  action={
                    waConfig?.connected
                      ? <span style={{ background: '#D1FAE5', color: '#065F46', padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700 }}>● Connecté</span>
                      : <span style={{ background: '#F3F4F6', color: '#6B7280', padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700 }}>○ Non configuré</span>
                  }
                />

                {waConfig?.connected && (
                  <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: '14px 18px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                    <div>
                      <div style={{ fontWeight: 700, color: '#065F46', fontSize: 15 }}>✅ {waConfig.display_name || 'Compte connecté'}</div>
                      {waConfig.phone && <div style={{ fontSize: 13, color: '#059669', marginTop: 3 }}>{waConfig.phone}</div>}
                      {waConfig.connected_at && <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>Connecté le {new Date(waConfig.connected_at).toLocaleDateString('fr-CH')}</div>}
                    </div>
                    <button
                      onClick={handleDeleteWa}
                      disabled={waDeleting}
                      style={{ background: 'none', border: '1px solid #FCA5A5', color: '#DC2626', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      {waDeleting ? '…' : 'Déconnecter'}
                    </button>
                  </div>
                )}

                {/* Formulaire credentials */}
                <div className="settings-form">
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Phone Number ID</label>
                    <input
                      type="text"
                      placeholder="ex : 123456789012345"
                      value={waForm.phone_number_id}
                      onChange={e => setWaForm(f => ({ ...f, phone_number_id: e.target.value.trim() }))}
                      style={{ width: '100%', padding: '9px 12px', border: '1.5px solid var(--line)', borderRadius: 8, fontSize: 14, fontFamily: 'monospace', boxSizing: 'border-box' }}
                    />
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>Trouvez-le dans Meta for Developers → Votre App → WhatsApp → Premiers pas</div>
                  </div>

                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      Access Token permanent
                      {waConfig?.access_token_masked && (
                        <span style={{ fontWeight: 400, color: '#6B7280', marginLeft: 8 }}>(actuel : {waConfig.access_token_masked})</span>
                      )}
                    </label>
                    <input
                      type="password"
                      placeholder={waConfig?.connected ? 'Laisser vide pour conserver le token actuel' : 'EAAxxxxxxxxxxxxxxxx…'}
                      value={waForm.access_token}
                      onChange={e => setWaForm(f => ({ ...f, access_token: e.target.value.trim() }))}
                      style={{ width: '100%', padding: '9px 12px', border: '1.5px solid var(--line)', borderRadius: 8, fontSize: 14, fontFamily: 'monospace', boxSizing: 'border-box' }}
                    />
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>Token système permanent (pas le token temporaire de 24h)</div>
                  </div>

                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button
                      onClick={handleSaveWa}
                      disabled={waSaving || !waForm.phone_number_id || (!waForm.access_token && !waConfig?.connected)}
                      style={{ background: '#25D366', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontWeight: 600, fontSize: 14, cursor: 'pointer', opacity: (waSaving || !waForm.phone_number_id) ? 0.6 : 1 }}>
                      {waSaving ? 'Enregistrement…' : 'Enregistrer'}
                    </button>
                    {waConfig?.connected && (
                      <button
                        onClick={handleTestWa}
                        disabled={waTesting}
                        style={{ background: '#fff', color: '#25D366', border: '1.5px solid #25D366', borderRadius: 8, padding: '9px 20px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                        {waTesting ? 'Test en cours…' : '🧪 Tester la connexion'}
                      </button>
                    )}
                  </div>

                  {waTestResult && (
                    <div style={{ padding: '10px 14px', borderRadius: 8, background: waTestResult.ok ? '#F0FDF4' : '#FEF2F2', border: `1px solid ${waTestResult.ok ? '#BBF7D0' : '#FECACA'}`, fontSize: 13, color: waTestResult.ok ? '#166534' : '#991B1B' }}>
                      {waTestResult.message}
                    </div>
                  )}
                </div>
              </Card>

              {/* Guide de configuration étape par étape */}
              {!waConfig?.connected && (
                <Card>
                  <CardHeader
                    title="📖 Comment configurer WhatsApp Business"
                    subtitle="Suivez ces étapes pour connecter votre compte Meta Business"
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {[
                      {
                        num: '①',
                        title: 'Créer un compte Meta Business Manager',
                        desc: 'Si ce n\'est pas déjà fait, créez votre compte Business sur Meta.',
                        link: 'https://business.facebook.com',
                        linkLabel: 'Ouvrir Meta Business Manager →',
                        note: 'Votre entreprise doit être vérifiée par Meta (formulaire + délai 1-3 jours).',
                      },
                      {
                        num: '②',
                        title: 'Créer une application Meta',
                        desc: 'Dans Meta for Developers, créez une nouvelle app de type "Business".',
                        link: 'https://developers.facebook.com/apps/creation/',
                        linkLabel: 'Créer une app Meta →',
                        note: 'Choisissez le type "Business", pas "Consumer".',
                      },
                      {
                        num: '③',
                        title: 'Ajouter le produit WhatsApp à votre app',
                        desc: 'Dans votre app → Tableau de bord → cliquez "+ Ajouter un produit" → WhatsApp.',
                        note: 'Associez votre compte WhatsApp Business Account (WABA) à l\'app.',
                      },
                      {
                        num: '④',
                        title: 'Récupérer votre Phone Number ID',
                        desc: 'Dans votre app → WhatsApp → Premiers pas. Copiez le "Phone Number ID" (suite de chiffres).',
                        note: 'C\'est l\'identifiant du numéro, pas le numéro lui-même.',
                      },
                      {
                        num: '⑤',
                        title: 'Générer un Access Token permanent',
                        desc: 'Créez un utilisateur système dans Meta Business → Paramètres → Utilisateurs système. Générez un token avec les permissions whatsapp_business_messaging.',
                        link: 'https://business.facebook.com/settings/system-users',
                        linkLabel: 'Gérer les utilisateurs système →',
                        note: 'N\'utilisez pas le token temporaire de 24h affiché dans "Premiers pas" — créez un token permanent via un utilisateur système.',
                      },
                      {
                        num: '⑥',
                        title: 'Configurer le webhook dans Meta',
                        desc: 'Dans votre app → WhatsApp → Configuration → Webhook. Entrez :',
                        webhook: true,
                        note: 'Abonnez-vous aux événements : messages, message_deliveries, message_reads.',
                      },
                    ].map((step, i) => (
                      <div key={i} style={{ display: 'flex', gap: 16, padding: '16px 0', borderBottom: i < 5 ? '1px solid var(--line)' : 'none' }}>
                        <div style={{ fontSize: 22, minWidth: 32, textAlign: 'center', lineHeight: 1.3 }}>{step.num}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{step.title}</div>
                          <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.5 }}>{step.desc}</div>
                          {step.webhook && (
                            <div style={{ margin: '8px 0', background: '#F8FAFC', border: '1px solid var(--line)', borderRadius: 8, padding: '10px 14px', fontFamily: 'monospace', fontSize: 12 }}>
                              <div><span style={{ color: '#6B7280' }}>URL :</span> <strong>https://api.beeappee.com/api/webhooks/meta/whatsapp</strong></div>
                              <div style={{ marginTop: 6 }}><span style={{ color: '#6B7280' }}>Token de vérification :</span> <strong>appee-whatsapp-webhook</strong></div>
                            </div>
                          )}
                          {step.link && (
                            <a href={step.link} target="_blank" rel="noopener noreferrer"
                               style={{ fontSize: 13, color: '#5A35FF', fontWeight: 600, textDecoration: 'none', display: 'inline-block', marginTop: 6 }}>
                              {step.linkLabel}
                            </a>
                          )}
                          {step.note && (
                            <div style={{ fontSize: 12, color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, padding: '6px 10px', marginTop: 8 }}>
                              💡 {step.note}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Historique des envois */}
              <Card>
                <CardHeader
                  title="📋 Historique WhatsApp"
                  subtitle="Statuts mis à jour en temps réel"
                />
                <WhatsAppLogsTable />
              </Card>

            </div>
          )}

          {/* ── SMTP ── */}
          {tab === 'smtp' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* ── Choix explicite du mode d'envoi ── */}
              <div>
                <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4 }}>Comment tes e-mails partent ?</div>
                <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 12 }}>Choisis une fois — tu peux changer quand tu veux.</div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {[
                    { id: 'auto', icon: '⚡', title: 'Envoi automatique', badge: 'Recommandé', desc: "Zéro config. Tout part via Appee, à ton nom. Le plus simple pour démarrer — tu n'as rien à installer." },
                    { id: 'own',  icon: '🏠', title: 'Ma propre adresse', badge: 'Avancé',      desc: "Tes e-mails partent de TON adresse (serveur SMTP). Plus authentique, mais à configurer ci-dessous." },
                  ].map(m => {
                    const active = (adminForm.email_mode || (smtp?.verified ? 'own' : 'auto')) === m.id;
                    return (
                      <button key={m.id} type="button"
                        onClick={() => {
                          setA('email_mode', m.id);
                          const payload = m.id === 'auto'
                            ? { email_mode: 'auto', sender_name: adminForm.sender_name || tenant?.name || '', reply_to: adminForm.reply_to || adminForm.email || '' }
                            : { email_mode: 'own' };
                          api.saveAdmin(payload).then(() => addNotification(m.id === 'auto' ? 'Envoi automatique activé ✅' : 'Mode « ta propre adresse » activé ✅')).catch(() => {});
                        }}
                        style={{ flex: '1 1 260px', textAlign: 'left', cursor: 'pointer', borderRadius: 14, padding: '14px 16px',
                          border: `2px solid ${active ? 'var(--violet)' : 'var(--line)'}`,
                          background: active ? 'var(--violet-soft, #F5F3FF)' : '#fff', fontFamily: 'inherit' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 18 }}>{m.icon}</span>
                          <span style={{ fontWeight: 800, color: 'var(--ink)' }}>{m.title}</span>
                          <span style={{ marginLeft: 'auto', fontSize: 10.5, fontWeight: 800, color: m.id === 'auto' ? '#166534' : 'var(--muted-2)', background: m.id === 'auto' ? '#ECFDF5' : '#F3F4F6', borderRadius: 999, padding: '2px 9px' }}>{m.badge}</span>
                          {active && <span style={{ color: 'var(--violet)', fontWeight: 900 }}>✓</span>}
                        </div>
                        <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 6, lineHeight: 1.5 }}>{m.desc}</div>
                      </button>
                    );
                  })}
                </div>

                {/* Mode auto : 2 infos — repliées une fois enregistrées */}
                {emailMode === 'auto' && (() => {
                  const filled   = !!(adminForm.sender_name && adminForm.reply_to);
                  const showForm = autoEditing || !filled;
                  if (!showForm) {
                    return (
                      <div style={{ marginTop: 12, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 20 }}>✅</span>
                        <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: '#166534', lineHeight: 1.5 }}>
                          Expéditeur affiché : <strong>{adminForm.sender_name}</strong> · Réponses reçues sur <strong>{adminForm.reply_to}</strong>
                        </div>
                        <button type="button" onClick={() => setAutoEditing(true)}
                          style={{ background: '#fff', border: '1px solid var(--line)', borderRadius: 10, padding: '7px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', color: 'var(--ink)' }}>
                          ✏️ Modifier
                        </button>
                      </div>
                    );
                  }
                  return (
                    <div style={{ marginTop: 12, background: '#fff', border: '1px solid var(--line)', borderRadius: 12, padding: 16 }}>
                      <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 12 }}>Deux infos suffisent — Appee gère le reste (adresse technique, délivrabilité, désinscription) :</div>
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <div style={{ flex: '1 1 220px' }}>
                          <label style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--muted)', display: 'block', marginBottom: 5 }}>Nom affiché (ton entreprise)</label>
                          <input value={adminForm.sender_name || ''} onChange={e => setA('sender_name', e.target.value)} placeholder={tenant?.name || 'Mon entreprise'}
                            style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 10, border: '1.5px solid var(--line)', fontSize: 14 }} />
                        </div>
                        <div style={{ flex: '1 1 220px' }}>
                          <label style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--muted)', display: 'block', marginBottom: 5 }}>E-mail où recevoir les réponses</label>
                          <input type="email" value={adminForm.reply_to || ''} onChange={e => setA('reply_to', e.target.value)} placeholder="toi@ton-entreprise.ch"
                            style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 10, border: '1.5px solid var(--line)', fontSize: 14 }} />
                        </div>
                      </div>
                      <button type="button"
                        onClick={() => api.saveAdmin({ email_mode: 'auto', sender_name: adminForm.sender_name, reply_to: adminForm.reply_to }).then(() => { addNotification('Enregistré ✅'); setAutoEditing(false); }).catch(() => {})}
                        disabled={!adminForm.sender_name || !adminForm.reply_to}
                        style={{ marginTop: 12, background: 'var(--violet)', color: '#fff', border: 'none', borderRadius: 10, padding: '9px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', opacity: (!adminForm.sender_name || !adminForm.reply_to) ? 0.5 : 1 }}>
                        Enregistrer
                      </button>
                      <div style={{ fontSize: 11.5, color: 'var(--muted-2)', marginTop: 10, lineHeight: 1.5 }}>
                        💡 Pas besoin de serveur ni de mot de passe. Tes clients voient <strong>{adminForm.sender_name || tenant?.name || 'ton entreprise'}</strong> comme expéditeur, et leurs réponses arrivent sur <strong>{adminForm.reply_to || adminForm.email || 'ton e-mail'}</strong>.
                      </div>
                    </div>
                  );
                })()}

                {/* Accès SMTP même en mode auto (si un SMTP existe) : basculer pour le modifier */}
                {emailMode === 'auto' && smtp?.verified && (
                  <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--muted)' }}>
                    🔧 Un SMTP est configuré ({smtp.host}).{' '}
                    <button type="button" onClick={() => { setA('email_mode', 'own'); api.saveAdmin({ email_mode: 'own' }).catch(() => {}); }}
                      style={{ background: 'none', border: 'none', padding: 0, color: 'var(--violet)', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, textDecoration: 'underline' }}>
                      Passer en « Ma propre adresse » pour le modifier →
                    </button>
                  </div>
                )}
              </div>

              {/* Statut actuel */}
              {smtp?.verified ? (
                <>
                  {emailMode === 'own' && !smtpEditing && (
                  <div style={{ background: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: 14, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 24 }}>✅</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#166534' }}>SMTP opérationnel</div>
                      <div style={{ fontSize: 12, color: '#4B5563', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {smtp.from_name} · {smtp.from_email} · {smtp.host}
                      </div>
                    </div>
                    <SmtpHealthBadge status={localHealth} />
                    <button type="button" onClick={() => setSmtpEditing(true)}
                      style={{ background: '#fff', border: '1px solid #86EFAC', borderRadius: 10, padding: '7px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', color: '#166534' }}>
                      ✏️ Modifier
                    </button>
                  </div>
                  )}
                  {/* Explication mode d'envoi actif — reflète le mode CHOISI */}
                  <div style={{ background: '#EEF2FF', border: '1.5px solid #C7D2FE', borderRadius: 12, padding: '14px 18px', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 22, flexShrink: 0 }}>{(adminForm.email_mode || 'own') === 'auto' ? '⚡' : '📧'}</span>
                    {(adminForm.email_mode || 'own') === 'auto' ? (
                      <div style={{ fontSize: 13, lineHeight: 1.7, color: '#3730A3' }}>
                        <strong>Mode d'envoi actif : automatique</strong><br />
                        <strong>Tous</strong> tes e-mails (anniversaires + campagnes) partent <strong>via Appee</strong>, à ton nom <strong>{adminForm.sender_name || smtp.from_name || tenant?.name}</strong>, réponses sur <strong>{adminForm.reply_to || smtp.from_email}</strong>.<br />
                        <span style={{ color: '#6366F1' }}>Ton SMTP est configuré mais non utilisé — tu as choisi l'envoi automatique.</span>
                      </div>
                    ) : (
                      <div style={{ fontSize: 13, lineHeight: 1.7, color: '#3730A3' }}>
                        <strong>Mode d'envoi actif : ta propre adresse</strong> — chaque type par le meilleur canal :<br />
                        🎂 <strong>Anniversaires &amp; rappels</strong> → depuis <strong>{smtp.from_email}</strong> (ta propre adresse : authentique, faible volume).<br />
                        📣 <strong>Campagnes</strong> → via l'infrastructure Appee (meilleure délivrabilité, protège ton domaine du volume), <strong>à ton nom {smtp.from_name}</strong>, réponses chez toi.<br />
                        <span style={{ color: '#6366F1' }}>Dans tous les cas, c'est ton entreprise qui s'affiche — Appee reste invisible.</span>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div style={{ background: '#FFF7ED', border: '1.5px solid #FED7AA', borderRadius: 14, padding: '14px 20px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <span style={{ fontSize: 24, flexShrink: 0 }}>⚠️</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#92400E' }}>Ton adresse d'envoi n'est pas encore configurée</div>
                    <div style={{ fontSize: 12, color: '#78350F', marginTop: 4, lineHeight: 1.6 }}>
                      ✅ <strong>Tes campagnes partent quand même</strong> — via le serveur Appee (expéditeur <em>noreply@beeappee.com</em>).<br />
                      ⚠️ Les <strong>e-mails automatiques</strong> (anniversaires) attendent, eux, ta propre adresse — sauf si tu actives l'envoi via Appee plus bas.<br />
                      💡 En 2 minutes ci-dessous, tes e-mails partent depuis <strong>ton adresse</strong> : plus pro, plus de réponses.
                    </div>
                  </div>
                </div>
              )}

              {localHealth?.ok === false && (
                <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 14, padding: '14px 20px' }}>
                  <div style={{ fontWeight: 700, color: '#DC2626', marginBottom: 4 }}>🔴 Erreur SMTP détectée</div>
                  <div style={{ fontSize: 13, color: '#7F1D1D' }}>{localHealth.error}</div>
                </div>
              )}

              {/* Aperçu boîte de réception — reflète le mode d'envoi actif */}
              {(() => {
                const brand    = adminForm.sender_name || smtp?.from_name || tenant?.name || 'Ton entreprise';
                const ownAddr  = adminForm.reply_to || smtp?.from_email || detectEmail || 'toi@ton-entreprise.ch';
                const autoAddr = 'envois@mail.beeappee.com';
                const initial  = (brand || 'E').trim().charAt(0).toUpperCase();
                const subject  = 'Joyeux anniversaire, Marie ! 🎂';
                const snippet  = `Toute l'équipe ${brand} vous souhaite une excellente journée…`;
                const PreviewCard = ({ tag, tagBg, tagFg, addr, note, highlight }) => (
                  <div style={{ flex: '1 1 280px', minWidth: 0, background: '#fff', border: `1px solid ${highlight ? '#86EFAC' : 'var(--line)'}`, borderRadius: 14, overflow: 'hidden' }}>
                    <div style={{ padding: '7px 14px', fontSize: 11, fontWeight: 800, background: tagBg, color: tagFg }}>{tag}</div>
                    <div style={{ display: 'flex', gap: 12, padding: '14px 16px', alignItems: 'flex-start' }}>
                      <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--violet)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, flexShrink: 0 }}>{initial}</div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                          <span style={{ fontWeight: 800, color: 'var(--ink)', fontSize: 14 }}>{brand}</span>
                          <span style={{ fontSize: 11, color: 'var(--muted-2)' }}>07:00</span>
                        </div>
                        <div style={{ marginBottom: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <span style={{ background: highlight ? '#DCFCE7' : '#E0E7FF', color: highlight ? '#166534' : '#3730A3', fontWeight: 700, borderRadius: 6, padding: '2px 7px', fontSize: 11 }}>{addr}</span>
                        </div>
                        <div style={{ fontWeight: 700, color: 'var(--ink)', fontSize: 13 }}>{subject}</div>
                        <div style={{ fontSize: 12, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{snippet}</div>
                      </div>
                    </div>
                    <div style={{ padding: '9px 16px 13px', fontSize: 11.5, color: 'var(--muted)', borderTop: '1px dashed var(--line)', lineHeight: 1.5 }}>{note}</div>
                  </div>
                );
                return (
                  <div style={{ background: 'var(--violet-soft, #F5F3FF)', border: '1px solid var(--line)', borderRadius: 16, padding: '16px 18px' }}>
                    <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 4 }}>📬 À quoi ressemble l'e-mail reçu par ton client</div>
                    <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 14 }}>Aperçu réel du mode que tu as choisi — c'est <strong>ton entreprise</strong> qui s'affiche, Appee reste invisible.</div>
                    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                      {emailMode === 'own' ? (
                        <PreviewCard highlight tag="🏠 TA PROPRE ADRESSE" tagBg="#ECFDF5" tagFg="#166534" addr={ownAddr}
                          note="✅ 100 % ton adresse. Meilleure délivrabilité (n'atterrit pas dans « Promotions »). Les réponses arrivent directement chez toi." />
                      ) : (
                        <PreviewCard tag="⚡ ENVOI AUTOMATIQUE" tagBg="#EEF2FF" tagFg="#3730A3" addr={autoAddr}
                          note={<>Ton nom <strong>{brand}</strong> est affiché, l'adresse technique est Appee. Les réponses arrivent sur <strong>{ownAddr}</strong>. Zéro config.</>} />
                      )}
                    </div>

                    {/* Ce qui change / ce qui reste — la clé de la compréhension */}
                    <div style={{ marginTop: 14, background: '#fff', border: '1px solid var(--line)', borderRadius: 12, padding: '12px 14px' }}>
                      <div style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--ink)', marginBottom: 10 }}>🔑 Ce qui change (et ce qui ne change pas)</div>
                      {[
                        { k: 'Nom affiché', v: brand, tag: 'identique', tagBg: '#ECFDF5', tagFg: '#166534', change: false },
                        { k: 'Réponses reçues sur', v: ownAddr, tag: 'identique', tagBg: '#ECFDF5', tagFg: '#166534', change: false },
                        { k: "Adresse d'envoi", v: emailMode === 'own' ? ownAddr : autoAddr, tag: emailMode === 'own' ? 'ta vraie adresse' : 'technique Appee', tagBg: '#EEF2FF', tagFg: '#3730A3', change: true },
                      ].map(row => (
                        <div key={row.k} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderTop: '1px solid #F3F4F6' }}>
                          <span style={{ fontSize: 12, color: 'var(--muted)', width: 130, flexShrink: 0 }}>{row.k}</span>
                          <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: row.change ? 'var(--violet)' : 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.v}</span>
                          <span style={{ flexShrink: 0, fontSize: 10.5, fontWeight: 800, background: row.tagBg, color: row.tagFg, borderRadius: 999, padding: '3px 9px' }}>{row.change ? '⚡ ' : '✓ '}{row.tag}</span>
                        </div>
                      ))}
                      <div style={{ fontSize: 11.5, color: 'var(--muted-2)', marginTop: 10, lineHeight: 1.5 }}>
                        👉 Seule l'<strong>adresse d'envoi</strong> diffère selon le mode. Ton <strong>nom</strong> et l'<strong>e-mail de réponse</strong> restent toujours les tiens.
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* ── Card config SMTP — mode « ta propre adresse », déroulée à la demande ── */}
              {emailMode === 'own' && (smtpEditing || !smtp?.verified) && (
              <Card>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                  <CardHeader
                    title="📧 Configuration e-mail"
                    subtitle="Entrez votre adresse e-mail — Appee détecte automatiquement votre fournisseur"
                  />
                  {smtp?.verified && (
                    <button type="button" onClick={() => setSmtpEditing(false)}
                      style={{ flexShrink: 0, marginTop: 4, background: 'none', border: '1px solid var(--line)', borderRadius: 10, padding: '6px 14px', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit', color: 'var(--muted)' }}>
                      ✕ Replier
                    </button>
                  )}
                </div>

                <div className="settings-form">

                  {/* Étape 1 — Email */}
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 6 }}>
                      Étape 1 — Votre adresse e-mail professionnelle
                    </label>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <input
                        type="email"
                        placeholder="info@votreentreprise.ch"
                        value={detectEmail}
                        onChange={e => { setDetectEmail(e.target.value); setDetected(null); setDetectErr(''); }}
                        onKeyDown={e => e.key === 'Enter' && handleDetect()}
                        style={{ flex: 1, padding: '10px 14px', borderRadius: 10, border: '1.5px solid var(--line)', fontSize: 14, outline: 'none' }}
                      />
                      <Button variant="secondary" loading={detecting} onClick={handleDetect} disabled={!detectEmail}>
                        🔍 Détecter
                      </Button>
                    </div>

                    {/* Résultat détection */}
                    {detected && (
                      <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, background: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: 10, padding: '10px 14px' }}>
                        <span style={{ fontSize: 20 }}>✅</span>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 13, color: '#166534' }}>{detected.label} détecté</div>
                          <div style={{ fontSize: 12, color: '#4B5563' }}>
                            {smtpForm.host} · Port {smtpForm.port} · {smtpForm.encryption.toUpperCase()}
                          </div>
                        </div>
                        <button onClick={() => setShowAdvanced(v => !v)} style={{ marginLeft: 'auto', fontSize: 11, color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                          {showAdvanced ? 'Masquer détails' : 'Voir détails'}
                        </button>
                      </div>
                    )}
                    {detectErr && (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#DC2626', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '8px 12px' }}>
                        ⚠️ {detectErr}
                      </div>
                    )}
                  </div>

                  {/* Étape 2 — Mot de passe (visible seulement si détecté ou avancé) */}
                  {(detected || showAdvanced) && (
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 6 }}>
                        Étape 2 — Mot de passe
                      </label>
                      <input
                        type="password"
                        placeholder="••••••••"
                        value={detectPassword}
                        onChange={e => setDetectPassword(e.target.value)}
                        style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid var(--line)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                      />
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                        {detected?.provider === 'google'    && '💡 Google Workspace : utilisez un "Mot de passe d\'application" dans votre compte Google si la double authentification est activée.'}
                        {detected?.provider === 'microsoft' && '💡 Microsoft 365 : activez "SMTP Auth" dans le centre d\'administration Microsoft 365 si l\'envoi échoue.'}
                        {detected?.provider === 'infomaniak'&& '💡 Infomaniak : votre mot de passe de messagerie Infomaniak.'}
                        {!detected?.provider               && 'Mot de passe de votre compte e-mail.'}
                      </div>
                    </div>
                  )}

                  {/* Nom expéditeur */}
                  {(detected || showAdvanced) && (
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 6 }}>
                        Étape 3 — Nom affiché chez vos clients
                      </label>
                      <input
                        type="text"
                        placeholder={tenant?.name || 'Mon Entreprise'}
                        value={smtpForm.from_name}
                        onChange={e => setSmtpForm(f => ({ ...f, from_name: e.target.value }))}
                        style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid var(--line)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                      />
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                        Vos clients verront : <strong>{smtpForm.from_name || tenant?.name || 'Mon Entreprise'}</strong> &lt;{detectEmail || smtp?.from_email || 'vous@votreentreprise.ch'}&gt;
                      </div>
                    </div>
                  )}

                  {/* Paramètres avancés */}
                  {(detected || showAdvanced) && (
                    <div>
                      <button onClick={() => setShowAdvanced(v => !v)} style={{ fontSize: 12, color: 'var(--violet)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0 }}>
                        {showAdvanced ? '▲ Masquer les paramètres avancés' : '▼ Paramètres avancés'}
                      </button>
                      {showAdvanced && (
                        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10, padding: '14px 16px', background: '#F9FAFB', borderRadius: 10, border: '1px solid var(--line)' }}>
                          <Input label="Serveur SMTP" value={smtpForm.host} onChange={e => setSmtpForm(f => ({ ...f, host: e.target.value }))} />
                          <div style={{ display: 'flex', gap: 10 }}>
                            <Input label="Port" type="number" value={smtpForm.port} onChange={e => setSmtpForm(f => ({ ...f, port: parseInt(e.target.value) }))} />
                            <Select label="Chiffrement" value={smtpForm.encryption} onChange={e => setSmtpForm(f => ({ ...f, encryption: e.target.value }))}>
                              <option value="tls">TLS</option>
                              <option value="ssl">SSL</option>
                              <option value="none">Aucun</option>
                            </Select>
                          </div>
                          <Input label="Nom d'utilisateur" value={smtpForm.username} onChange={e => setSmtpForm(f => ({ ...f, username: e.target.value }))} />
                          <Input label="E-mail expéditeur" type="email" value={smtpForm.from_email} onChange={e => setSmtpForm(f => ({ ...f, from_email: e.target.value }))} />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Bouton final + test */}
                  {(detected || showAdvanced) && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>E-mail de vérification (un mail test sera envoyé)</label>
                          <input
                            type="email"
                            value={testEmail}
                            onChange={e => setTestEmail(e.target.value)}
                            style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid var(--line)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                          />
                        </div>
                        <Button loading={smartTesting} onClick={handleSmartSave} disabled={!detectPassword || !testEmail}>
                          ⚡ Activer et tester
                        </Button>
                      </div>

                      {smartTestResult && (
                        <div style={{ padding: '12px 16px', borderRadius: 10, background: smartTestResult.ok ? '#F0FDF4' : '#FEF2F2', border: `1px solid ${smartTestResult.ok ? '#86EFAC' : '#FECACA'}`, fontSize: 13, fontWeight: 600, color: smartTestResult.ok ? '#166534' : '#DC2626' }}>
                          {smartTestResult.ok
                            ? '✅ Parfait ! Votre SMTP est configuré et vérifié. Les e-mails partent depuis votre propre adresse.'
                            : `❌ Échec : ${smartTestResult.message}`}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Si pas encore détecté — lien configuration manuelle */}
                  {!detected && !showAdvanced && (
                    <button onClick={() => setShowAdvanced(true)} style={{ fontSize: 12, color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', textAlign: 'left' }}>
                      Configurer manuellement
                    </button>
                  )}

                </div>
              </Card>
              )}

              {/* ── Option serveur générique Appee (si pas de SMTP propre) ── */}
              {!smtp?.verified && (
                <Card>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4 }}>
                        📬 Envoyer via le serveur générique Appee
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
                        Si vous n'avez pas encore de SMTP, vous pouvez autoriser Appee à envoyer les e-mails à votre place.
                        <strong style={{ color: '#DC2626' }}> Attention : vos clients verront l'adresse <em>noreply@beeappee.com</em> comme expéditeur</strong>, pas votre adresse personnelle.
                      </div>
                    </div>

                    {/* Encart d'avertissement toujours visible */}
                    <div style={{ background: '#FEF2F2', border: '1.5px solid #FECACA', borderRadius: 10, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#991B1B', marginBottom: 2 }}>⚠️ Ce que verra votre client :</div>
                      <div style={{ fontSize: 13, color: '#7F1D1D', fontFamily: 'monospace', background: '#fff', border: '1px solid #FECACA', borderRadius: 6, padding: '6px 10px' }}>
                        De : <strong>Votre Entreprise</strong> &lt;noreply@beeappee.com&gt;
                      </div>
                      <div style={{ fontSize: 12, color: '#991B1B', marginTop: 4 }}>
                        💬 Les réponses seront redirigées vers votre adresse si elle est renseignée dans le SMTP.<br />
                        ✅ Pour envoyer depuis <strong>votre propre adresse</strong>, configurez votre SMTP ci-dessus.
                      </div>
                    </div>

                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '14px 16px', borderRadius: 12,
                      background: adminForm.birthday_fallback_mailjet ? '#FFF7ED' : '#F9FAFB',
                      border: `1.5px solid ${adminForm.birthday_fallback_mailjet ? '#FED7AA' : '#E5E7EB'}`,
                    }}>
                      <input
                        type="checkbox"
                        id="birthday_fallback"
                        checked={!!adminForm.birthday_fallback_mailjet}
                        onChange={e => {
                          if (e.target.checked) setShowFallbackModal(true);
                          else {
                            setA('birthday_fallback_mailjet', false);
                            api.saveAdmin({ birthday_fallback_mailjet: false })
                              .then(() => addNotification('Serveur générique désactivé.'));
                          }
                        }}
                        style={{ width: 18, height: 18, cursor: 'pointer', accentColor: '#F59E0B' }}
                      />
                      <div>
                        <label htmlFor="birthday_fallback" style={{ cursor: 'pointer', fontWeight: 700, fontSize: 14, display: 'block' }}>
                          {adminForm.birthday_fallback_mailjet
                            ? '⚠️ Serveur générique Appee activé'
                            : 'J\'accepte d\'utiliser le serveur générique Appee'}
                        </label>
                        <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                          {adminForm.birthday_fallback_mailjet
                            ? 'Vos e-mails partent depuis noreply@beeappee.com — pas depuis votre adresse.'
                            : 'Par défaut désactivé — aucun e-mail ne part sans SMTP ni accord explicite.'}
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* ── PAUSE GLOBALE DES ENVOIS ── */}
          {tab === 'envois' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{
                background: sendingPaused ? 'var(--fn-danger-pale)' : '#fff',
                border: `1px solid ${sendingPaused ? 'var(--fn-danger-line)' : 'var(--line)'}`,
                borderRadius: 16, padding: '22px 24px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
                    background: sendingPaused ? 'var(--fn-danger-pale)' : 'var(--violet-pale)',
                  }}>{sendingPaused ? '⏸️' : '📮'}</div>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--ink)' }}>
                      {sendingPaused ? 'Tous les envois sont en pause' : 'Envois actifs'}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                      {sendingPaused
                        ? "Aucun e-mail ni SMS ne part vers tes contacts — ni manuel, ni automatique (anniversaires, campagnes programmées)."
                        : 'Tes campagnes, SMS et automatismes partent normalement vers tes contacts.'}
                    </div>
                  </div>
                </div>

                <p style={{ fontSize: 13.5, color: 'var(--muted)', lineHeight: 1.6, margin: '4px 0 18px' }}>
                  Besoin de tout arrêter un moment (congés, correction en cours, doute) ? Ce bouton
                  <strong> suspend d'un coup tous les envois vers tes clients</strong>. Tes contacts, campagnes
                  et réglages restent intacts — tu réactives quand tu veux, rien n'est perdu.
                  <br />
                  <span style={{ color: 'var(--muted-2)', fontSize: 12.5 }}>
                    (La réinitialisation de ton mot de passe et tes rapports internes ne sont pas concernés.)
                  </span>
                </p>

                {!sendingPaused ? (
                  <button
                    onClick={() => { if (window.confirm('Suspendre TOUS les envois (e-mails + SMS) vers tes contacts ?\n\nRien ne partira tant que tu ne réactives pas.')) togglePause(true); }}
                    disabled={savingPause}
                    style={{
                      background: 'var(--fn-danger)', color: '#fff', border: 'none', borderRadius: 12,
                      padding: '13px 22px', fontFamily: 'inherit', fontWeight: 800, fontSize: 14.5,
                      cursor: savingPause ? 'wait' : 'pointer', opacity: savingPause ? 0.6 : 1,
                    }}
                  >⏸️ Suspendre tous les envois</button>
                ) : (
                  <button
                    onClick={() => togglePause(false)}
                    disabled={savingPause}
                    style={{
                      background: 'var(--success)', color: '#fff', border: 'none', borderRadius: 12,
                      padding: '13px 22px', fontFamily: 'inherit', fontWeight: 800, fontSize: 14.5,
                      cursor: savingPause ? 'wait' : 'pointer', opacity: savingPause ? 0.6 : 1,
                    }}
                  >▶️ Reprendre les envois</button>
                )}
              </div>
            </div>
          )}

          {/* ── RAPPORTS AUTO ── */}
          {tab === 'reports' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Card>
                <CardHeader
                  title="📊 Rapports automatiques"
                  subtitle="Recevez chaque matin un rapport des anniversaires du jour et un suivi des e-mails envoyés"
                />
                <div className="settings-form">
                  <Input
                    label="E-mail admin (destinataire des rapports)"
                    type="email"
                    placeholder="vous@votreentreprise.ch"
                    value={adminForm.email}
                    onChange={e => setA('email', e.target.value)}
                    hint="Adresse qui reçoit le rapport quotidien anniversaires + rapport de suivi"
                  />
                  <Input
                    label="Nom de votre entreprise"
                    placeholder="Garage Dupont SA"
                    value={adminForm.company_name}
                    onChange={e => setA('company_name', e.target.value)}
                    hint="Utilisé dans tous les e-mails envoyés à vos clients"
                  />
                  {/* Checkbox activation envoi automatique */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '14px 16px',
                    background: adminForm.auto_send_enabled ? 'var(--violet-pale)' : '#FFF7ED',
                    border: `1px solid ${adminForm.auto_send_enabled ? 'var(--violet-light, #E5DEFF)' : '#FED7AA'}`,
                    borderRadius: 12,
                  }}>
                    <input
                      type="checkbox"
                      id="auto_send"
                      checked={adminForm.auto_send_enabled}
                      onChange={e => setA('auto_send_enabled', e.target.checked)}
                      style={{ width: 18, height: 18, cursor: 'pointer', accentColor: 'var(--violet)' }}
                    />
                    <div>
                      <label htmlFor="auto_send" style={{ cursor: 'pointer', fontWeight: 700, fontSize: 14, display: 'block' }}>
                        {adminForm.auto_send_enabled ? '✅ Envoi automatique activé' : '⏸️ Envoi automatique désactivé'}
                      </label>
                      <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                        {adminForm.auto_send_enabled
                          ? 'Les e-mails d\'anniversaire sont envoyés automatiquement chaque matin.'
                          : 'Aucun e-mail n\'est envoyé automatiquement. Activez pour relancer les envois.'}
                      </span>
                    </div>
                  </div>

                  {/* Planning fixe — lecture seule */}
                  <div style={{ background: '#F8F7FF', border: '1px solid #E5DEFF', borderRadius: 12, padding: '14px 18px' }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)', marginBottom: 10 }}>
                      🕐 Planning d'envoi automatique
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                      {[
                        { time: '07:00', label: '1ère vague — e-mails d\'anniversaire (jusqu\'à 10 contacts)', color: '#5A35FF' },
                        { time: '07:10', label: '2ème vague si plus de 10 anniversaires (anti-blacklist)', color: '#5A35FF' },
                        { time: '07:22', label: '3ème vague si plus de 20 anniversaires', color: '#5A35FF' },
                        { time: '07:55', label: 'Rapport anniversaires → votre adresse admin', color: '#059669' },
                        { time: '08:00', label: 'Rapport de suivi (envoyés, ouvertures, feedbacks)', color: '#059669' },
                      ].map(({ time, label, color }) => (
                        <div key={time} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                          <span style={{
                            fontWeight: 800, fontSize: 12, color: '#fff',
                            background: color, borderRadius: 6, padding: '2px 8px',
                            minWidth: 44, textAlign: 'center', flexShrink: 0,
                          }}>{time}</span>
                          <span style={{ color: 'var(--muted)' }}>{label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Button onClick={handleSaveAdmin} loading={adminSaving}>Sauvegarder la configuration</Button>
                </div>
              </Card>

              <Card>
                <CardHeader title="🧪 Tester les rapports" subtitle="Envoyez un rapport de test maintenant pour vérifier le rendu" />
                <div style={{ display: 'flex', gap: 10, padding: '0 0 4px', flexWrap: 'wrap' }}>
                  <Button onClick={() => handleSendTestReport('daily')}>
                    🎂 Tester rapport quotidien
                  </Button>
                  <Button variant="secondary" onClick={() => handleSendTestReport('tracking')}>
                    📊 Tester rapport tracking
                  </Button>
                  <Button variant="secondary" onClick={() => handleSendTestReport('weekly')}>
                    📆 Tester bilan hebdomadaire
                  </Button>
                </div>
                {!adminForm.email && (
                  <p style={{ fontSize: 13, color: '#EF4444', marginTop: 8 }}>
                    ⚠️ Renseignez d'abord l'e-mail admin ci-dessus.
                  </p>
                )}
              </Card>
            </div>
          )}

          {/* ── RAPPELS ── */}
          {tab === 'reminders' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Email de destination par défaut */}
              <Card>
                <CardHeader title="📬 Adresse de réception" subtitle="Tous vos rappels seront envoyés à cette adresse e-mail" />
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1 }}>
                    <Input
                      label="E-mail destinataire"
                      type="email"
                      value={reminderEmail}
                      onChange={e => setReminderEmail(e.target.value)}
                      placeholder="vous@votreentreprise.ch"
                    />
                  </div>
                  <Button onClick={async () => {
                    try {
                      await api.saveAdmin({ ...adminForm, reminder_email: reminderEmail });
                      addNotification('Adresse de rappel sauvegardée ✅');
                    } catch (err) {
                      addNotification(`Erreur : ${err.message}`, 'error');
                    }
                  }}>
                    Sauvegarder
                  </Button>
                </div>
                {/* Info mobile depuis le profil */}
                <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10, background: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: 10, padding: '10px 14px' }}>
                  <span style={{ fontSize: 16 }}>📱</span>
                  <div style={{ fontSize: 13, color: '#5A35FF' }}>
                    <span style={{ fontWeight: 700 }}>SMS envoyés au : </span>
                    {profileForm.mobile
                      ? <span style={{ fontWeight: 600 }}>{profileForm.mobile}</span>
                      : <span style={{ color: '#9CA3AF' }}>Aucun numéro — </span>}
                    {!profileForm.mobile && (
                      <button
                        onClick={() => setTab('profile')}
                        style={{ background: 'none', border: 'none', color: '#5A35FF', fontWeight: 700, fontSize: 13, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                      >
                        ajoute ton mobile dans ton profil →
                      </button>
                    )}
                  </div>
                </div>
              </Card>

              <Card>
                <CardHeader
                  title="🔔 Mes rappels"
                  subtitle="Jusqu'à 10 rappels automatiques par e-mail. Ne manquez plus aucun événement."
                  action={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 12, color: 'var(--muted)' }}>{reminders.length} / {REMINDER_MAX}</span>
                      <Button
                        size="sm"
                        onClick={startAddReminder}
                        disabled={reminders.length >= REMINDER_MAX || editingReminder !== null}
                      >
                        + Ajouter
                      </Button>
                    </div>
                  }
                />

                {reminders.length === 0 && editingReminder === null && (
                  <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--muted)', fontSize: 14 }}>
                    <div style={{ fontSize: 36, marginBottom: 10 }}>🔔</div>
                    Aucun rappel configuré.<br />
                    <span style={{ color: 'var(--violet)', cursor: 'pointer', fontWeight: 600 }} onClick={startAddReminder}>
                      Créer votre premier rappel →
                    </span>
                  </div>
                )}

                {/* List of existing reminders */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {reminders.map(r => {
                    const todayStr = localDateStr();
                    const sentToday = r.last_sent_date && r.last_sent_date.slice(0, 10) === todayStr;
                    const isPast = r.type !== 'birthday' && r.fixed_date && r.fixed_date.slice(0, 10) < todayStr;
                    const isDone = sentToday || isPast;
                    return (
                    <div key={r.id}>
                      {editingReminder === r.id ? (
                        <ReminderForm form={reminderForm} setRF={setRF} onSave={saveReminder} onCancel={cancelReminder} saving={reminderSaving} />
                      ) : (
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 12,
                          padding: '14px 16px',
                          background: isDone ? '#F0FDF4' : r.enabled ? 'var(--violet-pale)' : '#F9FAFB',
                          border: `1.5px solid ${isDone ? '#BBF7D0' : r.enabled ? 'var(--violet-light)' : 'var(--line)'}`,
                          borderRadius: 12,
                          opacity: isDone || !r.enabled ? 0.65 : 1,
                        }}>
                          <div style={{ fontSize: 22 }}>
                            {{ birthday: '🎂', prive: '🏠', professionnel: '💼', apero: '🥂', autre: '📌' }[r.type] ?? '🔔'}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                              {r.label}
                              {sentToday && <span style={{ fontSize: 11, background: '#DCFCE7', color: '#16A34A', fontWeight: 700, padding: '2px 8px', borderRadius: 999 }}>✉️ Envoyé aujourd'hui</span>}
                              {isPast && !sentToday && <span style={{ fontSize: 11, background: '#F3F4F6', color: '#6B7280', fontWeight: 600, padding: '2px 8px', borderRadius: 999 }}>Date passée</span>}
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                              {r.type === 'birthday'
                                ? `${DAYS_BEFORE_OPTIONS.find(o => o.value === r.days_before)?.label ?? `${r.days_before}j avant`} · ${r.time}`
                                : `${r.fixed_date ? new Date(r.fixed_date).toLocaleDateString('fr-CH') : 'Date non définie'} · ${r.time}`
                              }
                              {r.note && <span style={{ marginLeft: 8, fontStyle: 'italic' }}>— {r.note}</span>}
                            </div>
                            <div style={{ display: 'flex', gap: 5, marginTop: 5, flexWrap: 'wrap' }}>
                              {(r.channels || ['email']).map(ch => (
                                <span key={ch} style={{
                                  fontSize: 11, fontWeight: 700, borderRadius: 20, padding: '2px 9px',
                                  background: ch === 'sms' ? '#F0FDF4' : '#EDE9FF',
                                  color: ch === 'sms' ? '#065F46' : '#5A35FF',
                                  border: `1px solid ${ch === 'sms' ? '#86EFAC' : '#C4B5FD'}`,
                                }}>
                                  {ch === 'sms' ? '📱 SMS' : '✉ Email'}
                                </span>
                              ))}
                              {(r.extra_recipients || []).length > 0 && (
                                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', background: '#F3F4F6', borderRadius: 20, padding: '2px 9px' }}>
                                  +{r.extra_recipients.length} destinataire{r.extra_recipients.length > 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                            {/* Toggle */}
                            <button
                              onClick={() => toggleReminder(r.id)}
                              style={{
                                width: 40, height: 22, borderRadius: 11,
                                background: r.enabled ? 'var(--violet)' : '#D1D5DB',
                                border: 'none', cursor: 'pointer', position: 'relative', transition: 'background .2s',
                              }}
                            >
                              <div style={{
                                position: 'absolute', top: 3,
                                left: r.enabled ? 20 : 3,
                                width: 16, height: 16, borderRadius: '50%',
                                background: 'white', transition: 'left .2s',
                              }} />
                            </button>
                            <button onClick={() => startEditReminder(r)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, padding: '4px 6px' }}>✏️</button>
                            <button onClick={() => deleteReminder(r.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, padding: '4px 6px' }}>🗑</button>
                          </div>
                        </div>
                      )}
                    </div>
                    );
                  })}

                  {/* New reminder form (when no existing id matches) */}
                  {editingReminder !== null && !reminders.find(r => r.id === editingReminder) && (
                    <ReminderForm form={reminderForm} setRF={setRF} onSave={saveReminder} onCancel={cancelReminder} isNew saving={reminderSaving} />
                  )}
                </div>
              </Card>

              <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 12, padding: '14px 18px', fontSize: 13, color: '#15803D' }}>
                <strong>💡 Comment ça fonctionne :</strong><br />
                Les rappels sont envoyés à <strong>{reminderEmail || 'votre adresse e-mail'}</strong> à l'heure choisie.
                Pour les anniversaires, un rappel est déclenché pour chaque client dont la date approche.
                Configurez votre SMTP pour activer les envois.
              </div>
            </div>
          )}

          {/* ── PROFIL ── */}
          {tab === 'profile' && (
            <Card>
              <CardHeader title="👤 Profil utilisateur" />
              <div className="settings-form">
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                  <div className="profile-avatar" style={{
                    width: 52, height: 52, borderRadius: 14,
                    background: 'linear-gradient(135deg,#5A35FF,#7C5CFF)',
                    color: '#fff', fontSize: 22, fontWeight: 800,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {(profileForm.name || user.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700 }}>{profileForm.name || user.name}</div>
                    <div style={{ fontSize: 13, color: 'var(--muted)' }}>{profileForm.email || user.email}</div>
                  </div>
                </div>
                <Input
                  label="Nom complet"
                  value={profileForm.name}
                  onChange={e => setP('name', e.target.value)}
                />
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ flex: 2 }}>
                    <Input
                      label="E-mail"
                      type="email"
                      value={profileForm.email}
                      onChange={e => setP('email', e.target.value)}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Input
                      label="Mobile (SMS)"
                      type="tel"
                      value={profileForm.mobile}
                      onChange={e => setP('mobile', e.target.value)}
                      placeholder="+41 79 000 00 00"
                    />
                  </div>
                </div>
                <Input
                  label="Entreprise"
                  value={profileForm.company}
                  onChange={e => setP('company', e.target.value)}
                />
                <Button loading={profileSaving} onClick={handleSaveProfile}>Sauvegarder</Button>
              </div>
            </Card>
          )}

          {/* ── ENTREPRISE ── */}
          {tab === 'entreprise' && (() => {
            const aiScore = calcAiScore(companyForm);
            const scoreColor = aiScore >= 80 ? '#10B981' : aiScore >= 50 ? '#F59E0B' : '#EF4444';

            const socialNetworks = [
              { key: 'facebook',          label: 'Facebook',          icon: '📘', urlKey: 'facebook_url' },
              { key: 'instagram',         label: 'Instagram',         icon: '📸', urlKey: 'instagram_url' },
              { key: 'linkedin',          label: 'LinkedIn',          icon: '💼', urlKey: 'linkedin_url' },
              { key: 'google_mybusiness', label: 'Google My Business', icon: '📍', urlKey: 'google_mybusiness_url' },
            ];

            // Checklist des catégories IA
            const aiChecklist = [
              { cat: 'Identité',         icon: '🏢', pts: 20, done: [companyForm.name, companyForm.sector, companyForm.logo_url].filter(Boolean).length, total: 3,   fields: ['Nom', 'Secteur', 'Logo'] },
              { cat: 'Positionnement',   icon: '✍️', pts: 34, done: [companyForm.description, companyForm.target_audience, companyForm.services, companyForm.unique_selling_point, companyForm.values].filter(Boolean).length, total: 5, fields: ['Description', 'Clientèle cible', 'Services', 'USP', 'Valeurs'] },
              { cat: 'Contact & lieu',   icon: '📍', pts: 14, done: [companyForm.website, companyForm.address || companyForm.city, companyForm.business_phone || companyForm.business_email, companyForm.geographic_zone].filter(Boolean).length, total: 4, fields: ['Site web', 'Adresse', 'Téléphone/Email', 'Zone geo'] },
              { cat: 'Réseaux sociaux',  icon: '🌐', pts: 20, done: [companyForm.facebook_url, companyForm.instagram_url, companyForm.linkedin_url, companyForm.google_mybusiness_url].filter(Boolean).length, total: 4, fields: ['Facebook', 'Instagram', 'LinkedIn', 'Google My Business'] },
              { cat: 'Contexte marché',  icon: '💡', pts: 12, done: [companyForm.avg_client_value, companyForm.keywords, companyForm.competitors].filter(Boolean).length, total: 3, fields: ['Ticket moyen', 'Mots-clés', 'Concurrents'] },
            ];

            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

                {/* ── A. AI Readiness — checklist par catégorie ── */}
                <div style={{ background: '#fff', border: '1.5px solid var(--line)', borderRadius: 18, overflow: 'hidden' }}>
                  {/* Header */}
                  <div style={{ background: `linear-gradient(135deg, ${scoreColor}18, ${scoreColor}06)`, borderBottom: `1px solid ${scoreColor}30`, padding: '18px 22px', display: 'flex', alignItems: 'center', gap: 18 }}>
                    <div style={{ position: 'relative', width: 64, height: 64, flexShrink: 0 }}>
                      <svg width="64" height="64" viewBox="0 0 64 64">
                        <circle cx="32" cy="32" r="28" fill="none" stroke="#E5E7EB" strokeWidth="5" />
                        <circle cx="32" cy="32" r="28" fill="none" stroke={scoreColor} strokeWidth="5"
                          strokeDasharray={`${2 * Math.PI * 28}`}
                          strokeDashoffset={`${2 * Math.PI * 28 * (1 - aiScore / 100)}`}
                          strokeLinecap="round" transform="rotate(-90 32 32)" />
                      </svg>
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 14, color: scoreColor }}>{aiScore}%</div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 800, fontSize: 16, color: '#1F2937' }}>
                        🤖 Profil IA — {aiScore < 50 ? 'incomplet' : aiScore < 80 ? 'partiel' : 'prêt'}
                      </div>
                      <div style={{ fontSize: 12, color: '#6B7280', marginTop: 3 }}>
                        {aiScore < 50
                          ? 'Remplis les sections manquantes pour débloquer les conseils IA personnalisés'
                          : aiScore < 80
                          ? 'Bonne base ! Les sections en orange améliorent la qualité des stratégies IA'
                          : '✅ L\'IA dispose de tous les éléments pour te proposer des stratégies ultra-ciblées'}
                      </div>
                    </div>
                  </div>
                  {/* Checklist */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 0 }}>
                    {aiChecklist.map((c, i) => {
                      const pct = Math.round(c.done / c.total * 100);
                      const col = pct === 100 ? '#10B981' : pct >= 50 ? '#F59E0B' : '#EF4444';
                      return (
                        <div key={i} style={{ padding: '14px 18px', borderRight: '1px solid var(--line)', borderBottom: i < aiChecklist.length - 1 ? '1px solid var(--line)' : 'none' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>{c.icon} {c.cat}</span>
                            <span style={{ fontSize: 11, fontWeight: 800, color: col }}>{c.done}/{c.total}</span>
                          </div>
                          <div style={{ height: 4, background: '#E5E7EB', borderRadius: 3, overflow: 'hidden', marginBottom: 6 }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: col, borderRadius: 3, transition: 'width .4s' }} />
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {c.fields.map((f, j) => {
                              const filled = j === 0 ? !!companyForm.name && c.cat === 'Identité' ? !!companyForm.name :
                                c.cat === 'Identité' ? [!!companyForm.name, !!companyForm.sector, !!companyForm.logo_url][j] :
                                c.cat === 'Positionnement' ? [!!companyForm.description, !!companyForm.target_audience, !!companyForm.services, !!companyForm.unique_selling_point, !!companyForm.values][j] :
                                c.cat === 'Contact & lieu' ? [!!companyForm.website, !!(companyForm.address || companyForm.city), !!(companyForm.business_phone || companyForm.business_email), !!companyForm.geographic_zone][j] :
                                c.cat === 'Réseaux sociaux' ? [!!companyForm.facebook_url, !!companyForm.instagram_url, !!companyForm.linkedin_url, !!companyForm.google_mybusiness_url][j] :
                                [!!companyForm.avg_client_value, !!companyForm.keywords, !!companyForm.competitors][j] : false;
                              const isFilled = c.cat === 'Identité' ? [!!companyForm.name, !!companyForm.sector, !!companyForm.logo_url][j]
                                : c.cat === 'Positionnement' ? [!!companyForm.description, !!companyForm.target_audience, !!companyForm.services, !!companyForm.unique_selling_point, !!companyForm.values][j]
                                : c.cat === 'Contact & lieu' ? [!!companyForm.website, !!(companyForm.address || companyForm.city), !!(companyForm.business_phone || companyForm.business_email), !!companyForm.geographic_zone][j]
                                : c.cat === 'Réseaux sociaux' ? [!!companyForm.facebook_url, !!companyForm.instagram_url, !!companyForm.linkedin_url, !!companyForm.google_mybusiness_url][j]
                                : [!!companyForm.avg_client_value, !!companyForm.keywords, !!companyForm.competitors][j];
                              return (
                                <span key={j} style={{
                                  fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 999,
                                  background: isFilled ? '#ECFDF5' : '#FEF2F2',
                                  color: isFilled ? '#065F46' : '#991B1B',
                                  border: `1px solid ${isFilled ? '#6EE7B7' : '#FCA5A5'}`,
                                }}>{isFilled ? '✓' : '+'} {f}</span>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* ── Insight contextuel : anniversaire, etc. ── */}
                {companyForm.founded && (() => {
                  const founded = new Date(companyForm.founded);
                  const nowD = new Date();
                  const years = nowD.getFullYear() - founded.getFullYear();
                  const nextYear = years + 1;
                  const isRound = nextYear % 5 === 0 || nextYear % 10 === 0;
                  return (
                    <div className="company-anniversary-box">
                      <div className="cab-icon">🎂</div>
                      <div className="cab-body">
                        <div className="cab-title">
                          {isRound ? `🎉 Grosse année en vue — ${nextYear} ans l'année prochaine !` : `${nextYear} ans l'année prochaine`}
                        </div>
                        <div className="cab-sub">
                          {isRound
                            ? `Un anniversaire rond de ${nextYear} ans, ça se prépare ! Je peux te proposer une stratégie événement mémorable pour marquer le coup avec tes clients.`
                            : `L'entreprise fêtera ses ${nextYear} ans l'année prochaine. Je garde ça en tête pour te proposer des idées au bon moment.`}
                        </div>
                        {isRound && (
                          <button className="cab-btn" onClick={() => { window.location.href = '/strategy'; }}>
                            Voir les stratégies événement →
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* ── B. Logo & Identité visuelle ── */}
                <Card>
                  <CardHeader title="🎨 Logo & Identité visuelle" />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Logo upload */}
                    <div data-focus-key="logo_url">
                      <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, display: 'block' }}>Logo de l'entreprise</label>
                      <div
                        className="logo-upload-zone"
                        onClick={() => !uploadingLogo && document.getElementById('logo-file-input').click()}
                        onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('drag-over'); }}
                        onDragLeave={e => e.currentTarget.classList.remove('drag-over')}
                        onDrop={e => {
                          e.preventDefault();
                          e.currentTarget.classList.remove('drag-over');
                          const file = e.dataTransfer.files[0];
                          if (file && file.type.startsWith('image/')) handleLogoUpload(file);
                        }}
                        style={{
                          border: '2px dashed var(--line)', borderRadius: 12, padding: '20px 24px',
                          display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer',
                          background: '#FAFAFA', transition: 'border-color 0.15s, background 0.15s',
                          minHeight: 80,
                        }}
                      >
                        {companyForm.logo_url ? (
                          <div style={{ position: 'relative', flexShrink: 0 }}>
                            <img
                              src={companyForm.logo_url}
                              alt="Logo"
                              style={{ width: 60, height: 60, objectFit: 'contain', borderRadius: 8, border: '1px solid var(--line)', display: 'block' }}
                              onError={e => {
                                const src = e.target.src;
                                if (src.includes('/uploads/') && !src.includes('/storage/uploads/')) {
                                  e.target.src = src.replace('/uploads/', '/storage/uploads/');
                                } else {
                                  e.target.style.display = 'none';
                                }
                              }}
                            />
                            <button
                              type="button"
                              onClick={e => { e.stopPropagation(); setC('logo_url', null); }}
                              title="Supprimer le logo"
                              style={{
                                position: 'absolute', top: -7, right: -7,
                                width: 20, height: 20, borderRadius: '50%',
                                background: '#EF4444', border: '2px solid white',
                                color: 'white', fontSize: 11, fontWeight: 700,
                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                lineHeight: 1, padding: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                              }}
                            >✕</button>
                          </div>
                        ) : null}
                        <div style={{ width: 60, height: 60, borderRadius: 8, background: '#F3F4F6', display: companyForm.logo_url ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>🏢</div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13, color: '#374151' }}>
                            {uploadingLogo ? 'Upload en cours…' : companyForm.logo_url ? 'Cliquez ou glissez pour changer' : 'Cliquez ou glissez votre logo ici'}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>PNG, JPG, SVG · max 2 Mo</div>
                        </div>
                        <input id="logo-file-input" type="file" accept="image/*" style={{ display: 'none' }}
                          onChange={e => { const f = e.target.files[0]; if (f) handleLogoUpload(f); e.target.value = ''; }} />
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>
                        Votre logo sera utilisé par l'IA pour créer des visuels et personnaliser vos communications
                      </div>

                      {/* ── Couleurs de marque — utilisées par l'IA pour les visuels ── */}
                      <div style={{ marginTop: 16 }} data-focus-key="brand_primary">
                        <div style={{ fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 8 }}>
                          🎨 Tes couleurs de marque
                        </div>
                        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                            <input
                              type="color"
                              value={companyForm.brand_primary || '#6C3CF4'}
                              onChange={e => setC('brand_primary', e.target.value)}
                              style={{ width: 44, height: 36, border: 'none', borderRadius: 8, cursor: 'pointer', padding: 0, background: 'none' }}
                            />
                            <span style={{ fontSize: 13, color: '#374151' }}>
                              Couleur principale
                              <span style={{ display: 'block', fontSize: 11, color: companyForm.brand_primary ? 'var(--muted)' : '#D97706' }}>{companyForm.brand_primary || '← clique pour choisir'}</span>
                            </span>
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                            <input
                              type="color"
                              value={companyForm.brand_secondary || '#F2B01E'}
                              onChange={e => setC('brand_secondary', e.target.value)}
                              style={{ width: 44, height: 36, border: 'none', borderRadius: 8, cursor: 'pointer', padding: 0, background: 'none' }}
                            />
                            <span style={{ fontSize: 13, color: '#374151' }}>
                              Couleur secondaire
                              <span style={{ display: 'block', fontSize: 11, color: companyForm.brand_secondary ? 'var(--muted)' : '#D97706' }}>{companyForm.brand_secondary || '← clique pour choisir'}</span>
                            </span>
                          </label>
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>
                          L'IA s'en sert pour créer des visuels de posts cohérents avec ta marque
                        </div>
                        {/* 🎨 Coup d'avance : extraire les couleurs directement du site.
                            Doctrine : on montre CE QU'ON A TROUVÉ et OÙ avant d'appliquer. */}
                        {companyForm.website && (() => {
                          let siteHost = companyForm.website.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0];
                          return (
                            <button
                              type="button"
                              disabled={fetchingBrandColors}
                              onClick={async () => {
                                setFetchingBrandColors(true);
                                setBrandColorProposal(null);
                                try {
                                  const res = await api.brandColorsFromSite(companyForm.website);
                                  if (res?.primary) {
                                    setBrandColorProposal(res); // aperçu — rien n'est appliqué encore
                                  } else {
                                    addNotification('Aucune couleur exploitable trouvée sur le site.', 'error');
                                  }
                                } catch (e) {
                                  addNotification(e.message || 'Analyse du site impossible.', 'error');
                                } finally { setFetchingBrandColors(false); }
                              }}
                              style={{ marginTop: 10, border: '1.5px solid var(--line)', background: '#fff', color: 'var(--violet)',
                                fontFamily: 'inherit', fontWeight: 700, fontSize: 12.5, borderRadius: 999, padding: '9px 16px',
                                cursor: fetchingBrandColors ? 'wait' : 'pointer', opacity: fetchingBrandColors ? .6 : 1 }}
                            >
                              {fetchingBrandColors ? `🎨 Analyse de ${siteHost} en cours…` : `🎨 Aller chercher les couleurs sur ${siteHost}`}
                            </button>
                          );
                        })()}
                        {brandColorProposal && (
                          <div style={{ marginTop: 12, border: '1.5px solid var(--line)', borderRadius: 14, padding: '14px 16px', background: 'var(--violet-soft, #F5F3FF)' }}>
                            <div style={{ fontWeight: 700, fontSize: 13, color: '#374151', marginBottom: 10 }}>
                              🔎 Trouvé sur <span style={{ color: 'var(--violet)' }}>{brandColorProposal.host || companyForm.website}</span> — rien n'est appliqué tant que tu n'as pas validé
                            </div>
                            {brandColorProposal.analysis && (
                              <div style={{ fontSize: 12.5, color: '#374151', background: '#fff', border: '1px solid var(--line)', borderRadius: 10, padding: '8px 12px', marginBottom: 10 }}>
                                🧑‍🎨 {brandColorProposal.analysis}
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center' }}>
                              {[['primary', 'Principale'], ['secondary', 'Secondaire'], ['third', '3ᵉ couleur']].filter(([k]) => brandColorProposal[k]).map(([k, lbl]) => (
                                <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span style={{ width: 38, height: 38, borderRadius: 10, background: brandColorProposal[k], border: '2px solid #fff', boxShadow: '0 1px 4px rgba(0,0,0,.15)', display: 'inline-block' }} />
                                  <span style={{ fontSize: 12.5, color: '#374151' }}>
                                    {lbl}
                                    <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)' }}>{brandColorProposal[k]}</span>
                                  </span>
                                </div>
                              ))}
                              <button type="button"
                                onClick={() => setBrandColorProposal(p => ({ ...p, primary: p.secondary, secondary: p.primary }))}
                                style={{ border: '1.5px solid var(--line)', background: '#fff', borderRadius: 999, padding: '5px 12px', fontSize: 11.5, fontWeight: 700, color: '#374151', cursor: 'pointer', fontFamily: 'inherit' }}
                              >↔ Inverser</button>
                            </div>
                            {(brandColorProposal.palette || []).length > 2 && (
                              <div style={{ marginTop: 10 }}>
                                <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>Aussi trouvées sur le site — clique pour l'utiliser en principale :</div>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                  {brandColorProposal.palette.map(hex => (
                                    <button key={hex} type="button" title={`Utiliser ${hex} en couleur principale`}
                                      onClick={() => setBrandColorProposal(p => ({ ...p, primary: hex }))}
                                      style={{ width: 26, height: 26, borderRadius: 8, background: hex, cursor: 'pointer', padding: 0,
                                        border: brandColorProposal.primary === hex ? '2.5px solid var(--violet)' : '2px solid #fff',
                                        boxShadow: '0 1px 3px rgba(0,0,0,.15)' }}
                                    />
                                  ))}
                                </div>
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                              <button type="button"
                                onClick={() => {
                                  setC('brand_primary', brandColorProposal.primary);
                                  if (brandColorProposal.secondary) setC('brand_secondary', brandColorProposal.secondary);
                                  setBrandColorProposal(null);
                                  addNotification('🎨 Couleurs appliquées — pense à sauvegarder en bas de page');
                                }}
                                style={{ border: 'none', background: 'var(--violet)', color: '#fff', borderRadius: 999, padding: '8px 18px', fontSize: 12.5, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}
                              >✅ Appliquer ces couleurs</button>
                              <button type="button" onClick={() => setBrandColorProposal(null)}
                                style={{ border: '1.5px solid var(--line)', background: '#fff', color: 'var(--muted)', borderRadius: 999, padding: '8px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
                              >Annuler</button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="company-fields">
                      <Input label="Nom de l'entreprise" value={companyForm.name || ''} onChange={e => setC('name', e.target.value)} placeholder="Ex: Salon Éclat" />
                      <div data-focus-key="sector"><SectorCombobox value={companyForm.sector || ''} onChange={val => setC('sector', val)} /></div>
                      <Input label="Date de création" type="date" value={companyForm.founded || ''} onChange={e => setC('founded', e.target.value)} />
                      <div data-focus-key="website"><Input label="Site web" value={companyForm.website || ''} onChange={e => setC('website', e.target.value)} placeholder="https://monentreprise.ch" /></div>
                    </div>

                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 160 }}>
                        <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: 'block' }}>Gamme de prix</label>
                        <select className="company-select" value={companyForm.price_range || ''} onChange={e => setC('price_range', e.target.value)}>
                          <option value="">Sélectionner…</option>
                          <option value="budget">Budget / Accessible</option>
                          <option value="mid">Milieu de gamme</option>
                          <option value="premium">Premium</option>
                          <option value="luxury">Luxe</option>
                        </select>
                      </div>
                      <div style={{ flex: 1, minWidth: 160 }} data-focus-key="communication_tone">
                        <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: 'block' }}>Ton de communication</label>
                        <select className="company-select" value={companyForm.communication_tone || ''} onChange={e => setC('communication_tone', e.target.value)}>
                          <option value="">Sélectionner…</option>
                          <option value="formal">Formel / Professionnel</option>
                          <option value="friendly">Amical / Chaleureux</option>
                          <option value="casual">Décontracté / Fun</option>
                          <option value="expert">Expert / Technique</option>
                        </select>
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                      Ces informations permettent à l'IA d'adapter le ton et le style de vos messages
                    </div>
                  </div>
                </Card>

                {/* ── C. Description & Positionnement ── */}
                <Card>
                  <CardHeader title="✍️ Description & Positionnement" />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                      <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: 'block' }}>Description de l'entreprise</label>
                      <textarea className="company-textarea" rows={4}
                        placeholder="Ex: Salon de coiffure haut de gamme au cœur de Genève, spécialisé dans les colorations naturelles et les coupes tendance. Clientèle fidèle depuis 8 ans."
                        value={companyForm.description || ''} onChange={e => setC('description', e.target.value)} />
                    </div>
                    <div>
                      <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: 'block' }}>Clientèle cible</label>
                      <textarea className="company-textarea" rows={3}
                        placeholder="Ex: Femmes 35-55 ans, cadres, cherchant des soins haut de gamme à Genève"
                        value={companyForm.target_audience || ''} onChange={e => setC('target_audience', e.target.value)} />
                    </div>
                    <div>
                      <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: 'block' }}>Services proposés</label>
                      <textarea className="company-textarea" rows={3}
                        placeholder="Ex: Colorations, coupes, soins kératine, extensions..."
                        value={companyForm.services || ''} onChange={e => setC('services', e.target.value)} />
                    </div>
                    <div>
                      <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: 'block' }}>Ce qui vous distingue vraiment</label>
                      <textarea className="company-textarea" rows={2}
                        placeholder="Ce qui vous distingue vraiment de vos concurrents"
                        value={companyForm.unique_selling_point || ''} onChange={e => setC('unique_selling_point', e.target.value)} />
                    </div>
                    <div>
                      <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: 'block' }}>Valeurs & différences</label>
                      <textarea className="company-textarea" rows={2}
                        placeholder="Ex: Proximité, authenticité, qualité des produits, conseil personnalisé..."
                        value={companyForm.values || ''} onChange={e => setC('values', e.target.value)} />
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                      L'IA utilise ces éléments pour rédiger des messages marketing qui vous ressemblent vraiment
                    </div>
                  </div>
                </Card>

                {/* ── D. Localisation & Contact ── */}
                <Card>
                  <CardHeader title="📍 Localisation & Contact professionnel" subtitle="L'IA utilise ces infos pour cibler localement et personnaliser les stratégies" />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div className="company-fields">
                      <Input label="Adresse" value={companyForm.address || ''} onChange={e => setC('address', e.target.value)} placeholder="Rue des Alpes 12" />
                      <div data-focus-key="city"><Input label="NPA / Ville" value={companyForm.city || ''} onChange={e => setC('city', e.target.value)} placeholder="1201 Genève" /></div>
                    </div>
                    <div className="company-fields">
                      <div data-focus-key="business_phone"><Input label="Téléphone professionnel" type="tel" value={companyForm.business_phone || ''} onChange={e => setC('business_phone', e.target.value)} placeholder="+41 22 000 00 00" /></div>
                      <Input label="E-mail professionnel" type="email" value={companyForm.business_email || ''} onChange={e => setC('business_email', e.target.value)} placeholder="contact@monentreprise.ch" />
                    </div>
                    <div>
                      <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: 'block' }}>Zone géographique desservie</label>
                      <input className="company-input" value={companyForm.geographic_zone || ''} onChange={e => setC('geographic_zone', e.target.value)} placeholder="Ex: Genève, Vaud, Valais · ou 'Suisse romande' · ou 'National'" style={{ width: '100%', padding: '9px 12px', border: '1.5px solid var(--line)', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: 'block' }}>Horaires d'ouverture <span style={{ fontWeight: 400, color: 'var(--muted)' }}>(optionnel)</span></label>
                      <input className="company-input" value={companyForm.business_hours || ''} onChange={e => setC('business_hours', e.target.value)} placeholder="Ex: Lun-Ven 9h-18h, Sam 9h-12h" style={{ width: '100%', padding: '9px 12px', border: '1.5px solid var(--line)', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                    </div>
                  </div>
                </Card>

                {/* ── E. Contexte marché ── */}
                <Card>
                  <CardHeader title="💡 Contexte marché" subtitle="Ces données affinent les stratégies et le ROI estimé par l'IA" />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div className="company-fields">
                      <div data-focus-key="avg_client_value">
                        <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: 'block' }}>Ticket moyen client <span style={{ fontWeight: 400, color: 'var(--muted)' }}>(CHF)</span></label>
                        <input type="number" min="0" step="10" className="company-input" value={companyForm.avg_client_value || ''} onChange={e => setC('avg_client_value', e.target.value)} placeholder="Ex: 150" style={{ width: '100%', padding: '9px 12px', border: '1.5px solid var(--line)', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Permet à l'IA d'estimer le ROI de chaque campagne</div>
                      </div>
                      <div>
                        <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: 'block' }}>Nombre de collaborateurs</label>
                        <select className="company-select" value={companyForm.team_size || ''} onChange={e => setC('team_size', e.target.value)}>
                          <option value="">Sélectionne...</option>
                          <option value="solo">Juste moi (solo)</option>
                          <option value="2-5">2 à 5 personnes</option>
                          <option value="6-15">6 à 15 personnes</option>
                          <option value="16-50">16 à 50 personnes</option>
                          <option value="50+">Plus de 50</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: 'block' }}>Mots-clés principaux <span style={{ fontWeight: 400, color: 'var(--muted)' }}>(SEO & contenu)</span></label>
                      <input className="company-input" value={companyForm.keywords || ''} onChange={e => setC('keywords', e.target.value)} placeholder="Ex: coiffure femme genève, salon coiffure luxe, coloration naturelle" style={{ width: '100%', padding: '9px 12px', border: '1.5px solid var(--line)', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Séparés par des virgules — utilisés dans les objets d'e-mails et les campagnes</div>
                    </div>
                    <div>
                      <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: 'block' }}>Principaux concurrents <span style={{ fontWeight: 400, color: 'var(--muted)' }}>(optionnel)</span></label>
                      <textarea className="company-textarea" rows={2} value={companyForm.competitors || ''} onChange={e => setC('competitors', e.target.value)} placeholder="Ex: Salon Belle, Institut Lumière, Coiff'Expert Genève" />
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>L'IA s'en sert pour différencier tes messages et identifier tes avantages concurrentiels</div>
                    </div>
                  </div>
                  <div className="company-collab-hint" style={{ marginTop: 14 }}>
                    💡 Pour que tes collaborateurs soient intégrés dans Appee, importe-les dans tes contacts avec la catégorie <strong>Collaborateur</strong> — Appee te rappellera leurs anniversaires et créera des moments forts avec eux.
                  </div>
                </Card>

                {/* ── E. Présence digitale & réseaux sociaux ── */}
                <Card>
                  <CardHeader title="🌐 Présence digitale & réseaux sociaux" />
                  {/* ── 3 colonnes fixes : Facebook / Instagram / LinkedIn ── */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
                    {socialNetworks.filter(n => n.key !== 'google_mybusiness').map(net => {
                      const hasUrl = !!companyForm[net.urlKey];
                      const analysis = hasUrl ? (companyForm.social_analysis?.[net.key] ?? null) : null;
                      const isAnalyzing = analyzingSocial[net.key];
                      const isExpanded = expandedSocial[net.key];
                      const scoreCol = analysis
                        ? analysis.score >= 70 ? '#10B981' : analysis.score >= 40 ? '#F59E0B' : '#EF4444'
                        : null;
                      return (
                        <div key={net.key} className="social-card" style={{
                          border: '1.5px solid var(--line)', borderRadius: 14, padding: '16px',
                          background: 'white', display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0,
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 14 }}>
                            <span style={{ fontSize: 20 }}>{net.icon}</span>
                            <span>{net.label}</span>
                            {analysis && hasUrl && (
                              <span style={{ marginLeft: 'auto', background: `${scoreCol}20`, color: scoreCol, borderRadius: 999, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>
                                {analysis.score}/100
                              </span>
                            )}
                          </div>

                          {/* ── Google My Business — flow structuré ── */}
                          {net.key === 'google_mybusiness' ? (
                            <div>
                              <p style={{ fontSize: 13, color: '#374151', marginBottom: 10 }}>
                                Avez-vous une fiche Google My Business ?
                              </p>
                              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                                <button
                                  onClick={() => setC('gmb_has_page', true)}
                                  style={{
                                    padding: '6px 16px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
                                    background: companyForm.gmb_has_page === true ? '#5A35FF' : '#F3F4F6',
                                    color: companyForm.gmb_has_page === true ? 'white' : '#374151',
                                    border: companyForm.gmb_has_page === true ? '2px solid #5A35FF' : '2px solid #E5E7EB',
                                    fontWeight: companyForm.gmb_has_page === true ? 700 : 400,
                                  }}
                                >Oui ✅</button>
                                <button
                                  onClick={() => {
                                    setC('gmb_has_page', false);
                                    setC('google_mybusiness_url', '');
                                    if (companyForm.social_analysis?.google_mybusiness) {
                                      const newA = { ...companyForm.social_analysis };
                                      delete newA.google_mybusiness;
                                      setC('social_analysis', newA);
                                    }
                                  }}
                                  style={{
                                    padding: '6px 16px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
                                    background: companyForm.gmb_has_page === false ? '#EF4444' : '#F3F4F6',
                                    color: companyForm.gmb_has_page === false ? 'white' : '#374151',
                                    border: companyForm.gmb_has_page === false ? '2px solid #EF4444' : '2px solid #E5E7EB',
                                    fontWeight: companyForm.gmb_has_page === false ? 700 : 400,
                                  }}
                                >Non ❌</button>
                              </div>

                              {companyForm.gmb_has_page === true && (
                                <div>
                                  {companyForm.name && (
                                    <a
                                      href={`https://www.google.com/search?q=${encodeURIComponent(stripLegalSuffix(companyForm.name || '') || companyForm.name)} Google My Business`}
                                      target="_blank" rel="noopener noreferrer"
                                      style={{ fontSize: 12, color: '#5A35FF', display: 'inline-block', marginBottom: 8 }}
                                    >🔍 Trouver ma fiche Google →</a>
                                  )}
                                  <input
                                    value={companyForm.google_mybusiness_url || ''}
                                    onChange={e => {
                                      const val = e.target.value;
                                      setC('google_mybusiness_url', val);
                                      if (!val && companyForm.social_analysis?.google_mybusiness) {
                                        const newA = { ...companyForm.social_analysis };
                                        delete newA.google_mybusiness;
                                        setC('social_analysis', newA);
                                      }
                                    }}
                                    placeholder="Coller l'URL de votre fiche Google"
                                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1.5px solid #E5E7EB', fontSize: 13, boxSizing: 'border-box', marginBottom: 8 }}
                                  />
                                  <button
                                    onClick={() => handleAnalyzeSocial(net.key)}
                                    disabled={isAnalyzing}
                                    style={{
                                      background: isAnalyzing ? '#E5E7EB' : 'var(--violet)', color: isAnalyzing ? '#9CA3AF' : 'white',
                                      border: 'none', borderRadius: 8, padding: '8px 12px', fontWeight: 600, fontSize: 12, cursor: isAnalyzing ? 'not-allowed' : 'pointer',
                                    }}>
                                    {isAnalyzing ? '⏳ Analyse en cours…' : '🔍 Analyser'}
                                  </button>
                                </div>
                              )}

                              {companyForm.gmb_has_page === false && (
                                <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 8, padding: 12 }}>
                                  <p style={{ fontSize: 13, fontWeight: 600, color: '#92400E', marginBottom: 6 }}>
                                    📍 Une fiche Google My Business est essentielle pour être trouvé localement !
                                  </p>
                                  <p style={{ fontSize: 12, color: '#78350F', marginBottom: 8 }}>
                                    C'est gratuit, vous apparaissez sur Google Maps et les recherches locales. L'IA intégrera cette priorité dans votre audit.
                                  </p>
                                  <a
                                    href="https://business.google.com/create"
                                    target="_blank" rel="noopener noreferrer"
                                    style={{ fontSize: 13, color: '#5A35FF', fontWeight: 700 }}
                                  >➕ Créer ma fiche gratuite →</a>
                                </div>
                              )}
                            </div>
                          ) : (
                            /* ── Autres réseaux — flow recherche par nom/handle ── */
                            <div>
                              {/* URL déjà enregistrée */}
                              {companyForm[net.urlKey] ? (
                                <div style={{ marginBottom: 10 }}>
                                  {/* URL + logo + changer */}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#F0FDF4', border: '1.5px solid #86EFAC', borderRadius: 8, padding: '7px 10px', marginBottom: 6 }}>
                                    {companyForm.social_analysis?.[net.key]?.profile_image ? (
                                      <img src={companyForm.social_analysis[net.key].profile_image} alt="" style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} onError={e => { e.target.style.display='none'; }} />
                                    ) : (
                                      <span style={{ fontSize: 18, flexShrink: 0 }}>{net.icon}</span>
                                    )}
                                    <a
                                      href={companyForm[net.urlKey]}
                                      target="_blank" rel="noopener noreferrer"
                                      style={{ fontSize: 12, color: '#047857', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}
                                    >{companyForm[net.urlKey]}</a>
                                    <button
                                      onClick={() => {
                                        setC(net.urlKey, '');
                                        if (companyForm.social_analysis?.[net.key]) {
                                          const newA = { ...companyForm.social_analysis };
                                          delete newA[net.key];
                                          setC('social_analysis', newA);
                                        }
                                        setSocialCandidates(s => ({ ...s, [net.key]: null }));
                                      }}
                                      style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: 16, padding: '0 2px', lineHeight: 1, flexShrink: 0 }}
                                      title="Changer de page"
                                    >×</button>
                                  </div>
                                  {/* Analyser + Enregistrer */}
                                  <div style={{ display: 'flex', gap: 6 }}>
                                    <button
                                      onClick={() => handleAnalyzeSocial(net.key)}
                                      disabled={isAnalyzing}
                                      style={{
                                        background: isAnalyzing ? '#E5E7EB' : '#6B7280', color: isAnalyzing ? '#9CA3AF' : 'white',
                                        border: 'none', borderRadius: 8, padding: '7px 10px', fontWeight: 600, fontSize: 11,
                                        cursor: isAnalyzing ? 'not-allowed' : 'pointer', flex: 1,
                                      }}
                                    >
                                      {isAnalyzing ? '⏳…' : '🔍 Analyser'}
                                    </button>
                                    <button
                                      onClick={() => saveCompany()}
                                      disabled={companySaving}
                                      style={{
                                        background: companySaving ? '#E5E7EB' : '#10B981', color: companySaving ? '#9CA3AF' : 'white',
                                        border: 'none', borderRadius: 8, padding: '7px 10px', fontWeight: 700, fontSize: 11,
                                        cursor: companySaving ? 'not-allowed' : 'pointer', flex: 1,
                                      }}
                                    >
                                      {companySaving ? '⏳…' : '💾 Enregistrer'}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                /* Champ de recherche par nom / handle */
                                <div>
                                  <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>
                                    Tapez le nom de votre entreprise ou votre @handle
                                  </div>
                                  <div style={{ display: 'flex', gap: 6 }}>
                                    <input
                                      type="text"
                                      placeholder={`Ex: Salon Éclat ou @saloneclat`}
                                      value={socialSearchQuery[net.key] || ''}
                                      onChange={e => setSocialSearchQuery(s => ({ ...s, [net.key]: e.target.value }))}
                                      onKeyDown={e => { if (e.key === 'Enter') handleFindSocialPage(net.key); }}
                                      style={{ flex: 1, padding: '8px 10px', border: '1.5px solid var(--line)', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', minWidth: 0 }}
                                    />
                                    <button
                                      onClick={() => handleFindSocialPage(net.key)}
                                      disabled={socialSearching[net.key] || !(socialSearchQuery[net.key] || '').trim()}
                                      style={{
                                        background: (socialSearching[net.key] || !(socialSearchQuery[net.key] || '').trim()) ? '#E5E7EB' : 'var(--violet)',
                                        color: (socialSearching[net.key] || !(socialSearchQuery[net.key] || '').trim()) ? '#9CA3AF' : 'white',
                                        border: 'none', borderRadius: 8, padding: '8px 12px', fontWeight: 700, fontSize: 12,
                                        cursor: (socialSearching[net.key] || !(socialSearchQuery[net.key] || '').trim()) ? 'not-allowed' : 'pointer',
                                        whiteSpace: 'nowrap', flexShrink: 0,
                                      }}
                                    >
                                      {socialSearching[net.key] ? '⏳' : '🔍 Chercher'}
                                    </button>
                                  </div>

                                  {/* Résultats de recherche */}
                                  {socialCandidates[net.key] !== null && socialCandidates[net.key] !== undefined && (
                                    <div style={{ marginTop: 10 }}>
                                      {socialCandidates[net.key].length === 0 ? (
                                        <div style={{ fontSize: 12, color: 'var(--muted)', background: '#F9FAFB', borderRadius: 8, padding: '10px 12px' }}>
                                          Aucune page trouvée. Essayez un autre nom ou collez l'URL directement ci-dessous.
                                        </div>
                                      ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', marginBottom: 2 }}>
                                            Sélectionnez votre page :
                                          </div>
                                          {socialCandidates[net.key].map((c, idx) => (
                                            <div
                                              key={idx}
                                              style={{
                                                display: 'flex', alignItems: 'center', gap: 8,
                                                background: c.verified ? '#F0FDF4' : '#F9FAFB',
                                                border: `1.5px solid ${c.verified ? '#86EFAC' : '#E5E7EB'}`,
                                                borderRadius: 8, padding: '8px 10px',
                                              }}
                                            >
                                              {/* Logo de profil de la page */}
                                              {c.image ? (
                                                <img
                                                  src={c.image} alt=""
                                                  style={{ width: 38, height: 38, borderRadius: 8, objectFit: 'cover', border: '1px solid #E5E7EB', flexShrink: 0 }}
                                                  onError={e => { e.target.style.display = 'none'; }}
                                                />
                                              ) : (
                                                <div style={{ width: 38, height: 38, borderRadius: 8, background: '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                                                  {net.icon}
                                                </div>
                                              )}
                                              <div style={{ flex: 1, minWidth: 0 }}>
                                                {c.name && (
                                                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1F2937', marginBottom: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {c.name}
                                                  </div>
                                                )}
                                                <div style={{ fontSize: 11, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                  {c.url}
                                                </div>
                                                {c.verified
                                                  ? <div style={{ fontSize: 10, color: '#059669', fontWeight: 600, marginTop: 1 }}>✓ Page accessible</div>
                                                  : <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 1 }}>Page non vérifiée</div>
                                                }
                                              </div>
                                              <button
                                                onClick={() => handleSelectSocialCandidate(net.key, net.urlKey, c.url, c.image)}
                                                style={{
                                                  background: 'var(--violet)', color: 'white', border: 'none',
                                                  borderRadius: 7, padding: '6px 12px', fontWeight: 700, fontSize: 12,
                                                  cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                                                }}
                                              >
                                                ✅ Choisir
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      {/* Option URL manuelle */}
                                      <div style={{ marginTop: 8, borderTop: '1px solid var(--line)', paddingTop: 8 }}>
                                        <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>Ou collez l'URL directement :</div>
                                        <div style={{ display: 'flex', gap: 6 }}>
                                          <input
                                            type="url"
                                            placeholder={`https://www.${net.key === 'linkedin' ? 'linkedin.com/company/' : net.key + '.com/'}...`}
                                            style={{ flex: 1, padding: '7px 10px', border: '1.5px solid var(--line)', borderRadius: 7, fontSize: 12, boxSizing: 'border-box', minWidth: 0 }}
                                            onBlur={e => { if (e.target.value) handleSelectSocialCandidate(net.key, net.urlKey, e.target.value); }}
                                            onKeyDown={e => { if (e.key === 'Enter' && e.target.value) handleSelectSocialCandidate(net.key, net.urlKey, e.target.value); }}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Hint initial: pas encore recherché */}
                                  {(socialCandidates[net.key] === null || socialCandidates[net.key] === undefined) && companyForm.name && !socialSearchQuery[net.key] && (
                                    <div style={{ marginTop: 6, fontSize: 11, color: 'var(--muted)' }}>
                                      💡 Conseil : recherchez «{' '}
                                      <button
                                        onClick={() => {
                                          const name = stripLegalSuffix(companyForm.name || '').trim() || companyForm.name;
                                          setSocialSearchQuery(s => ({ ...s, [net.key]: name }));
                                          handleFindSocialPage(net.key, name);
                                        }}
                                        style={{ background: 'none', border: 'none', color: 'var(--violet)', cursor: 'pointer', fontSize: 11, fontWeight: 700, padding: 0, textDecoration: 'underline' }}
                                      >{stripLegalSuffix(companyForm.name || '').trim() || companyForm.name}</button>
                                      {' » '} pour trouver votre page automatiquement
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          {analysis && (
                            <div className="social-analysis-result" style={{ fontSize: 12, lineHeight: 1.5 }}>
                              {analysis.analyzed_at && (
                                <div style={{ color: 'var(--muted)', marginBottom: 6, fontSize: 11 }}>
                                  Analysé le {new Date(analysis.analyzed_at).toLocaleDateString('fr-CH')}
                                </div>
                              )}
                              <div style={{ color: '#374151', marginBottom: 8, fontStyle: 'italic' }}>{analysis.summary}</div>

                              {/* ── Indicateurs clés ── */}
                              {analysis.indicators?.length > 0 && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 5, marginBottom: 8 }}>
                                  {analysis.indicators.map((ind, i) => {
                                    const col = ind.status === 'good' ? '#10B981' : ind.status === 'warning' ? '#F59E0B' : ind.status === 'critical' ? '#EF4444' : '#9CA3AF';
                                    return (
                                      <div key={i} style={{ background: `${col}12`, border: `1px solid ${col}35`, borderRadius: 8, padding: '7px 9px' }}>
                                        <div style={{ fontSize: 10, color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.2, marginBottom: 2 }}>{ind.label}</div>
                                        <div style={{ fontSize: 13, fontWeight: 800, color: '#1F2937', lineHeight: 1.2 }}>{ind.value}</div>
                                        {ind.hint && <div style={{ fontSize: 10, color: col, marginTop: 3, lineHeight: 1.3 }}>{ind.hint}</div>}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              <button
                                onClick={() => setExpandedSocial(s => ({ ...s, [net.key]: !s[net.key] }))}
                                style={{ background: 'none', border: 'none', color: 'var(--violet)', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: 0 }}>
                                {isExpanded ? '▲ Masquer les conseils' : '▼ Voir les conseils IA'}
                              </button>
                              {isExpanded && (
                                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                  {analysis.strengths?.length > 0 && (
                                    <div>
                                      <div style={{ fontWeight: 700, color: '#10B981', marginBottom: 2 }}>✓ Points forts :</div>
                                      {analysis.strengths.map((s, i) => <div key={i} style={{ paddingLeft: 8, color: '#374151' }}>{s}</div>)}
                                    </div>
                                  )}
                                  {analysis.improvements?.length > 0 && (
                                    <div>
                                      <div style={{ fontWeight: 700, color: '#F59E0B', marginBottom: 2 }}>→ À améliorer :</div>
                                      {analysis.improvements.map((s, i) => <div key={i} style={{ paddingLeft: 8, color: '#374151' }}>{s}</div>)}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* ── Google My Business — pleine largeur ── */}
                  {(() => {
                    const net = socialNetworks.find(n => n.key === 'google_mybusiness');
                    const hasUrl = companyForm.gmb_has_page === true;
                    const analysis = hasUrl ? (companyForm.social_analysis?.google_mybusiness ?? null) : null;
                    const isAnalyzing = analyzingSocial.google_mybusiness;
                    const isExpanded = expandedSocial.google_mybusiness;
                    const scoreCol = analysis ? (analysis.score >= 70 ? '#10B981' : analysis.score >= 40 ? '#F59E0B' : '#EF4444') : null;
                    return (
                      <div style={{ border: '1.5px solid var(--line)', borderRadius: 14, padding: '16px', background: 'white', display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 14 }}>
                          <span style={{ fontSize: 20 }}>{net.icon}</span>
                          <span>{net.label}</span>
                          {analysis && hasUrl && (
                            <span style={{ marginLeft: 'auto', background: `${scoreCol}20`, color: scoreCol, borderRadius: 999, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>
                              {analysis.score}/100
                            </span>
                          )}
                        </div>
                        {/* GMB flow */}
                        <div>
                          <p style={{ fontSize: 13, color: '#374151', marginBottom: 10 }}>Avez-vous une fiche Google My Business ?</p>
                          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                            <button onClick={() => setC('gmb_has_page', true)} style={{ padding: '6px 16px', borderRadius: 20, fontSize: 13, cursor: 'pointer', background: companyForm.gmb_has_page === true ? '#5A35FF' : '#F3F4F6', color: companyForm.gmb_has_page === true ? 'white' : '#374151', border: companyForm.gmb_has_page === true ? '2px solid #5A35FF' : '2px solid #E5E7EB', fontWeight: companyForm.gmb_has_page === true ? 700 : 400 }}>Oui ✅</button>
                            <button onClick={() => { setC('gmb_has_page', false); setC('google_mybusiness_url', ''); if (companyForm.social_analysis?.google_mybusiness) { const newA = { ...companyForm.social_analysis }; delete newA.google_mybusiness; setC('social_analysis', newA); } }} style={{ padding: '6px 16px', borderRadius: 20, fontSize: 13, cursor: 'pointer', background: companyForm.gmb_has_page === false ? '#EF4444' : '#F3F4F6', color: companyForm.gmb_has_page === false ? 'white' : '#374151', border: companyForm.gmb_has_page === false ? '2px solid #EF4444' : '2px solid #E5E7EB', fontWeight: companyForm.gmb_has_page === false ? 700 : 400 }}>Non ❌</button>
                          </div>
                          {companyForm.gmb_has_page === true && (
                            <div>
                              {companyForm.name && (<a href={`https://www.google.com/search?q=${encodeURIComponent(stripLegalSuffix(companyForm.name || '') || companyForm.name)} Google My Business`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#5A35FF', display: 'inline-block', marginBottom: 8 }}>🔍 Trouver ma fiche Google →</a>)}
                              <input value={companyForm.google_mybusiness_url || ''} onChange={e => { const val = e.target.value; setC('google_mybusiness_url', val); if (!val && companyForm.social_analysis?.google_mybusiness) { const newA = { ...companyForm.social_analysis }; delete newA.google_mybusiness; setC('social_analysis', newA); } }} placeholder="Coller l'URL de votre fiche Google" style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1.5px solid #E5E7EB', fontSize: 13, boxSizing: 'border-box', marginBottom: 8 }} />
                              <button onClick={() => handleAnalyzeSocial('google_mybusiness')} disabled={isAnalyzing} style={{ background: isAnalyzing ? '#E5E7EB' : 'var(--violet)', color: isAnalyzing ? '#9CA3AF' : 'white', border: 'none', borderRadius: 8, padding: '8px 12px', fontWeight: 600, fontSize: 12, cursor: isAnalyzing ? 'not-allowed' : 'pointer' }}>
                                {isAnalyzing ? '⏳ Analyse en cours…' : '🔍 Analyser'}
                              </button>
                            </div>
                          )}
                          {companyForm.gmb_has_page === false && (
                            <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 8, padding: 12 }}>
                              <p style={{ fontSize: 13, fontWeight: 600, color: '#92400E', marginBottom: 6 }}>📍 Une fiche Google My Business est essentielle pour être trouvé localement !</p>
                              <p style={{ fontSize: 12, color: '#78350F', marginBottom: 8 }}>C'est gratuit, vous apparaissez sur Google Maps et les recherches locales.</p>
                              <a href="https://business.google.com/create" target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: '#5A35FF', fontWeight: 700 }}>➕ Créer ma fiche gratuite →</a>
                            </div>
                          )}
                        </div>
                        {analysis && (
                          <div className="social-analysis-result" style={{ fontSize: 12, lineHeight: 1.5 }}>
                            {analysis.analyzed_at && <div style={{ color: 'var(--muted)', marginBottom: 6, fontSize: 11 }}>Analysé le {new Date(analysis.analyzed_at).toLocaleDateString('fr-CH')}</div>}
                            <div style={{ color: '#374151', marginBottom: 8, fontStyle: 'italic' }}>{analysis.summary}</div>
                            {analysis.indicators?.length > 0 && (
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 5, marginBottom: 8 }}>
                                {analysis.indicators.map((ind, i) => {
                                  const col = ind.status === 'good' ? '#10B981' : ind.status === 'warning' ? '#F59E0B' : ind.status === 'critical' ? '#EF4444' : '#9CA3AF';
                                  return (
                                    <div key={i} style={{ background: `${col}12`, border: `1px solid ${col}35`, borderRadius: 8, padding: '7px 9px' }}>
                                      <div style={{ fontSize: 10, color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.2, marginBottom: 2 }}>{ind.label}</div>
                                      <div style={{ fontSize: 13, fontWeight: 800, color: '#1F2937', lineHeight: 1.2 }}>{ind.value}</div>
                                      {ind.hint && <div style={{ fontSize: 10, color: col, marginTop: 3, lineHeight: 1.3 }}>{ind.hint}</div>}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            <button onClick={() => setExpandedSocial(s => ({ ...s, google_mybusiness: !s.google_mybusiness }))} style={{ background: 'none', border: 'none', color: 'var(--violet)', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: 0 }}>
                              {isExpanded ? '▲ Masquer les conseils' : '▼ Voir les conseils IA'}
                            </button>
                            {isExpanded && (
                              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {analysis.strengths?.length > 0 && <div><div style={{ fontWeight: 700, color: '#10B981', marginBottom: 2 }}>✓ Points forts :</div>{analysis.strengths.map((s, i) => <div key={i} style={{ paddingLeft: 8, color: '#374151' }}>{s}</div>)}</div>}
                                {analysis.improvements?.length > 0 && <div><div style={{ fontWeight: 700, color: '#F59E0B', marginBottom: 2 }}>→ À améliorer :</div>{analysis.improvements.map((s, i) => <div key={i} style={{ paddingLeft: 8, color: '#374151' }}>{s}</div>)}</div>}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: -4, marginBottom: 4 }}>
                    L'IA analyse votre présence sur chaque réseau et vous donne des recommandations concrètes
                  </div>

                  {/* ── Site internet ── */}
                  <div style={{ borderTop: '1.5px solid var(--line)', paddingTop: 18, marginTop: 6 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 20 }}>🌍</span> Site internet
                      <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--muted)', background: 'var(--line)', borderRadius: 20, padding: '2px 8px' }}>
                        SEO · Mobile · Sécurité · Réseaux sociaux
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <input
                        type="url"
                        placeholder="https://www.mon-entreprise.ch"
                        value={companyForm.website_url || companyForm.website || ''}
                        onChange={e => setC('website_url', e.target.value)}
                        style={{ flex: 1, minWidth: 220, padding: '9px 12px', border: '1.5px solid var(--line)', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }}
                      />
                      <button
                        onClick={handleAnalyzeWebsite}
                        disabled={analyzingWebsite || !(companyForm.website_url || companyForm.website)}
                        style={{
                          background: analyzingWebsite || !(companyForm.website_url || companyForm.website) ? '#E5E7EB' : 'var(--violet)',
                          color: analyzingWebsite || !(companyForm.website_url || companyForm.website) ? '#9CA3AF' : 'white',
                          border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 700, fontSize: 13,
                          cursor: analyzingWebsite || !(companyForm.website_url || companyForm.website) ? 'not-allowed' : 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {analyzingWebsite ? '⏳ Analyse…' : '🔍 Analyser le site'}
                      </button>
                      {(companyForm.website_url || companyForm.website) && (
                        <a
                          href={companyForm.website_url || companyForm.website}
                          target="_blank" rel="noopener noreferrer"
                          style={{ fontSize: 12, color: 'var(--violet)', fontWeight: 600, whiteSpace: 'nowrap' }}
                        >↗ Ouvrir</a>
                      )}
                    </div>

                    {/* Résultats analyse site */}
                    {websiteAnalysis && (
                      <div style={{ marginTop: 12, background: '#F8FAFF', border: '1.5px solid #C7D2FE', borderRadius: 10, padding: '14px 16px', fontSize: 13 }}>
                        <div style={{ fontWeight: 700, marginBottom: 6, color: '#1F2937', display: 'flex', alignItems: 'center', gap: 8 }}>
                          📋 {websiteAnalysis.title || companyForm.website_url}
                          {(companyForm.website_url || companyForm.website) && (
                            <a href={companyForm.website_url || companyForm.website} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: 'var(--violet)', fontWeight: 500 }}>↗ Ouvrir</a>
                          )}
                        </div>
                        {websiteAnalysis.description && (
                          <p style={{ color: '#4B5563', marginBottom: 12, fontSize: 12, lineHeight: 1.5, fontStyle: 'italic' }}>{websiteAnalysis.description}</p>
                        )}

                        {/* ── Indicateurs techniques ── */}
                        {websiteAnalysis.indicators?.length > 0 && (
                          <div style={{ marginBottom: 14 }}>
                            <div style={{ fontWeight: 700, fontSize: 12, color: '#374151', marginBottom: 8 }}>⚡ Santé technique du site</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                              {websiteAnalysis.indicators.map((ind, i) => {
                                const col = ind.status === 'good' ? '#10B981' : ind.status === 'warning' ? '#F59E0B' : ind.status === 'critical' ? '#EF4444' : '#9CA3AF';
                                return (
                                  <div key={i} style={{ background: `${col}10`, border: `1.5px solid ${col}30`, borderRadius: 9, padding: '8px 10px' }}>
                                    <div style={{ fontSize: 10, color: '#6B7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 3 }}>{ind.label}</div>
                                    <div style={{ fontSize: 13, fontWeight: 800, color: '#1F2937', lineHeight: 1.2, marginBottom: 2 }}>{ind.value}</div>
                                    {ind.hint && <div style={{ fontSize: 10, color: col, lineHeight: 1.3 }}>{ind.hint}</div>}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Analyse & Conseil */}
                        {websiteAnalysis.analysis && (
                          <div style={{ marginTop: 10, background: '#EFF6FF', border: '1.5px solid #BFDBFE', borderRadius: 9, padding: '12px 14px' }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#1D4ED8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>📊 Analyse</div>
                            <p style={{ fontSize: 13, color: '#1E3A5F', lineHeight: 1.6, margin: 0 }}>{websiteAnalysis.analysis}</p>
                          </div>
                        )}
                        {websiteAnalysis.conseil && (
                          <div style={{ marginTop: 8, background: '#F0FDF4', border: '1.5px solid #86EFAC', borderRadius: 9, padding: '12px 14px', marginBottom: 14 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#059669', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>💡 Conseil prioritaire</div>
                            <p style={{ fontSize: 13, color: '#064E3B', lineHeight: 1.6, margin: 0 }}>{websiteAnalysis.conseil}</p>
                          </div>
                        )}

                        {/* Réseaux trouvés */}
                        {websiteAnalysis.social_links && Object.keys(websiteAnalysis.social_links).length > 0 ? (
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 12, color: '#374151', marginBottom: 6 }}>🔗 Réseaux sociaux détectés sur le site :</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              {Object.entries(websiteAnalysis.social_links).map(([net, url]) => {
                                const netLabel = { facebook: 'Facebook', instagram: 'Instagram', linkedin: 'LinkedIn', google_mybusiness: 'Google My Business' }[net] || net;
                                const currentVal = companyForm[`${net}_url`] || companyForm[`${net}Url`] || '';
                                const alreadySet = !!currentVal;
                                const sameUrl = currentVal === url;
                                return (
                                  <div key={net} style={{ display: 'flex', alignItems: 'center', gap: 6, background: sameUrl ? '#DCFCE7' : alreadySet ? '#FEF3C7' : '#EDE9FE', borderRadius: 8, padding: '5px 10px', fontSize: 12 }}>
                                    <span style={{ fontWeight: 700 }}>{netLabel}</span>
                                    <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#5A35FF', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{url}</a>
                                    {sameUrl ? (
                                      <span style={{ color: '#059669', fontWeight: 700 }}>✓ Identique</span>
                                    ) : alreadySet ? (
                                      <button onClick={() => { if (net === 'google_mybusiness') { setC('google_mybusiness_url', url); setC('gmb_has_page', true); } else setC(`${net}_url`, url); }} style={{ background: '#F59E0B', color: 'white', border: 'none', borderRadius: 6, padding: '2px 8px', fontSize: 11, cursor: 'pointer', fontWeight: 700 }}>
                                        ⚠ Remplacer
                                      </button>
                                    ) : (
                                      <button onClick={() => { if (net === 'google_mybusiness') { setC('google_mybusiness_url', url); setC('gmb_has_page', true); } else setC(`${net}_url`, url); }} style={{ background: '#5A35FF', color: 'white', border: 'none', borderRadius: 6, padding: '2px 8px', fontSize: 11, cursor: 'pointer', fontWeight: 700 }}>
                                        + Utiliser
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <div style={{ color: 'var(--muted)', fontSize: 12 }}>
                            Aucun lien vers un réseau social trouvé sur ce site. Vérifiez l'URL ou renseignez les liens manuellement.
                          </div>
                        )}
                        {/* Double contrôle : comparaison site vs profil */}
                        {websiteAnalysis.discrepancies?.length > 0 && (
                          <div style={{ marginTop: 10, background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 12px' }}>
                            <div style={{ fontWeight: 700, fontSize: 12, color: '#92400E', marginBottom: 4 }}>⚠ Divergences détectées :</div>
                            {websiteAnalysis.discrepancies.map((d, i) => (
                              <div key={i} style={{ fontSize: 12, color: '#78350F' }}>→ {d}</div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Card>

                {/* ── F. Audit Marketing Complet ── */}
                {(() => {
                  // Liste unifiée : audit courant en tête + historique
                  const allAudits = [
                    ...(companyForm.marketing_audit?.sections ? [companyForm.marketing_audit] : []),
                    ...auditHistory,
                  ].sort((a, b) => new Date(b.audited_at) - new Date(a.audited_at));

                  return (
                    <div className="audit-cta-card" style={{
                      border: '2px solid #7C3AED', borderRadius: 16, padding: '24px',
                      background: 'linear-gradient(135deg, #F5F3FF, #EDE9FE)',
                    }}>
                      {/* En-tête + bouton lancer */}
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: allAudits.length > 0 ? 20 : 0 }}>
                        <div style={{ flex: 1, minWidth: 200 }}>
                          <div style={{ fontWeight: 800, fontSize: 17, color: '#4C1D95', marginBottom: 6 }}>
                            🔍 Audit marketing complet
                          </div>
                          <div style={{ fontSize: 13, color: '#5B21B6', lineHeight: 1.5 }}>
                            L'IA analyse tout ce qu'elle sait de votre entreprise — positionnement, réseaux, site web, communication — et vous donne un rapport complet avec priorités d'action.
                          </div>
                        </div>
                        <button
                          onClick={handleMarketingAudit}
                          disabled={auditLoading}
                          style={{
                            background: auditLoading ? '#C4B5FD' : 'linear-gradient(135deg, #7C3AED, #5A35FF)',
                            color: 'white', border: 'none', borderRadius: 10, padding: '11px 22px',
                            fontWeight: 700, fontSize: 14, cursor: auditLoading ? 'not-allowed' : 'pointer',
                            boxShadow: auditLoading ? 'none' : '0 4px 12px rgba(90,53,255,0.3)',
                            whiteSpace: 'nowrap', flexShrink: 0,
                          }}>
                          {auditLoading ? `⏳ ${auditLoadingMsg}` : '🚀 Lancer l\'audit'}
                        </button>
                      </div>

                      {/* Liste des audits accordion */}
                      {allAudits.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {allAudits.map((audit, idx) => {
                            const isOpen = expandedAuditIdx === idx;
                            const prevAudit = allAudits[idx + 1];
                            const delta = prevAudit ? audit.overall_score - prevAudit.overall_score : null;
                            const scoreCol = audit.overall_score >= 70 ? '#10B981' : audit.overall_score >= 45 ? '#F59E0B' : '#EF4444';
                            const isLatest = idx === 0;

                            return (
                              <div key={idx} style={{
                                background: isOpen ? 'white' : '#FAF7FF',
                                border: `1.5px solid ${isOpen ? '#C4B5FD' : '#DDD6FE'}`,
                                borderRadius: 12,
                                overflow: 'hidden',
                                transition: 'border-color 0.15s',
                              }}>
                                {/* Ligne summary cliquable */}
                                <div
                                  onClick={() => setExpandedAuditIdx(isOpen ? null : idx)}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px',
                                    cursor: 'pointer', userSelect: 'none',
                                  }}
                                >
                                  {/* Score */}
                                  <div style={{
                                    minWidth: 52, height: 52, borderRadius: 10,
                                    background: `${scoreCol}15`, border: `2px solid ${scoreCol}40`,
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0,
                                  }}>
                                    <span style={{ fontSize: 18, fontWeight: 900, color: scoreCol, lineHeight: 1 }}>{audit.overall_score}</span>
                                    <span style={{ fontSize: 9, color: scoreCol, fontWeight: 600 }}>/100</span>
                                  </div>
                                  {/* Infos */}
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                      <span style={{ fontWeight: 700, fontSize: 13, color: '#1F2937' }}>
                                        {new Date(audit.audited_at).toLocaleDateString('fr-CH', { day: 'numeric', month: 'long', year: 'numeric' })}
                                      </span>
                                      {isLatest && <span style={{ fontSize: 10, fontWeight: 700, background: '#7C3AED', color: 'white', borderRadius: 999, padding: '1px 8px' }}>Dernier</span>}
                                      {delta !== null && (
                                        <span style={{ fontSize: 12, fontWeight: 700, color: delta >= 0 ? '#16A34A' : '#DC2626' }}>
                                          {delta > 0 ? `▲ +${delta} pts` : delta < 0 ? `▼ ${delta} pts` : '= stable'}
                                        </span>
                                      )}
                                    </div>
                                    {audit.top_priority && (
                                      <div style={{ fontSize: 12, color: '#7C3AED', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        🎯 {audit.top_priority}
                                      </div>
                                    )}
                                  </div>
                                  {/* Sections mini-badges */}
                                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                                    {(audit.sections || []).slice(0, 5).map((s, si) => {
                                      const c = s.status === 'good' ? '#10B981' : s.status === 'warning' ? '#F59E0B' : '#EF4444';
                                      return <div key={si} style={{ width: 8, height: 8, borderRadius: '50%', background: c }} title={`${s.title} — ${s.score}/100`} />;
                                    })}
                                  </div>
                                  {/* Flèche */}
                                  <span style={{ fontSize: 14, color: '#7C3AED', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}>▼</span>
                                </div>

                                {/* Contenu déployé */}
                                {isOpen && (
                                  <div style={{ borderTop: '1px solid #DDD6FE', padding: '16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {audit.ai_message && (
                                      <div style={{ background: 'linear-gradient(135deg,#EDE9FE,#F5F3FF)', border: '1px solid #C4B5FD', borderRadius: 10, padding: '12px 16px', display: 'flex', gap: 10 }}>
                                        <span style={{ fontSize: 20 }}>🤖</span>
                                        <p style={{ margin: 0, fontSize: 13, color: '#5B21B6', fontStyle: 'italic', lineHeight: 1.5 }}>{audit.ai_message}</p>
                                      </div>
                                    )}
                                    {audit.summary && (
                                      <div style={{ fontSize: 13, color: '#5B21B6', lineHeight: 1.6, padding: '10px 14px', background: 'white', borderRadius: 10, border: '1px solid #DDD6FE' }}>
                                        {audit.summary}
                                      </div>
                                    )}
                                    {audit.top_priority && (
                                      <div style={{ fontSize: 13, fontWeight: 700, color: '#4C1D95', padding: '10px 14px', background: '#FEF3C7', borderRadius: 10, border: '1px solid #FDE68A' }}>
                                        🎯 Priorité #1 : {audit.top_priority}
                                      </div>
                                    )}
                                    {(audit.sections || []).map((sec, si) => {
                                      const scfg = {
                                        good:     { bg: '#D1FAE5', color: '#065F46', border: '#6EE7B7', label: '✓ Bon' },
                                        warning:  { bg: '#FEF3C7', color: '#92400E', border: '#FDE68A', label: '⚠ À améliorer' },
                                        critical: { bg: '#FEE2E2', color: '#991B1B', border: '#FECACA', label: '✗ Critique' },
                                      }[sec.status] || { bg: '#F3F4F6', color: '#374151', border: '#E5E7EB', label: sec.status };
                                      return (
                                        <div key={si} style={{ background: 'white', borderRadius: 10, padding: '12px 14px', border: `1.5px solid ${scfg.border}` }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                                            <div style={{ fontWeight: 700, fontSize: 13, flex: 1, color: '#1F2937' }}>{sec.title}</div>
                                            <span style={{ background: scfg.bg, color: scfg.color, borderRadius: 999, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>{scfg.label}</span>
                                            <span style={{ fontSize: 12, fontWeight: 700, color: scfg.color }}>{sec.score}/100</span>
                                          </div>
                                          {sec.findings?.length > 0 && (
                                            <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6, marginBottom: 4 }}>
                                              {sec.findings.map((f, fi) => <div key={fi}>• {f}</div>)}
                                            </div>
                                          )}
                                          {sec.actions?.length > 0 && (
                                            <div style={{ fontSize: 12, color: '#5B21B6', lineHeight: 1.6, fontWeight: 600 }}>
                                              Actions : {sec.actions.map((a, ai) => <span key={ai}>{ai > 0 ? ' · ' : ''}{a}</span>)}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                    {/* Bouton stratégie (dernier audit seulement) */}
                                    {isLatest && (
                                      <div style={{ marginTop: 8, padding: '14px 18px', background: 'linear-gradient(135deg,#F5F3FF,#EDE9FE)', border: '1.5px solid #C4B5FD', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
                                        <div>
                                          <div style={{ fontWeight: 700, fontSize: 13, color: '#5B21B6', marginBottom: 3 }}>🤝 Appee peut vous guider pas à pas</div>
                                          <div style={{ fontSize: 12, color: '#6D28D9' }}>Créez une stratégie personnalisée basée sur cet audit.</div>
                                        </div>
                                        <button onClick={handleCreateAuditStrategy} disabled={creatingAuditStrat} style={{ padding: '9px 18px', background: creatingAuditStrat ? '#C4B5FD' : 'linear-gradient(135deg,#7C3AED,#5A35FF)', color: 'white', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: creatingAuditStrat ? 'not-allowed' : 'pointer', flexShrink: 0 }}>
                                          {creatingAuditStrat ? '⏳ Création…' : '🚀 Améliorer mon score'}
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* ── G. Save button ── */}
                <div style={{ position: 'sticky', bottom: 16, zIndex: 10, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button onClick={() => saveCompany()} disabled={companySaving} style={{ boxShadow: '0 4px 14px rgba(0,0,0,0.15)' }}>
                    {companySaving ? 'Sauvegarde…' : '💾 Sauvegarder'}
                  </Button>
                </div>
              </div>
            );
          })()}

          {/* ── FAMILLE & PERSO ── */}
          {tab === 'famille' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Card>
                <CardHeader
                  title="👨‍👩‍👧 Famille & vie perso"
                  subtitle="Ces infos restent privées — Appee s'en sert uniquement pour te faire des rappels personnels au bon moment."
                />

                {/* ── Récapitulatif famille ── */}
                {(personalProfile?.partner_name || (personalProfile?.children?.length > 0)) && (
                  <div style={{ marginBottom: 20, padding: '14px 18px', background: 'linear-gradient(135deg,#F5F3FF,#EDE9FF)', border: '1px solid #DDD6FE', borderRadius: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#7C3AED', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: 12 }}>Ma famille</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                      {/* Conjoint(e) */}
                      {personalProfile?.partner_name && (() => {
                        const g = personalProfile.partner_gender;
                        const icon = g === 'wife' ? '👩' : g === 'husband' ? '👨' : '🧑';
                        const label = g === 'wife' ? 'Femme / Compagne' : g === 'husband' ? 'Mari / Compagnon' : 'Partenaire';
                        const age = personalProfile.partner_birthdate
                          ? Math.floor((new Date() - new Date(personalProfile.partner_birthdate)) / (365.25 * 24 * 3600 * 1000))
                          : null;
                        return (
                          <div key="partner" style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'white', borderRadius: 12, padding: '10px 14px', border: '1.5px solid #C4B5FD', minWidth: 160, position: 'relative' }}>
                            <div style={{ width: 40, height: 40, borderRadius: '50%', background: g === 'wife' ? '#FDF2F8' : g === 'husband' ? '#EFF6FF' : '#F5F3FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>{icon}</div>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 14, color: '#1E1B4B' }}>{personalProfile.partner_name}</div>
                              <div style={{ fontSize: 11, color: '#7C3AED', fontWeight: 600 }}>{label}{age ? ` · ${age} ans` : ''}</div>
                              {personalProfile.partner_birthdate && (
                                <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                                  🎂 {new Date(personalProfile.partner_birthdate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long' })}
                                </div>
                              )}
                            </div>
                            <button onClick={deletePartner} title="Supprimer" style={{ position: 'absolute', top: 6, right: 8, background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#FCA5A5', lineHeight: 1, padding: 2, borderRadius: 4, transition: 'color .15s' }}
                              onMouseEnter={e => e.target.style.color='#EF4444'} onMouseLeave={e => e.target.style.color='#FCA5A5'}>×</button>
                          </div>
                        );
                      })()}

                      {/* Enfants */}
                      {(personalProfile?.children || []).map((child, idx) => {
                        const age = child.birthdate
                          ? Math.floor((new Date() - new Date(child.birthdate)) / (365.25 * 24 * 3600 * 1000))
                          : null;
                        const g = child.gender;
                        const childIcon = g === 'girl'
                          ? (age !== null && age < 4 ? '👶' : age !== null && age < 13 ? '👧' : '👩')
                          : g === 'boy'
                            ? (age !== null && age < 4 ? '👶' : age !== null && age < 13 ? '👦' : '👨')
                            : (age !== null && age < 4 ? '👶' : '🧒');
                        const childBg  = g === 'girl' ? '#FDF2F8' : g === 'boy' ? '#EFF6FF' : '#FFF7ED';
                        const childCol = g === 'girl' ? '#DB2777' : g === 'boy' ? '#2563EB' : '#EA580C';
                        const gLabel   = g === 'girl' ? 'Fille' : g === 'boy' ? 'Garçon' : 'Enfant';
                        return (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'white', borderRadius: 12, padding: '10px 14px', border: '1.5px solid #C4B5FD', minWidth: 160, position: 'relative' }}>
                            <div style={{ width: 40, height: 40, borderRadius: '50%', background: childBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>{childIcon}</div>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 14, color: '#1E1B4B' }}>{child.name || `Enfant ${idx + 1}`}</div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: childCol }}>{gLabel}{age !== null ? ` · ${age} ans` : ''}</div>
                              {child.birthdate && (
                                <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                                  🎂 {new Date(child.birthdate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long' })}
                                </div>
                              )}
                            </div>
                            <button onClick={() => deleteChildAndSave(idx)} title="Supprimer" style={{ position: 'absolute', top: 6, right: 8, background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#FCA5A5', lineHeight: 1, padding: 2, borderRadius: 4, transition: 'color .15s' }}
                              onMouseEnter={e => e.target.style.color='#EF4444'} onMouseLeave={e => e.target.style.color='#FCA5A5'}>×</button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

                  {/* Conjoint(e) */}
                  <div className="company-section">
                    <div className="company-section-title">💑 Conjoint(e) / Partenaire</div>
                    <div className="company-fields">
                      <Input
                        label="Prénom"
                        value={personalForm.partner_name || ''}
                        onChange={e => setPers('partner_name', e.target.value)}
                        placeholder="Ex: Marie, Pierre…"
                      />
                      <Input
                        label="Date de naissance"
                        type="date"
                        value={personalForm.partner_birthdate || ''}
                        onChange={e => setPers('partner_birthdate', e.target.value)}
                      />
                      <div>
                        <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: 'block' }}>Relation</label>
                        <select
                          className="company-select"
                          value={personalForm.partner_gender || ''}
                          onChange={e => setPers('partner_gender', e.target.value)}
                        >
                          <option value="">Sélectionne…</option>
                          <option value="wife">Ma femme / compagne</option>
                          <option value="husband">Mon mari / compagnon</option>
                          <option value="partner">Mon/ma partenaire</option>
                        </select>
                      </div>
                    </div>
                    {personalForm.partner_birthdate && (
                      <div style={{ marginTop: 10, padding: '10px 14px', background: 'linear-gradient(135deg,#EDE9FF,#F5F3FF)', border: '1px solid #A78BFA', borderRadius: 10, fontSize: 13, color: '#5A35FF', fontWeight: 600 }}>
                        💡 Je vais créer deux rappels automatiques pour l'anniversaire de {personalForm.partner_name || 'ton/ta partenaire'} : 1 semaine avant et 2 jours avant (avec des idées cadeaux 🎁)
                      </div>
                    )}
                  </div>

                  {/* Enfants */}
                  <div className="company-section">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <div className="company-section-title" style={{ margin: 0 }}>👶 Enfants</div>
                      <Button size="sm" variant="secondary" onClick={addChild}>+ Ajouter un enfant</Button>
                    </div>
                    {(!personalForm.children || personalForm.children.length === 0) ? (
                      <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--muted)', fontSize: 13 }}>
                        Aucun enfant renseigné.{' '}
                        <span style={{ color: 'var(--violet)', cursor: 'pointer', fontWeight: 600 }} onClick={addChild}>
                          Ajouter →
                        </span>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {(personalForm.children || []).map((child, idx) => (
                          <div key={idx} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', padding: '12px 14px', background: '#F9FAFB', borderRadius: 10, border: '1px solid var(--line)', flexWrap: 'wrap' }}>
                            <div style={{ flex: '1 1 120px', minWidth: 100 }}>
                              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Prénom</label>
                              <input
                                value={child.name || ''}
                                onChange={e => setChild(idx, 'name', e.target.value)}
                                placeholder="Ex: Lucas, Emma…"
                                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--line)', fontSize: 14, boxSizing: 'border-box' }}
                              />
                            </div>
                            <div style={{ flex: '1 1 90px', minWidth: 80 }}>
                              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Genre</label>
                              <select
                                value={child.gender || ''}
                                onChange={e => setChild(idx, 'gender', e.target.value)}
                                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1.5px solid var(--line)', fontSize: 14, background: 'white', boxSizing: 'border-box' }}
                              >
                                <option value="">–</option>
                                <option value="girl">👧 Fille</option>
                                <option value="boy">👦 Garçon</option>
                              </select>
                            </div>
                            <div style={{ flex: '1 1 130px', minWidth: 110 }}>
                              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Date de naissance</label>
                              <input
                                type="date"
                                value={child.birthdate || ''}
                                onChange={e => setChild(idx, 'birthdate', e.target.value)}
                                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--line)', fontSize: 14, boxSizing: 'border-box' }}
                              />
                            </div>
                            <button
                              onClick={() => removeChild(idx)}
                              style={{ background: '#FEE2E2', border: 'none', borderRadius: 8, padding: '8px 10px', cursor: 'pointer', fontSize: 14, color: '#DC2626', flexShrink: 0 }}
                            >🗑</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <Button onClick={savePersonal} disabled={personalSaving}>
                    {personalSaving ? 'Sauvegarde…' : '💾 Sauvegarder'}
                  </Button>
                </div>
              </Card>

              <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 12, padding: '14px 18px', fontSize: 13, color: '#15803D' }}>
                <strong>🔒 Confidentialité :</strong> Ces informations sont stockées de manière sécurisée et ne sont jamais partagées ni utilisées dans des campagnes. Elles servent uniquement à créer des rappels personnels pour toi.
              </div>
            </div>
          )}

          {/* ── COLLABORATEURS ── */}
          {tab === 'collaborateurs' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Card>
                <CardHeader
                  title="👥 Mon équipe"
                  subtitle="Renseigne tes collaborateurs pour qu'Appee les intègre dans ta gestion — anniversaires, rappels, contacts internes."
                  action={
                    <Button size="sm" onClick={startNewCollab} disabled={editingCollab !== null}>
                      + Ajouter
                    </Button>
                  }
                />

                {/* Formulaire d'ajout / édition */}
                {editingCollab !== null && (
                  <div style={{ padding: '18px', background: 'white', border: '2px solid var(--violet)', borderRadius: 14, display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--violet)' }}>
                      {editingCollab === 'new' ? '✨ Nouveau collaborateur' : '✏️ Modifier'}
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <div style={{ flex: 2 }}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Prénom & Nom *</label>
                        <input value={collabForm.name || ''} onChange={e => setCol('name', e.target.value)}
                          placeholder="Ex: Sophie Martin"
                          style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--line)', fontSize: 14, boxSizing: 'border-box' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Rôle / Poste</label>
                        <input value={collabForm.role || ''} onChange={e => setCol('role', e.target.value)}
                          placeholder="Ex: Responsable accueil"
                          style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--line)', fontSize: 14, boxSizing: 'border-box' }} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>E-mail</label>
                        <input type="email" value={collabForm.email || ''} onChange={e => setCol('email', e.target.value)}
                          placeholder="sophie@entreprise.ch"
                          style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--line)', fontSize: 14, boxSizing: 'border-box' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Téléphone</label>
                        <input type="tel" value={collabForm.phone || ''} onChange={e => setCol('phone', e.target.value)}
                          placeholder="+41 79 123 45 67"
                          style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--line)', fontSize: 14, boxSizing: 'border-box' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Date de naissance</label>
                        <input type="date" value={collabForm.birthdate || ''} onChange={e => setCol('birthdate', e.target.value)}
                          style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--line)', fontSize: 14, boxSizing: 'border-box' }} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
                      <Button variant="secondary" size="sm" onClick={cancelCollab}>Annuler</Button>
                      <Button size="sm" onClick={saveCollab} disabled={!collabForm.name.trim()} loading={collabSaving}>
                        {editingCollab === 'new' ? 'Ajouter à l\'équipe' : 'Sauvegarder'}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Liste des collaborateurs */}
                {collaborators.length === 0 && editingCollab === null ? (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)', fontSize: 14 }}>
                    <div style={{ fontSize: 40, marginBottom: 10 }}>👥</div>
                    Aucun collaborateur renseigné.<br />
                    <span style={{ color: 'var(--violet)', cursor: 'pointer', fontWeight: 600 }} onClick={startNewCollab}>
                      Ajouter le premier membre →
                    </span>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {collaborators.map(c => (
                      <div key={c.id} style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '12px 16px',
                        background: 'var(--violet-pale)',
                        border: '1.5px solid var(--violet-light)',
                        borderRadius: 12,
                      }}>
                        <div style={{
                          width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                          background: 'linear-gradient(135deg,#5A35FF,#7C5CFF)',
                          color: '#fff', fontSize: 16, fontWeight: 800,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {(c.name || '?').charAt(0).toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 14 }}>{c.name}</div>
                          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                            {[c.role, c.email, c.phone].filter(Boolean).join(' · ')}
                            {c.birthdate && <span style={{ marginLeft: 8 }}>🎂 {new Date(c.birthdate).toLocaleDateString('fr-CH', { day: 'numeric', month: 'long' })}</span>}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                          <button onClick={() => startEditCollab(c)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, padding: '4px 6px' }}>✏️</button>
                          <button onClick={() => deleteCollab(c.id, c.name)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, padding: '4px 6px' }}>🗑</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              <div style={{ background: 'linear-gradient(135deg,#EDE9FF,#F5F3FF)', border: '1.5px solid #A78BFA', borderRadius: 12, padding: '14px 18px', fontSize: 13, color: '#5A35FF' }}>
                <strong>💡 Bon à savoir :</strong> Les collaborateurs renseignés ici permettent à Appee de te rappeler leurs anniversaires et dates importantes. Pour les intégrer dans les campagnes clients, importe-les dans les contacts avec la catégorie <strong>Collaborateur</strong>.
              </div>
            </div>
          )}

          {/* ── PLAN ── */}
          {tab === 'plan' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Card>
                <CardHeader title="📦 Votre plan actuel" action={
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <PlanBadge plan={tenant.plan} />
                    {billing?.subscription_status === 'active' && (
                      <Button variant="secondary" size="sm" loading={portalBusy} onClick={openBillingPortal}>⚙️ Gérer (factures, résiliation)</Button>
                    )}
                  </div>
                } />
                <div className="plan-usage-grid">
                  {[
                    { label: 'Contacts',       used: tenant.contacts_used,  limit: tenant.contacts_limit },
                    { label: 'E-mails / mois', used: tenant.emails_used,    limit: tenant.emails_limit },
                    { label: 'Générations IA', used: tenant.ai_used,        limit: tenant.ai_limit,         isAi: true },
                    { label: 'Utilisateurs',   used: 1,                     limit: ({ starter: 1, pro: 3, premium: 10 })[tenant.plan] || 1 },
                  ].map(item => {
                    const pct   = Math.min(100, Math.round(((item.used || 0) / (item.limit || 1)) * 100));
                    const color = pct >= 100 ? '#EF4444' : pct > 80 ? '#F59E0B' : 'var(--violet)';
                    return (
                      <div key={item.label} className="plan-usage-item">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, fontSize: 13 }}>
                          <span style={{ fontWeight: 600 }}>{item.label}</span>
                          <span style={{ color: pct >= 100 ? '#EF4444' : 'var(--muted)', fontWeight: pct >= 100 ? 700 : 400 }}>
                            {(item.used || 0).toLocaleString()} / {(item.limit || 0).toLocaleString()}
                            {pct >= 100 && ' 🔴'}
                          </span>
                        </div>
                        <div style={{ height: 8, background: 'var(--line)', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4, transition: 'width .5s' }} />
                        </div>
                        {item.isAi && pct >= 80 && (
                          <div style={{ marginTop: 10, padding: '10px 14px', background: pct >= 100 ? '#FEF2F2' : '#FFFBEB', border: `1px solid ${pct >= 100 ? '#FECACA' : '#FDE68A'}`, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                            <span style={{ fontSize: 12, color: pct >= 100 ? '#991B1B' : '#92400E', fontWeight: 600 }}>
                              {pct >= 100 ? '⚠️ Quota IA dépassé — Appee utilise les conseils standards.' : `⚡ ${100 - pct}% de crédits IA restants`}
                            </span>
                            <button onClick={() => document.getElementById('ai-packs-section')?.scrollIntoView({ behavior: 'smooth' })}
                              style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: pct >= 100 ? '#EF4444' : '#F59E0B', border: 'none', borderRadius: 999, padding: '4px 12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              + Crédits IA
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* SMS credits — pool annuel */}
                  <div className="plan-usage-item">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, fontSize: 13 }}>
                      <span style={{ fontWeight: 600 }}>📱 SMS / mois</span>
                      <span style={{ color: (tenant.sms_credits || 0) === 0 ? '#EF4444' : 'var(--muted)', fontWeight: (tenant.sms_credits || 0) === 0 ? 700 : 400 }}>
                        {(tenant.sms_credits || 0)} crédit{(tenant.sms_credits || 0) !== 1 ? 's' : ''} restant{(tenant.sms_credits || 0) !== 1 ? 's' : ''}
                        {(tenant.sms_credits || 0) === 0 && ' 🔴'}
                      </span>
                    </div>
                    <div style={{ height: 8, background: 'var(--line)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%',
                        width: `${Math.min(100, Math.round(((tenant.sms_credits || 0) / (tenant.sms_plan_included || 50)) * 100))}%`,
                        background: (tenant.sms_credits || 0) === 0 ? '#EF4444' : (tenant.sms_credits || 0) < 10 ? '#F59E0B' : '#10B981',
                        borderRadius: 4, transition: 'width .5s',
                      }} />
                    </div>
                    <div style={{ marginTop: 6, fontSize: 11, color: 'var(--muted)' }}>
                      Plan {tenant.plan || 'starter'} → {tenant.sms_plan_included || 50} SMS inclus/mois · <span style={{ color: 'var(--muted-2)' }}>remis à zéro chaque mois (non cumulé). Les packs achetés, eux, restent.</span>
                    </div>
                    {(tenant.sms_credits || 0) < 10 && (
                      <div style={{ marginTop: 10, padding: '10px 14px', background: (tenant.sms_credits || 0) === 0 ? '#FEF2F2' : '#FFFBEB', border: `1px solid ${(tenant.sms_credits || 0) === 0 ? '#FECACA' : '#FDE68A'}`, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                        <span style={{ fontSize: 12, color: (tenant.sms_credits || 0) === 0 ? '#991B1B' : '#92400E', fontWeight: 600 }}>
                          {(tenant.sms_credits || 0) === 0 ? '⚠️ Plus de crédits SMS — achetez un pack pour continuer.' : `⚡ Seulement ${tenant.sms_credits} SMS restants`}
                        </span>
                        <button
                          onClick={() => document.getElementById('sms-packs-section')?.scrollIntoView({ behavior: 'smooth' })}
                          style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: (tenant.sms_credits || 0) === 0 ? '#EF4444' : '#F59E0B', border: 'none', borderRadius: 999, padding: '4px 12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          + Acheter des SMS
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </Card>

              <div className="plans-comparison">
                {[
                  {
                    name: 'Starter', price: '49',  contacts: '500',    emails: "1'500",  ai: '200',   sms: '40',
                    color: '#6B7280', highlight: 'Pour se lancer',
                    unlocks: [],
                  },
                  {
                    name: 'Pro',     price: '149', contacts: "5'000",  emails: "6'000",  ai: '800',   sms: '120',
                    color: '#5A35FF', highlight: 'Le plus populaire parmi nos PME', popular: true,
                    unlocks: ['Stratégies automatisées', 'Segmentation avancée', 'Rapport de campagne IA'],
                  },
                  {
                    name: 'Premium', price: '299', contacts: "25'000", emails: "15'000", ai: "3'000", sms: '250',
                    color: '#D97706', highlight: 'Pour les équipes ambitieuses',
                    unlocks: ['Tout Pro inclus', 'Base contacts ×5', 'Support prioritaire', 'Export avancé'],
                  },
                ].map((plan, idx, arr) => {
                  const isCurrent = plan.name.toLowerCase() === tenant?.plan;
                  const currentIdx = arr.findIndex(p => p.name.toLowerCase() === tenant?.plan);
                  const isUpgrade = idx > currentIdx;
                  return (
                    <div key={plan.name}
                      className={`plan-card2${isCurrent ? ' plan-card2-current' : ''}${plan.popular && !isCurrent ? ' plan-card2-popular' : ''}`}
                      style={{ '--pcolor': plan.color }}>

                      {plan.popular && !isCurrent && (
                        <div className="plan2-badge-pop">⭐ Recommandé</div>
                      )}
                      {isCurrent && (
                        <div className="plan2-badge-cur" style={{ background: plan.color }}>✓ Votre plan</div>
                      )}

                      <div className="plan2-name" style={{ color: plan.color }}>{plan.name}</div>
                      <div className="plan2-price">
                        <span className="plan2-chf">CHF</span>
                        <span className="plan2-amount">{plan.price}</span>
                        <span className="plan2-period">/mois</span>
                      </div>
                      <div className="plan2-highlight">{plan.highlight}</div>

                      <div className="plan2-divider" />

                      <div className="plan2-features">
                        <div className="plan2-feat"><span className="plan2-feat-icon">👥</span>{plan.contacts} contacts</div>
                        <div className="plan2-feat"><span className="plan2-feat-icon">📧</span>{plan.emails} e-mails/mois</div>
                        <div className="plan2-feat"><span className="plan2-feat-icon">✨</span>{plan.ai} génér. IA</div>
                        <div className="plan2-feat"><span className="plan2-feat-icon">📱</span>{plan.sms} SMS/mois</div>
                      </div>

                      {plan.unlocks.length > 0 && (
                        <div className="plan2-unlocks" style={{ background: isCurrent ? 'rgba(0,0,0,.03)' : undefined }}>
                          {plan.unlocks.map(u => (
                            <div key={u} className="plan2-unlock" style={{ color: plan.color }}>🔑 {u}</div>
                          ))}
                        </div>
                      )}

                      {isCurrent ? (
                        <div className="plan2-current-label" style={{ borderColor: plan.color, color: plan.color }}>
                          ✓ Plan actuel
                        </div>
                      ) : isUpgrade ? (
                        <button className="plan2-cta plan2-cta-upgrade" style={{ background: plan.color }}
                          onClick={() => billing?.subscription_status === 'active'
                            ? openBillingPortal()
                            : startCheckout('plan', plan.name.toLowerCase())}>
                          Passer à {plan.name} →
                        </button>
                      ) : (
                        <button className="plan2-cta plan2-cta-down" onClick={openBillingPortal}>
                          Downgrade
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* ── Packs SMS additionnels ── */}
              <div id="sms-packs-section">
                <Card>
                  <CardHeader title="📱 Packs SMS additionnels" />
                  <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
                    Tes SMS inclus sont épuisés ? Achète un pack. Les crédits d'un pack sont <strong style={{ color: 'var(--ink)' }}>cumulés à ton solde et ne périment jamais</strong> : ils restent tant que tu ne les as pas utilisés, même d'un mois sur l'autre. <span style={{ color: 'var(--muted-2)' }}>(Le forfait mensuel de ton abonnement, lui, repart à zéro chaque mois.)</span>
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
                    {[
                      { credits: 50,   price: 5,   label: '50 SMS',     popular: false },
                      { credits: 100,  price: 10,  label: '100 SMS',    popular: true  },
                      { credits: 500,  price: 47,  label: '500 SMS',    popular: false },
                      { credits: 1000, price: 92,  label: '1\'000 SMS', popular: false },
                    ].map(pack => (
                      <div key={pack.credits} style={{
                        border: pack.popular ? '2px solid #10B981' : '1.5px solid var(--line)',
                        borderRadius: 14, padding: '16px 14px', textAlign: 'center',
                        position: 'relative', background: pack.popular ? '#F0FDF4' : '#fff',
                      }}>
                        {pack.popular && (
                          <div style={{ position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)', background: '#10B981', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 10px', borderRadius: 999 }}>
                            POPULAIRE
                          </div>
                        )}
                        <div style={{ fontSize: 22, fontWeight: 800, color: '#1F2937', marginBottom: 2 }}>📱 {pack.label}</div>
                        <div style={{ fontSize: 24, fontWeight: 800, color: pack.popular ? '#10B981' : '#5A35FF', margin: '8px 0' }}>
                          CHF {pack.price}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 12 }}>
                          {(pack.price / pack.credits * 100).toFixed(1)} cts/SMS
                        </div>
                        <button
                          onClick={() => startCheckout('pack', 'sms', [50, 100, 500, 1000].indexOf(pack.credits))}
                          style={{
                            width: '100%', padding: '8px 0',
                            background: pack.popular ? '#10B981' : 'var(--violet)',
                            color: '#fff', border: 'none', borderRadius: 8,
                            fontWeight: 700, fontSize: 13, cursor: 'pointer',
                          }}>
                          Acheter →
                        </button>
                      </div>
                    ))}
                  </div>
                  <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 14, textAlign: 'center' }}>
                    Les crédits SMS ne sont pas liés à un abonnement mensuel — ils restent valables indéfiniment.
                  </p>
                </Card>
              </div>

              {/* ── Packs crédits IA additionnels ── */}
              <div id="ai-packs-section">
                <Card>
                  <CardHeader title="✨ Packs crédits IA additionnels" />
                  <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
                    Plus de textes et de visuels IA ce mois-ci ? Ajoute des crédits — 1 visuel = 15 crédits, 1 texte = 1 crédit.
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
                    {[
                      { credits: 250,  price: 9,  label: '250 crédits',   popular: false },
                      { credits: 1000, price: 29, label: "1'000 crédits", popular: true  },
                      { credits: 3000, price: 69, label: "3'000 crédits", popular: false },
                    ].map((pack, idx) => (
                      <div key={pack.credits} style={{
                        border: pack.popular ? '2px solid #8B5CF6' : '1.5px solid var(--line)',
                        borderRadius: 14, padding: '16px 14px', textAlign: 'center',
                        position: 'relative', background: pack.popular ? '#F5F3FF' : '#fff',
                      }}>
                        {pack.popular && (
                          <div style={{ position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)', background: '#8B5CF6', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 10px', borderRadius: 999 }}>
                            POPULAIRE
                          </div>
                        )}
                        <div style={{ fontSize: 22, fontWeight: 800, color: '#1F2937', marginBottom: 2 }}>✨ {pack.label}</div>
                        <div style={{ fontSize: 24, fontWeight: 800, color: pack.popular ? '#8B5CF6' : '#5A35FF', margin: '8px 0' }}>
                          CHF {pack.price}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 12 }}>
                          ≈ {Math.floor(pack.credits / 15)} visuels ou {pack.credits} textes
                        </div>
                        <button
                          onClick={() => startCheckout('pack', 'ai', idx)}
                          style={{
                            width: '100%', padding: '8px 0',
                            background: pack.popular ? '#8B5CF6' : 'var(--violet)',
                            color: '#fff', border: 'none', borderRadius: 8,
                            fontWeight: 700, fontSize: 13, cursor: 'pointer',
                          }}>
                          Acheter →
                        </button>
                      </div>
                    ))}
                  </div>
                  <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 14, textAlign: 'center' }}>
                    Les crédits IA s'ajoutent à ta limite mensuelle et ne périment pas.
                  </p>
                </Card>
              </div>
            </div>
          )}

          {/* ── SÉCURITÉ ── */}
          {tab === 'security' && (
            <>
            <Card>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 300px' }}>
                  <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 800 }}>🎛️ Mode expert</h3>
                  <p style={{ margin: 0, fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.6 }}>
                    Débloque les outils avancés : Stratégie complète, Studio (modèles & pages),
                    Analytics détaillés. Ton choix est mémorisé sur ce compte.
                  </p>
                </div>
                <button
                  onClick={toggleExpertMode}
                  style={{
                    flexShrink: 0, width: 52, height: 30, borderRadius: 999, border: 'none', cursor: 'pointer',
                    background: expertMode ? 'var(--violet)' : 'var(--line)', position: 'relative', transition: 'background .25s',
                  }}
                  aria-label={expertMode ? 'Désactiver le mode expert' : 'Activer le mode expert'}
                >
                  <span style={{
                    position: 'absolute', top: 4, left: expertMode ? 26 : 4,
                    width: 22, height: 22, borderRadius: '50%', background: '#fff',
                    transition: 'left .25s', boxShadow: '0 1px 4px rgba(0,0,0,.2)',
                  }} />
                </button>
              </div>
            </Card>
            <div style={{ height: 16 }} />
            <Card>
              <CardHeader title="🔒 Sécurité" />
              <div className="settings-form">
                <Input
                  label="Mot de passe actuel"
                  type="password"
                  placeholder="••••••••"
                  value={pwForm.current}
                  onChange={e => setPw('current', e.target.value)}
                />
                <Input
                  label="Nouveau mot de passe"
                  type="password"
                  placeholder="8 caractères minimum"
                  value={pwForm.password}
                  onChange={e => setPw('password', e.target.value)}
                />
                <Input
                  label="Confirmer le mot de passe"
                  type="password"
                  placeholder="••••••••"
                  value={pwForm.confirm}
                  onChange={e => setPw('confirm', e.target.value)}
                />
                {pwForm.password && pwForm.confirm && pwForm.password !== pwForm.confirm && (
                  <div style={{ fontSize: 12, color: '#EF4444', marginTop: -8 }}>⚠ Les mots de passe ne correspondent pas.</div>
                )}
                <Button
                  loading={pwSaving}
                  onClick={handleChangePassword}
                  disabled={!pwForm.current || !pwForm.password || !pwForm.confirm}
                >
                  Changer le mot de passe
                </Button>
                <div style={{ marginTop: 24, paddingTop: 24, borderTop: '1px solid var(--line)' }}>
                  <h4 style={{ marginBottom: 12 }}>Export RGPD</h4>
                  <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>Exportez toutes vos données personnelles en conformité avec le RGPD.</p>
                  <Button variant="secondary">Exporter mes données</Button>
                </div>
                <div style={{ marginTop: 24, paddingTop: 24, borderTop: '1px solid var(--line)' }}>
                  <h4 style={{ marginBottom: 12, color: 'var(--danger)' }}>Zone de danger</h4>
                  <Button variant="danger" onClick={() => setDelAccountOpen(o => !o)}>Supprimer mon compte</Button>
                  {delAccountOpen && (
                    <div style={{ marginTop: 16, padding: '16px 18px', background: '#FEF2F2', border: '1.5px solid #FECACA', borderRadius: 12 }}>
                      <div style={{ fontWeight: 800, color: '#991B1B', marginBottom: 6 }}>⚠️ Suppression définitive et irréversible</div>
                      <div style={{ fontSize: 13, color: '#B91C1C', marginBottom: 12, lineHeight: 1.5 }}>
                        Ton compte, tes contacts, tes campagnes, tes pages et toutes tes données seront <b>effacés définitivement</b>. Ton abonnement est résilié. Cette action ne peut pas être annulée.
                      </div>
                      <input
                        value={delAccountInput}
                        onChange={e => setDelAccountInput(e.target.value)}
                        placeholder="Tape le nom exact de ton entreprise pour confirmer"
                        style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #FCA5A5', borderRadius: 10, fontSize: 14, marginBottom: 12, fontFamily: 'inherit' }}
                      />
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <Button variant="secondary" onClick={() => { setDelAccountOpen(false); setDelAccountInput(''); }} disabled={delAccountBusy}>Annuler</Button>
                        <Button variant="danger" onClick={handleDeleteAccount} loading={delAccountBusy} disabled={!delAccountInput.trim()}>Supprimer définitivement</Button>
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop: 20, padding: '16px 18px', background: '#FEF2F2', border: '1.5px solid #FECACA', borderRadius: 12 }}>
                    <div style={{ fontWeight: 800, color: '#991B1B', marginBottom: 4 }}>🗑 Supprimer toute la base contacts</div>
                    <div style={{ fontSize: 13, color: '#B91C1C', marginBottom: 12, lineHeight: 1.5 }}>
                      Cette action supprime définitivement tous vos contacts. Elle est irréversible.
                    </div>
                    <Button variant="danger" onClick={handleDeleteAllContacts}>Supprimer tous les contacts</Button>
                  </div>
                </div>
              </div>
            </Card>
            </>
          )}
        </div>
      </div>

      {/* ── Modal confirmation serveur générique Appee ── */}
      {showFallbackModal && (
        <div className="modal-overlay" onClick={() => setShowFallbackModal(false)}>
          <div className="modal-box" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">⚠️ Utiliser le serveur générique Appee ?</div>
              <button className="modal-close" onClick={() => setShowFallbackModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--ink)' }}>
                En activant cette option, vos e-mails d'anniversaire seront envoyés via le serveur d'Appee.
                Vos clients recevront les messages, mais <strong>l'adresse expéditrice ne sera pas la vôtre</strong>.
              </div>
              <div style={{ background: '#FEF2F2', border: '1.5px solid #FECACA', borderRadius: 10, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#991B1B', marginBottom: 2 }}>Ce que verra votre client :</div>
                <div style={{ fontSize: 13, color: '#7F1D1D', fontFamily: 'monospace', background: '#fff', border: '1px solid #FECACA', borderRadius: 6, padding: '6px 10px' }}>
                  De : <strong>Votre Entreprise</strong> &lt;noreply@beeappee.com&gt;
                </div>
                <div style={{ fontSize: 12, color: '#991B1B', marginTop: 4, lineHeight: 1.6 }}>
                  📧 Votre nom d'entreprise s'affiche, mais l'adresse est celle d'Appee.<br />
                  💬 Les réponses sont redirigées vers votre adresse si renseignée.<br />
                  ✅ Pour envoyer depuis votre propre adresse, configurez votre SMTP.
                </div>
              </div>
              <div style={{ fontSize: 13, color: 'var(--muted)', fontStyle: 'italic' }}>
                Cette option est provisoire. Nous recommandons de configurer votre propre SMTP pour une meilleure délivrabilité.
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-btn-cancel" onClick={() => setShowFallbackModal(false)}>Annuler</button>
              <button
                className="modal-btn-save"
                style={{ background: '#F59E0B' }}
                onClick={async () => {
                  setA('birthday_fallback_mailjet', true);
                  setShowFallbackModal(false);
                  try {
                    await api.saveAdmin({ birthday_fallback_mailjet: true });
                    addNotification('✅ Serveur générique Appee activé.');
                  } catch {
                    addNotification('Erreur sauvegarde', 'error');
                  }
                }}
              >
                J'ai compris, activer le serveur générique
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
